import { notifyClubStaff } from './lib/notifications';
import { getMatchTemporalState } from './lib/matchmakingUtils';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSessionState } from './hooks/useSessionState';
import 'moment/locale/es';
import { es as dateFnsEs } from 'date-fns/locale';
import { createPortal } from 'react-dom';
import {
  Menu,
  Trophy,
  CalendarDays,
  ShieldCheck,
  Phone,
  CheckCircle2,
  Settings,
  LayoutList,
  Medal,
  Trash2,
  Bell,
  FileText,
  AlertTriangle,
  AlertCircle,
  CloudOff,
  X,
  Download,
  LogOut,
  LogIn,
  Shield,
  MapPin,
  Compass,
  MessageCircle,
  MessageSquare,
  Users,
  Search,
  History,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Share2,
  Share,
  Instagram,
  Star,
  Globe,
  User as UserIcon,
  UserMinus,
  Minus,
  Check,
  Mars,
  Venus,
  Swords,
  UsersRound,
  LayoutGrid,
  Building2,
  ChevronsUp,
  Volume2,
  VolumeX,
  Lock,
  Unlock,
  Camera,
  Sparkles,
  BellOff,
  BellRing,
  Clock,
  Edit2,
  Award,
  Flame,
  Target,
  ArrowLeft,
  HelpCircle,
  Coffee,
  Info,
  Home
} from 'lucide-react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { onMessage } from 'firebase/messaging';
import { collection, onSnapshot, doc, deleteDoc, getDoc, getDocs, query, where, setDoc, orderBy, updateDoc, serverTimestamp, addDoc, writeBatch } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'motion/react';


import { auth, db, getCollectionPath, messaging, getMessagingInstance, storage } from './lib/firebase';
import { handleFirestoreError } from './lib/errorHandlers';
import { getRgba, formatPairName, formatPlayerName } from './lib/utils';
import { drawCanvasBg, drawCanvasHeader, drawExportFooter, drawExportBorder, handleShareCanvas } from './lib/canvas-utils';
import { OrganizerDashboard } from './components/admin/OrganizerDashboard';
import { Player, Team, Match, Tournament, News, Club, Config, UserProfile, RankingConfig } from './types';
import { getCategoryStyles } from './lib/categoryStyles';
import { ALL_CITIES } from './data/locations';

// Components
import { NavButton } from './components/common/NavButton';
import { PlayerMiniCard } from './components/player/PlayerMiniCard';
import { PublicTournamentCard } from './components/tournament/PublicTournamentCard';
import { PlayerDetailModal } from './components/player/PlayerDetailModal';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { LoginModal } from './components/common/LoginModal';
import { PairAvatar } from './components/common/PairAvatar';
import { ProfileSetupModal } from './components/common/ProfileSetupModal';
import { AdminOnboardingModal } from './components/admin/AdminOnboardingModal';
import { LazyItem } from './components/common/LazyItem';
import { EditProfileModal } from './components/player/EditProfileModal';
import { MyProfile } from './components/player/MyProfile';
import { FadeInImage } from './components/common/FadeInImage';
import { MatchDetailModal } from './components/match/MatchDetailModal';
import { SupportChatModal } from './components/common/SupportChatModal';
import { PrivateChatModal } from './components/common/PrivateChatModal';
import { PrizeEditModal } from './components/common/PrizeEditModal';
import { PrizeDetailModal } from './components/common/PrizeDetailModal';
import { NotificationBanner } from './components/pwa/NotificationBanner';
import { InstallGuide } from './components/common/InstallGuide';
import { NotificationBlockedModal } from './components/common/NotificationBlockedModal';
import { MyBookings } from './components/booking/MyBookings';
import { UnderConstruction } from './components/common/UnderConstruction';
import { GuestLanding } from './components/public/GuestLanding';
import { PublicTournamentView } from './components/public/PublicTournamentView';
import { SocialDashboard } from './components/social/SocialDashboard';
import { PublicMatches } from './components/matchmaking/PublicMatches';
import { UnifiedHistory } from './components/common/UnifiedHistory';
import { NotificationSettings } from './components/player/NotificationSettings';
import { usePWAInstall, useNetworkStatus } from './lib/pwa';
import { PosSessionProvider } from './context/PosSessionContext';
import { EmailVerificationBlocking } from './components/auth/EmailVerificationBlocking';
import { PreLaunchModal } from './components/public/PreLaunchModal';
import { AuthActionHandler } from './components/auth/AuthActionHandler';
import { ToolsDashboard } from './components/tools/ToolsDashboard';

// Helper to get visually striking FUT-like styles based on player category


interface RankingPrize {
  id: string; // `${groupKey}_${position}`
  text?: string;
  imageUrl?: string;
  groupKey: string;
  position: number;
}

interface RoundStatusBadgeProps {
  startDate: Date | null;
  matchState?: 'inactivo' | 'activo' | 'demorado' | 'en_proceso' | 'finalizado' | 'en_disputa';
}

function RoundStatusBadge({ startDate, matchState = 'inactivo' }: RoundStatusBadgeProps) {
  const formattedDate = startDate
    ? startDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })
    : '';

  if (matchState === 'en_disputa') {
    return (
      <div className="flex items-center gap-1 px-1.5 py-[1px] rounded-full bg-red-500/10 border border-red-500/20 shadow-[0_0_12px_rgba(239,68,68,0.15)] backdrop-blur-md">
        <span className="relative flex h-1 w-1 mr-0.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-1 w-1 bg-red-500"></span>
        </span>
        <span className="text-[6.5px] font-black uppercase tracking-widest text-red-300">
          En Disputa
        </span>
      </div>
    );
  }

  if (matchState === 'finalizado') {
    return (
      <div className="flex items-center gap-1 px-1.5 py-[1px] rounded-full bg-blue-500/10 border border-blue-500/20 shadow-[0_0_12px_rgba(59,130,246,0.15)] backdrop-blur-md">
        <CheckCircle2 size={8} className="text-blue-400" />
        <span className="text-[6.5px] font-black uppercase tracking-widest text-blue-300">
          Finalizado
        </span>
      </div>
    );
  }

  if (matchState === 'en_proceso') {
    return (
      <div className="flex items-center gap-1 px-1.5 py-[1px] rounded-full bg-amber-500/10 border border-amber-500/20 shadow-[0_0_12px_rgba(245,158,11,0.15)] backdrop-blur-md">
        <span className="relative flex h-1 w-1 mr-0.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-1 w-1 bg-amber-500"></span>
        </span>
        <span className="text-[6.5px] font-black uppercase tracking-widest text-amber-300">
          En Proceso
        </span>
      </div>
    );
  }

  if (matchState === 'demorado') {
    return (
      <div className="flex items-center gap-1 px-1.5 py-[1px] rounded-full bg-red-500/10 border border-red-500/20 shadow-[0_0_12px_rgba(239,68,68,0.15)] backdrop-blur-md">
        <Clock size={8} className="text-red-400" />
        <span className="text-[6.5px] font-black uppercase tracking-widest text-red-300">
          Demorado
        </span>
      </div>
    );
  }

  if (matchState === 'activo') {
    return (
      <div className="flex items-center gap-1 px-1.5 py-[1px] rounded-full bg-emerald-500/10 border border-emerald-500/20 shadow-[0_0_12px_rgba(16,185,129,0.15)] backdrop-blur-md">
        <span className="relative flex h-1 w-1 mr-0.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-1 w-1 bg-emerald-500"></span>
        </span>
        <span className="text-[6.5px] font-black uppercase tracking-widest text-emerald-300">
          Activo
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 px-1.5 py-[1px] rounded-full bg-gray-500/10 border border-gray-500/20 shadow-[0_0_12px_rgba(107,114,128,0.15)] backdrop-blur-md">
      <Lock size={7.5} className="text-gray-400 shrink-0" />
      <span className="text-[6.5px] font-black uppercase tracking-widest text-gray-300">
        Inactivo
      </span>
    </div>
  );
}

interface RankingRowProps {
  player: Player;
  idx: number;
  globalIdx: number;
  highlightedPlayerId: string | null;
  config: any;
  targetPrize: RankingPrize | undefined;
  userRole: string | null;
  groupKey: string;
  onPlayerClick: (playerId: string) => void;
  onPrizeClick: (groupKey: string, position: number, prize: RankingPrize | undefined) => void;
  pointsToDisplay: number;
}

const RankingRow = React.memo(function RankingRow({
  player: p,
  idx,
  globalIdx,
  highlightedPlayerId,
  config,
  targetPrize,
  userRole,
  groupKey,
  onPlayerClick,
  onPrizeClick,
  pointsToDisplay,
}: RankingRowProps) {
  return (
    <motion.div
      id={`player-row-${p.id}`}
      onClick={() => onPlayerClick(p.id)}
      className={`group flex items-center px-4 md:px-6 py-1 border-b border-white/5 last:border-0 hover:bg-white/5 cursor-pointer transition-all duration-700 relative ${idx % 2 === 1 ? 'bg-white/[0.02]' : 'bg-transparent'} ${p.id === highlightedPlayerId ? 'scale-[1.01] z-10' : ''}`}
      style={p.id === highlightedPlayerId ? {
        backgroundColor: `${config.primaryColor}25`,
        borderLeft: `4px solid ${config.primaryColor}`,
        boxShadow: `0 0 20px ${config.primaryColor}30, inset 0 0 20px ${config.primaryColor}20`,
      } : undefined}
    >
      {/* Position */}
      <div className="w-6 md:w-8 flex justify-center shrink-0">
        <span className={`text-sm md:text-base font-black italic ${globalIdx === 0 ? 'text-yellow-400 drop-shadow-[0_0_10px_rgba(250,204,21,0.5)]' :
          globalIdx === 1 ? 'text-slate-300 drop-shadow-[0_0_10px_rgba(203,213,225,0.5)]' :
            globalIdx === 2 ? 'text-[#CD7F32] drop-shadow-[0_0_10px_rgba(205,127,50,0.5)]' :
              'text-white/40'
          }`}>
          #{globalIdx + 1}
        </span>
      </div>

      {/* Minimalist Vertical Divider 1 */}
      <div className="w-[1px] h-6 bg-white/10 mx-1.5 shrink-0 self-center" />

      {/* Player Info */}
      <div className="flex-1 px-1 md:px-2 flex items-center gap-1.5 min-w-0">
        <div className="relative w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden border border-white/10 shrink-0">
          {p.photoUrl ? (
            <FadeInImage src={p.photoUrl} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${p.gender === 'Femenino' ? 'bg-pink-500/20' : 'bg-blue-500/20'}`}>
              <Users size={16} className="opacity-50" />
            </div>
          )}
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`font-black uppercase tracking-wide text-white truncate group-hover:theme-primary-text transition-colors ${p.name.length > 22
              ? 'text-[8px] md:text-[9px]'
              : p.name.length > 16
                ? 'text-[9px] md:text-[10px]'
                : 'text-[10px] md:text-xs'
              }`}>
              {p.name}
            </span>
          </div>
          {p.nickname && (
            <span className="text-[9px] md:text-[10px] font-bold italic opacity-50 truncate">"{p.nickname}"</span>
          )}
        </div>
      </div>

      {/* Minimalist Vertical Divider 2 */}
      <div className="w-[1px] h-6 bg-white/10 mx-1.5 shrink-0 self-center" />

      {/* Points */}
      <div className="w-10 md:w-12 text-center flex flex-col items-center justify-center shrink-0">
        <span className="text-[12px] md:text-sm font-black italic theme-primary-text drop-shadow-[0_0_8px_rgba(var(--color-primary-0-5))] leading-none">
          {Math.round(pointsToDisplay)}
        </span>
        <span className="text-[6px] md:text-[7px] font-black uppercase tracking-widest opacity-40 mt-0.5 leading-none">PTS</span>
      </div>

    </motion.div>
  );
});

interface PublicSedeCardProps {
  key?: string;
  s: UserProfile;
  user: any;
  setActivePrivateChatRecipientId: (id: string) => void;
  setActivePrivateChatRecipientName: (name: string) => void;
  setActivePrivateChatRecipientPhoto: (photo: string) => void;
  isHighlighted?: boolean;
}

function PublicSedeCard({
  s,
  user,
  setActivePrivateChatRecipientId,
  setActivePrivateChatRecipientName,
  setActivePrivateChatRecipientPhoto,
  isHighlighted
}: PublicSedeCardProps) {
  const [showMap, setShowMap] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHighlighted && cardRef.current) {
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, [isHighlighted]);

  const fullAddressQuery = s.address + (s.city ? `, ${s.city}` : '');
  const mapSearchUrl = s.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddressQuery)}`
    : s.location || '';

  return (
    <div ref={cardRef} className={`bg-white/5 backdrop-blur-xl rounded-[var(--radius-large)] border ${isHighlighted ? 'border-theme-primary shadow-[0_0_30px_rgba(var(--color-primary-0-5))] scale-[1.02]' : 'border-white/10 shadow-xl'} flex flex-col overflow-hidden group transition-all duration-700`}>
      {/* Portada Horizontal con Overlay de Logo y Nombre */}
      <div className="w-full h-40 md:h-48 bg-[#1a1c23] relative overflow-hidden shrink-0 border-b border-white/5">
        {(s.coverUrl || (s.galleryUrls && s.galleryUrls.length > 0)) && (
          <FadeInImage
            src={s.coverUrl || (s.galleryUrls && s.galleryUrls[0]) || ''}
            alt={s.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#15171d] via-black/20 to-black/80"></div>
        
        {/* Logo y Nombre Overlay */}
        <div className="absolute top-4 left-4 right-4 flex items-center gap-2 z-10">
          <div className="w-14 h-14 bg-transparent rounded-full flex items-center justify-center overflow-hidden shrink-0 drop-shadow-[0_0_10px_rgba(0,0,0,0.8)]">
            {s.photoUrl ? (
              <FadeInImage src={s.photoUrl} alt={s.name} className="w-full h-full object-contain" />
            ) : (
              <MapPin className="theme-primary-text opacity-50" />
            )}
          </div>
          <div className="flex-1 min-w-0 text-left drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
            <h3 className="text-xl md:text-2xl font-black leading-tight theme-font-secondary truncate text-white">{s.name}</h3>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-5 flex flex-col flex-1 relative">

        <div className="flex flex-col gap-3 mb-3 relative z-10 w-full">
          <div className="text-xs opacity-80 flex flex-wrap items-center justify-center gap-3 w-full">
            <span className="flex items-center min-w-0 truncate text-white/90 max-w-[200px] sm:max-w-[300px]">
              <MapPin size={14} className="mr-1.5 shrink-0 text-white/50" />
              <span className="truncate font-medium">{s.address || 'Sin dirección registrada'}</span>
            </span>
            {s.address && (
              <button
                onClick={() => setShowMap(!showMap)}
                className={`text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-md border transition-all duration-300 flex items-center gap-1 cursor-pointer shrink-0 ${showMap
                  ? 'theme-primary-bg text-black border-transparent shadow-[0_0_10px_rgba(var(--glow-rgb),0.3)]'
                  : 'bg-white/5 text-white/80 border-white/10 hover:bg-white/10'
                  }`}
              >
                <MapPin size={10} />
                {showMap ? 'Ocultar' : 'Mapa'}
              </button>
            )}

            <div className="bg-white/5 border border-white/10 px-2 py-1 rounded-md flex items-center gap-1.5 shrink-0">
              <div className="w-1.5 h-1.5 rounded-full theme-primary-bg animate-pulse"></div>
              <span className="text-[9px] uppercase font-black tracking-widest text-white/70">
                {s.courtsCount || 1} {s.courtsCount === 1 ? 'Cancha' : 'Canchas'}
              </span>
            </div>
          </div>
        </div>

        {/* Panel Desplegable del Mapa Interactivo */}
        {showMap && s.address && (
          <div className="mt-2 mb-4 overflow-hidden rounded-2xl border border-white/10 shadow-inner animate-in slide-in-from-top-4 duration-300">
            <div className="relative w-full h-[200px] bg-black/40">
              <iframe
                title={`Mapa de ${s.name}`}
                width="100%"
                height="200"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                src={`https://maps.google.com/maps?q=${encodeURIComponent(fullAddressQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                className="opacity-90 hover:opacity-100 transition-opacity duration-300"
              ></iframe>
            </div>

            <div className="bg-black/30 border-t border-white/10 p-2 flex justify-center">
              <a
                href={mapSearchUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-theme-primary hover:scale-105 hover:text-white transition-all duration-300"
              >
                🚙 Cómo llegar
              </a>
            </div>
          </div>
        )}

        <div className="mt-auto pt-4 flex items-center justify-center gap-6 border-t border-white/5 w-full">
            {s.instagram && (
              <a
                href={`https://instagram.com/${s.instagram.replace('@', '')}`}
                target="_blank"
                rel="noreferrer"
                className="p-1 text-pink-500 hover:text-pink-400 hover:scale-125 transition-all duration-300 flex items-center justify-center filter drop-shadow-[0_0_8px_rgba(236,72,153,0.2)] hover:drop-shadow-[0_0_12px_rgba(236,72,153,0.6)] shrink-0"
                title="Instagram"
              >
                <Instagram size={20} />
              </a>
            )}
            {s.website && (
              <a
                href={s.website}
                target="_blank"
                rel="noreferrer"
                className="p-1 text-blue-400 hover:text-blue-300 hover:scale-125 transition-all duration-300 flex items-center justify-center filter drop-shadow-[0_0_8px_rgba(96,165,250,0.2)] hover:drop-shadow-[0_0_12px_rgba(96,165,250,0.6)] shrink-0"
                title="Página Web"
              >
                <Globe size={20} />
              </a>
            )}
            {s.phone && (
              <a
                href={`https://wa.me/${s.phone.replace(new RegExp('[^0-9]', 'g'), '')}`}
                target="_blank"
                rel="noreferrer"
                className="p-1 text-emerald-400 hover:text-emerald-300 hover:scale-125 transition-all duration-300 flex items-center justify-center filter drop-shadow-[0_0_8px_rgba(52,211,153,0.2)] hover:drop-shadow-[0_0_12px_rgba(52,211,153,0.6)] shrink-0"
                title="WhatsApp"
              >
                <MessageCircle size={20} />
              </a>
            )}
            {user && user.uid !== s.uid && (
              <button
                onClick={() => {
                  setActivePrivateChatRecipientId(s.uid);
                  setActivePrivateChatRecipientName(s.name || 'Sede Oficial');
                  setActivePrivateChatRecipientPhoto(s.photoUrl || '');
                  setActivePrivateChatRecipientPhone((s as any).whatsapp || s.phone || '');
                }}
                className="h-8 sm:h-9 px-3 sm:px-4 rounded-xl theme-primary-bg flex items-center justify-center gap-1.5 transition-all hover:scale-105 text-black shrink-0 font-black text-[10px] sm:text-xs uppercase tracking-wider hover:brightness-110" title="Mensaje Privado"
              >
                <MessageSquare size={18} className="stroke-[2.5]" />
                <span>Chat</span>
              </button>
            )}
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// CONFIGURACIÓN DE MODO CONSTRUCCIÓN / LANZAMIENTO
// Cambia a 'false' para desactivar la pantalla de "Sitio en Construcción" para todo el público.
// =========================================================================
export const IS_UNDER_CONSTRUCTION = false




function FixtureTutorial() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-6 flex flex-col gap-2 relative z-10 w-full max-w-md md:max-w-2xl lg:max-w-3xl mx-auto">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-left shadow-lg backdrop-blur-md"
      >
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="w-6 h-6 rounded-full bg-theme-primary/20 flex items-center justify-center text-theme-primary shrink-0">
            <HelpCircle size={14} />
          </div>
          <span className="font-black uppercase tracking-wider text-[9px] sm:text-[10px] md:text-xs truncate whitespace-nowrap">¿Cómo funciona el Fixture autogestionado de RPT?</span>
        </div>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} className="shrink-0 ml-2">
          <ChevronDown size={16} className="text-white/40" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 md:p-5 rounded-xl bg-black/30 border border-white/5 mt-2 backdrop-blur-md shadow-2xl">
              
              <div className="flex flex-col">
                <div className="flex gap-3 border-b border-white/5 pb-4 mb-4">
                  <div className="w-7 h-7 shrink-0 rounded-full bg-theme-primary/20 flex items-center justify-center text-theme-primary font-black text-xs">1</div>
                  <div>
                    <h4 className="text-[11px] font-black uppercase tracking-wider text-yellow-400 mb-1 flex items-center gap-1.5"><MessageCircle size={12}/> Coordinar</h4>
                    <p className="text-[10px] text-white/70 leading-relaxed">Chequea tu próxima fecha y coordina a tiempo con tus rivales usando el chat grupal directo de cada partido.</p>
                  </div>
                </div>

                <div className="flex gap-3 border-b border-white/5 pb-4 mb-4">
                  <div className="w-7 h-7 shrink-0 rounded-full bg-theme-primary/20 flex items-center justify-center text-theme-primary font-black text-xs">2</div>
                  <div>
                    <h4 className="text-[11px] font-black uppercase tracking-wider text-yellow-400 mb-1 flex items-center gap-1.5"><CalendarDays size={12}/> Jugar</h4>
                    <p className="text-[10px] text-white/70 leading-relaxed">Reservar un turno y jugar dentro del tiempo preestablecido (habitualmente 1 semana).</p>
                  </div>
                </div>

                <div className="flex gap-3 border-b border-white/5 pb-4 mb-4">
                  <div className="w-7 h-7 shrink-0 rounded-full bg-theme-primary/20 flex items-center justify-center text-theme-primary font-black text-xs">3</div>
                  <div>
                    <h4 className="text-[11px] font-black uppercase tracking-wider text-yellow-400 mb-1 flex items-center gap-1.5"><Edit2 size={12}/> Resultados</h4>
                    <p className="text-[10px] text-white/70 leading-relaxed">Cualquiera de los jugadores podrá cargar el resultado del partido. Luego, deberá ser confirmado por un jugador rival. En caso de error siempre es posible modificar y proponer un nuevo resultado. El resultado se aprobará automáticamente a las 24hs de cargado en caso de no recibir respuesta.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-7 h-7 shrink-0 rounded-full bg-theme-primary/20 flex items-center justify-center text-theme-primary font-black text-xs">4</div>
                  <div>
                    <h4 className="text-[11px] font-black uppercase tracking-wider text-yellow-400 mb-1 flex items-center gap-1.5"><AlertTriangle size={12}/> Conflictos</h4>
                    <p className="text-[10px] text-white/70 leading-relaxed">En caso de conflicto entre los jugadores, se podrá elevar una disputa o impugnación de resultado para que la revise un administrador u organizador.</p>
                  </div>
                </div>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const FreeDateMatchCard = React.memo(({ match, tournament }: { match: Match, tournament?: Tournament }) => {
  const { startDate, endDate } = getMatchTemporalState(match, tournament);
  
  const formatDateRange = () => {
    if (!startDate || !endDate) return '';
    const f1 = startDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    const f2 = endDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    return `Desde el ${f1} al ${f2}`;
  };

  const isElimination = match.zoneIndex === undefined;

  return (
    <div className="relative group bg-white/5 border border-white/10 rounded-3xl pt-3.5 pb-2.5 px-4 overflow-hidden flex flex-col justify-center items-center opacity-80 min-h-[160px]">
      <div className="absolute top-0 inset-x-0 px-4 py-1.5 bg-white/[0.04] border-b border-white/[0.06] flex items-center justify-between z-20">
        <div className="opacity-0 pointer-events-none w-16" /> {/* Spacer */}
        {startDate && endDate && (
          <div className="flex items-center justify-end">
            <span className="text-[10px] font-black uppercase tracking-wider text-white/50 bg-white/5 px-2 py-0.5 rounded border border-white/5 flex items-center gap-1">
              <CalendarDays size={10} className="text-theme-primary/60" />
              <span>{formatDateRange()}</span>
            </span>
          </div>
        )}
      </div>
      
      <div className="absolute inset-0 bg-white/5 blur-3xl opacity-20" />
      <div className="relative flex flex-col items-center gap-3 text-center mt-6">
        <span className="px-3 py-1.5 rounded-lg bg-white/10 text-white/60 text-[11px] font-black uppercase tracking-widest flex items-center gap-2 border border-white/5">
          {isElimination ? <Award size={14} /> : <Coffee size={14} />} {isElimination ? 'Pase Directo' : 'Fecha Libre'}
        </span>
        <div className="text-[11px] font-medium text-white/50 leading-relaxed max-w-[280px]">
          {isElimination
            ? 'Por tu posición en la clasificación y la cantidad de equipos en la llave, a tu pareja le corresponde un pase directo. Has avanzado automáticamente a la siguiente fase.'
            : 'Debido a la cantidad impar de equipos en tu zona, a tu pareja le corresponde fecha libre. No deben jugar en esta ronda.'}
        </div>
      </div>
    </div>
  );
});

interface GrillaMatchCardProps {
  match: Match;
  roundName?: string;
  teams: Team[];
  tournaments: Tournament[];
  players: Player[];
  user: User | null;
  userRole: string;
  config: Config;
  highlightedMatchId: string | null;
  openPlayerDetails: (id: string | null) => void;
  setSelectedMatchMode: (mode: string) => void;
  setSelectedMatchDetails: (match: Match) => void;
}

const GrillaMatchCard = React.memo(function GrillaMatchCard({
  match,
  teams,
  tournaments,
  players,
  user,
  userRole,
  config,
  highlightedMatchId,
  openPlayerDetails,
  setSelectedMatchMode,
  setSelectedMatchDetails,
}: GrillaMatchCardProps) {
  const getTeam = (teamId?: string) =>
    teams.find((t) => t.id === teamId) || {
      name: 'Equipo Desconocido',
      player1Id: '',
      player2Id: '',
      player3Id: '',
      photoUrl: '',
      id: '',
    };
  const getPlayer = (playerId?: string) => players.find(p => p.id === playerId);

  const t1 = getTeam(match.team1Id);
  const t2 = getTeam(match.team2Id);
  const tournament = tournaments.find(t => t.id === match.tournamentId);
  const { isActive, startDate, endDate, matchState } = getMatchTemporalState(match, tournament);
  
  const isAdmin = userRole === 'admin' || userRole === 'superadmin' || userRole === 'co_admin' || userRole === 'gestor_torneos';
  const isExpressAndNotAdmin = tournament?.managementMode === 'express' && !isAdmin;

  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isDelayed, setIsDelayed] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  let winnerTeamId: string | null = null;
  if (matchState === 'finalizado' && match.reportedResult?.sets) {
    let t1Sets = 0;
    let t2Sets = 0;
    match.reportedResult.sets.forEach(s => {
      if ((s.t1 || 0) > (s.t2 || 0)) t1Sets++;
      else if ((s.t2 || 0) > (s.t1 || 0)) t2Sets++;
    });
    if (t1Sets > t2Sets) winnerTeamId = match.team1Id;
    else if (t2Sets > t1Sets) winnerTeamId = match.team2Id;
  } else if (matchState === 'finalizado' && match.winnerId) {
    winnerTeamId = match.winnerId;
  }

  useEffect(() => {
    if (!endDate || match.status !== 'pending' || !isActive) return;

    const tick = () => {
      const testExpiry = localStorage.getItem(`test_expiry_${match.id}`);
      const now = Date.now();
      const target = testExpiry ? parseInt(testExpiry) : endDate.getTime();
      const diff = target - now;

      if (diff <= 0) {
        setTimeLeft(0);
        if (!isDelayed) {
          setIsDelayed(true);
        }
      } else {
        setTimeLeft(diff);
        if (isDelayed) setIsDelayed(false);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [endDate?.getTime(), match.id, isDelayed, match.status, isActive]);


  const formatDateRange = () => {
    if (!startDate || !endDate) return '';
    const f1 = startDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    const f2 = endDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    return `Desde el ${f1} al ${f2}`;
  };

  const formatExpressDate = () => {
    if (!match.date || !match.time) return 'Horario a confirmar';
    try {
      const [y, m, d] = match.date.split('-');
      const dateObj = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      let dayName = dateObj.toLocaleDateString('es-ES', { weekday: 'short' }).replace('.', '');
      dayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
      return `${dayName} ${parseInt(d, 10)} - ${match.time} hs`;
    } catch {
      return `${match.date} - ${match.time} hs`;
    }
  };

  return (
    <div
      data-match-id={match.id}
      className={`relative overflow-hidden transition-all duration-500 rounded-3xl border shadow-2xl flex flex-col px-4 pb-2.5 pt-3.5 group
        ${isActive ? 'bg-gradient-to-b from-white/10 to-white/[0.02] backdrop-blur-2xl cursor-default' : 'bg-white/[0.02] backdrop-blur-md opacity-60 cursor-default'} 
        ${highlightedMatchId === match.id ? 'ring-2 ring-theme-primary/60 scale-[1.01] z-50 border-theme-primary shadow-[0_0_15px_rgba(var(--theme-primary),0.3)]' : 'border-white/[0.08] hover:border-white/20'}
        ${isDelayed && match.status === 'pending' ? 'ring-1 ring-red-500/30 bg-red-500/[0.03]' : ''}
      `}
    >
      {/* Integrated Top Header Banner */}
      <div className="relative -mx-4 -mt-3.5 mb-1.5 px-4 py-1.5 bg-white/[0.04] border-b border-white/[0.06] rounded-t-3xl flex items-center justify-between z-20">
        <div className="flex justify-start relative z-10">
          <RoundStatusBadge startDate={startDate} matchState={matchState} />
        </div>
        
        {tournament?.managementMode === 'express' ? (
          <div className="flex items-center justify-end relative z-10">
            <span className={`text-[10px] font-black uppercase tracking-wider bg-white/5 px-2 py-0.5 rounded border border-white/5 flex items-center gap-1 ${match.date && match.time ? 'text-white' : 'text-white/50'}`}>
              <CalendarDays size={10} className={match.date && match.time ? 'text-theme-primary' : 'text-white/30'} />
              <span>{formatExpressDate()}</span>
            </span>
          </div>
        ) : (
          startDate && endDate && (
            <div className="flex items-center justify-end relative z-10">
              <span className="text-[10px] font-black uppercase tracking-wider text-white/50 bg-white/5 px-2 py-0.5 rounded border border-white/5 flex items-center gap-1">
                <CalendarDays size={10} className="text-theme-primary/60" />
                <span>{formatDateRange()}</span>
              </span>
            </div>
          )
        )}
      </div>

      {isDelayed && match.status === 'pending' && (
        <div className="absolute inset-0 bg-red-500/[0.03] rounded-2xl pointer-events-none z-0" />
      )}

      {!isActive && (
        <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/25 rounded-2xl backdrop-blur-[2.5px]">
          <div className="bg-black/75 p-3 rounded-full border border-white/10 shadow-[0_8px_25px_rgba(0,0,0,0.5)]">
            <Lock className="w-5 h-5 theme-primary-text" />
          </div>
        </div>
      )}

      <div className={`flex items-center justify-between px-2 ${!isActive ? 'blur-sm grayscale' : ''}`}>
        {/* Team 1 */}
        <div className="flex-1 flex flex-col items-center mt-0">
          <PairAvatar
            p1Id={t1?.player1Id}
            p2Id={t1?.player2Id}
            p1Image={players.find(p => p.id === t1?.player1Id)?.photoUrl}
            p2Image={players.find(p => p.id === t1?.player2Id)?.photoUrl}
            className="w-13 h-13 md:w-15 md:h-15"
            onPlayerClick={openPlayerDetails}
          />
        </div>

        {/* Center Section (Action Area) */}
        <div className="flex flex-col items-center justify-center px-4 shrink-0 relative z-20 translate-y-4">
          {(user &&
            (
              match.participantIds?.includes(user.uid) ||
              t1?.player1Id === user.uid || t1?.player2Id === user.uid ||
              t2?.player1Id === user.uid || t2?.player2Id === user.uid ||
              userRole === 'admin' || userRole === 'superadmin'
            )) && (
              <div className="flex flex-col gap-1.5 items-center">
                {matchState === 'finalizado' ? (
                  <div className="flex flex-col items-center gap-1.5">

                    {match.reportedResult?.sets && match.reportedResult.sets.length > 0 ? (
                      <div className="flex gap-2 items-center mt-0.5">
                        {match.reportedResult.sets.map((s, i) => (
                          <div key={i} className="text-[11px] font-black tracking-wider text-blue-400">
                            {s.t1 ?? '-'}-{s.t2 ?? '-'}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center mt-0.5 text-[11px] font-black tracking-wider text-blue-400">
                        {[match.s1, match.s2, match.s3].filter(Boolean).join(' | ') || [match.reportedResult?.s1, match.reportedResult?.s2, match.reportedResult?.s3].filter(Boolean).join(' | ')}
                      </div>
                    )}
                  </div>
                ) : matchState === 'en_disputa' ? (
                  <div className="flex flex-col items-center text-center max-w-[100px]">
                    <AlertTriangle size={14} className="text-red-400 mb-1" />
                    <span className="text-[7px] text-red-300 uppercase font-bold tracking-widest leading-tight">
                      Resultado Impugnado
                    </span>
                    <span className="text-[6px] text-white/50 uppercase font-medium mt-0.5">
                      En revisión por Admin
                    </span>
                  </div>
                ) : (
                  <>
                    {tournament?.managementMode !== 'express' && (
                      <div className="relative group/coord-btn">
                        <div className="absolute -inset-1.5 bg-yellow-400/10 blur-2xl opacity-0 group-hover/coord-btn:opacity-100 transition-opacity duration-500 rounded-full pointer-events-none" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMatchMode('chat');
                            setSelectedMatchDetails(match);
                          }}
                          className="relative z-10 w-[95px] h-8 flex items-center justify-center gap-1.5 bg-yellow-400/[0.05] backdrop-blur-xl border border-yellow-400/30 rounded-lg shadow-[0_2px_8px_rgba(250,204,21,0.08)] hover:bg-yellow-400/12 hover:border-yellow-400/60 hover:scale-105 transition-all duration-300 overflow-hidden"
                        >
                          <div className="absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent group-hover/coord-btn:translate-x-[100%] transition-transform duration-700 ease-in-out" />
                          <MessageCircle size={12} className="text-yellow-400/80 drop-shadow-[0_0_6px_rgba(250,204,21,0.5)] group-hover/coord-btn:text-yellow-400 group-hover/coord-btn:rotate-12 transition-transform" />
                          <span className="text-[9px] font-black uppercase tracking-tighter text-yellow-400/80 group-hover/coord-btn:text-yellow-400 leading-none">
                            Coordinar
                          </span>
                        </button>
                      </div>
                    )}

                    {!isExpressAndNotAdmin && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (matchState === 'inactivo') return;
                          setSelectedMatchMode('result');
                          setSelectedMatchDetails(match);
                        }}
                        disabled={matchState === 'inactivo'}
                        style={matchState === 'en_proceso' && match.reportedById !== user?.uid ? { animation: 'pulse 1s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : {}}
                        className={`relative z-10 w-[95px] h-8 flex items-center justify-center gap-1.5 rounded-lg shadow-[0_0_6px_rgba(255,255,255,0.04)] transition-all duration-300 group/btn2 ${
                          matchState === 'inactivo' ? 'bg-white/5 opacity-50 cursor-not-allowed' : 
                          matchState === 'en_proceso' && match.reportedById !== user?.uid
                            ? 'w-[105px] h-9 bg-yellow-500/20 backdrop-blur-md border border-white shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                            : 'bg-white/5 backdrop-blur-md border border-white/15 hover:bg-white/10 hover:border-white/45 hover:shadow-[0_0_10px_rgba(255,255,255,0.12)] hover:scale-105'
                        }`}
                      >
                        {matchState === 'inactivo' ? (
                          <Lock size={11} className="text-white/40 shrink-0" />
                        ) : (
                          <Edit2 size={11} className={`text-white/60 group-hover/btn2:text-white transition-colors shrink-0 ${matchState === 'en_proceso' ? 'text-yellow-400 group-hover/btn2:text-yellow-300' : ''}`} />
                        )}
                        <div className="flex flex-col items-center leading-[1.1] text-center">
                          <span className="text-[8.5px] font-black uppercase tracking-[0.15em] text-white/95 group-hover/btn2:text-white transition-colors">
                            {matchState === 'inactivo' ? 'Bloqueado' : matchState === 'en_proceso' ? (match.reportedById !== user?.uid ? 'Validar' : 'Ver') : 'Cargar'}
                          </span>
                          <span className={`font-bold uppercase tracking-[0.05em] text-white/60 group-hover/btn2:text-white transition-colors whitespace-nowrap ${matchState === 'inactivo' ? 'text-[6px]' : 'text-[7px]'}`}>
                            {matchState === 'inactivo' ? 'Cargar Resultado' : matchState === 'en_proceso' ? 'Resultado' : 'Resultados'}
                          </span>
                        </div>
                      </button>
                    )}
                    {matchState === 'en_proceso' && match.reportedById === user?.uid && (
                      <span className="text-[6px] text-yellow-400 uppercase font-black tracking-widest text-center mt-3 leading-[1.1] w-full animate-pulse drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]">Esperando confirmacion<br/>del rival</span>
                    )}
                  </>
                )}
              </div>
            )}
        </div>

        {/* Team 2 */}
        <div className="flex-1 flex flex-col items-center mt-0">
          <PairAvatar
            p1Id={t2?.player1Id}
            p2Id={t2?.player2Id}
            p1Image={players.find(p => p.id === t2?.player1Id)?.photoUrl}
            p2Image={players.find(p => p.id === t2?.player2Id)?.photoUrl}
            className="w-13 h-13 md:w-15 md:h-15"
            onPlayerClick={id => { if (id) openPlayerDetails(id); }}
          />
        </div>
      </div>

      {/* Players Section */}
      <div className="mt-1.5 grid grid-cols-2 gap-x-12 gap-y-0.5 px-1 relative">
        <div className="flex flex-col gap-0.5 relative pl-6">
          {winnerTeamId === match.team1Id && (
            <Trophy size={14} className="text-yellow-400 absolute left-0 top-1/2 -translate-y-1/2" />
          )}
          <div className={`text-[9.5px] md:text-[10px] font-bold uppercase tracking-wider transition-colors flex items-center gap-1 justify-start ${winnerTeamId === match.team1Id ? 'text-theme-primary' : 'text-white/50 hover:text-white/80'}`}>
            <span className="truncate">{formatPlayerName(getPlayer(t1.player1Id)?.name || 'Jugador 1', 'short')}</span>
          </div>
          <div className={`text-[9.5px] md:text-[10px] font-bold uppercase tracking-wider transition-colors flex items-center gap-1 justify-start ${winnerTeamId === match.team1Id ? 'text-theme-primary/80' : 'text-white/30 hover:text-white/60'}`}>
            <span className="truncate">{formatPlayerName(getPlayer(t1.player2Id)?.name || 'Jugador 2', 'short')}</span>
          </div>
        </div>
        <div className="flex flex-col gap-0.5 text-right relative pr-6">
          {winnerTeamId === match.team2Id && (
            <Trophy size={14} className="text-yellow-400 absolute right-0 top-1/2 -translate-y-1/2" />
          )}
          <div className={`text-[9.5px] md:text-[10px] font-bold uppercase tracking-wider transition-colors flex items-center gap-1 justify-end ${winnerTeamId === match.team2Id ? 'text-theme-primary' : 'text-white/50 hover:text-white/80'}`}>
            <span className="truncate">{formatPlayerName(getPlayer(t2.player1Id)?.name || 'Jugador 1', 'short')}</span>
          </div>
          <div className={`text-[9.5px] md:text-[10px] font-bold uppercase tracking-wider transition-colors flex items-center gap-1 justify-end ${winnerTeamId === match.team2Id ? 'text-theme-primary/80' : 'text-white/30 hover:text-white/60'}`}>
            <span className="truncate">{formatPlayerName(getPlayer(t2.player2Id)?.name || 'Jugador 2', 'short')}</span>
          </div>
        </div>
      </div>

      {/* Validation Timer */}
      {matchState === 'en_proceso' && (match.reportedAt || match.updatedAt) && (
        <div className="mt-3 -mb-2.5 -mx-4 bg-black/40 border-t border-white/10 py-1.5 flex justify-center items-center gap-2 z-20">
          <span className="text-[6px] md:text-[6.5px] text-white/60 uppercase font-black tracking-widest text-center">
            Validación automática:
          </span>
          <span className="text-[7.5px] md:text-[8px] text-amber-400 font-black tracking-wider drop-shadow-[0_0_8px_rgba(250,204,21,0.4)]">
            {String(Math.max(0, Math.floor(((24 * 60 * 60 * 1000) - (Date.now() - (match.reportedAt || match.updatedAt || Date.now()))) / (1000 * 60 * 60)))).padStart(2, '0')}:{String(Math.max(0, Math.floor((((24 * 60 * 60 * 1000) - (Date.now() - (match.reportedAt || match.updatedAt || Date.now()))) % (1000 * 60 * 60)) / (1000 * 60)))).padStart(2, '0')} Hs
          </span>
        </div>
      )}

      {/* Delayed Match Warning Modal */}
      <AnimatePresence>
        {showWarning && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowWarning(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-sm bg-[#1a1a1a] border border-red-500/30 rounded-3xl p-6 shadow-2xl overflow-hidden"
            >
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-red-500/10 rounded-full blur-3xl" />
              <div className="relative z-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mb-4 border border-red-500/30 animate-pulse">
                  <AlertTriangle size={32} className="text-red-500" />
                </div>
                <h3 className="text-xl font-black uppercase tracking-wider text-white mb-2">Partido Demorado</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6 font-medium">
                  Este encuentro ha excedido el tiempo reglamentario de inicio. Es obligatorio comenzar a jugar <span className="text-red-400 font-bold">lo antes posible</span> para evitar penalizaciones deportivas, quita de puntos o medidas administrativas por parte de la organización.
                </p>
                <button
                  onClick={() => setShowWarning(false)}
                  className="w-full py-3 bg-red-500 hover:bg-red-600 text-white font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-red-500/20 active:scale-95"
                >
                  Entendido
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
});

export default function App() {
  const { scrollY } = useScroll();
  const isOnline = useNetworkStatus();

  const [globalDialog, setGlobalDialog] = useState<{
    isOpen: boolean;
    type: 'alert' | 'confirm' | 'prompt';
    title: string;
    message: string;
    defaultValue?: string;
    onResolve: (value: any) => void;
  } | null>(null);

  const promptInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const originalAlert = window.alert;
    const originalConfirm = window.confirm;
    const originalPrompt = window.prompt;

    (window as any).alert = (message: string) => {
      return new Promise<void>((resolve) => {
        setGlobalDialog({
          isOpen: true,
          type: 'alert',
          title: 'Notificación',
          message: String(message),
          onResolve: () => {
            setGlobalDialog(null);
            resolve();
          }
        });
      });
    };

    (window as any).confirm = (message: string) => {
      return new Promise<boolean>((resolve) => {
        setGlobalDialog({
          isOpen: true,
          type: 'confirm',
          title: 'Confirmación',
          message: String(message),
          onResolve: (val) => {
            setGlobalDialog(null);
            resolve(val);
          }
        });
      });
    };

    (window as any).prompt = (message: string, defaultValue?: string) => {
      return new Promise<string | null>((resolve) => {
        setGlobalDialog({
          isOpen: true,
          type: 'prompt',
          title: 'Jugador Invitado',
          message: String(message),
          defaultValue: defaultValue || '',
          onResolve: (val) => {
            setGlobalDialog(null);
            resolve(val);
          }
        });
      });
    };

    return () => {
      window.alert = originalAlert;
      window.confirm = originalConfirm;
      window.prompt = originalPrompt;
    };
  }, []);

  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useSessionState('rpt2_activeTab', 'inicio');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [historialTab, setHistorialTab] = useSessionState<'mis_partidos_oficiales' | 'todos_partidos_oficiales' | 'torneos'>('rpt2_historialTab', 'mis_partidos_oficiales');
  const [torneosTab, setTorneosTab] = useSessionState<'mi_fixture' | 'activos' | 'inscripciones'>('rpt2_torneosTab', 'mi_fixture');
  const [activosFilter, setActivosFilter] = useSessionState<'mis_torneos' | 'todos'>('rpt2_activosFilter', 'mis_torneos');
  const [searchTournaments, setSearchTournaments] = useState('');
  const [searchGrilla, setSearchGrilla] = useState('');
  const [expandedBanners, setExpandedBanners] = useState<string[]>([]);
  const [selectedBanner, setSelectedBanner] = useState<string | null>(null);
  const [showRankingInfo, setShowRankingInfo] = useState(false);
  const [adminSection, setAdminSection] = useState('resultados');
  const [searchHistory, setSearchHistory] = useState('');
  const [highlightBookingRequestId, setHighlightBookingRequestId] = useState<string | null>(null);
  const [bookingSubTab, setBookingSubTab] = useSessionState<'grilla' | 'mis-turnos' | 'mis-peticiones'>('rpt2_bookingSubTab', 'grilla');
  const [globalMyTurnosCount, setGlobalMyTurnosCount] = useState<number>(0);
  const [guestTournamentViewId, setGuestTournamentViewId] = useState<string | null>(null);
  const [highlightClubId, setHighlightClubId] = useState<string | null>(null);

  // Scroll al top cuando cambia la pestaña
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [activeTab]);

  // Global History Trap (PWA Anti-Exit)
  useEffect(() => {
    // Configuración inicial del historial
    if (!window.history.state || !window.history.state.isAppTrap) {
      // Creamos el estado "Raíz"
      window.history.replaceState({ isAppRoot: true }, '', window.location.href);
      // Avanzamos al estado "Trampa"
      window.history.pushState({ isAppTrap: true }, '', window.location.href);
    }

    const handlePopState = (e: PopStateEvent) => {
      // Si están retrocediendo desde un modal abierto, no intervenimos.
      // El hook useBackButton cerrará el modal y el usuario volverá a la "Trampa".
      if (e.state && e.state.modalOpen) return;

      // Si el usuario retrocedió y cayó en la "Raíz" (o se salió del estado),
      // lo empujamos inmediatamente hacia adelante usando la navegación nativa del navegador.
      // Esta es la forma oficial y segura en PWAs que no bloquean los navegadores.
      if (!e.state || e.state.isAppRoot) {
        window.history.forward();
        
        // Bonus de UX: Si estaban scrolleados hacia abajo, los llevamos al principio
        // Usamos setTimeout porque el navegador intenta restaurar el scroll original después del popstate
        if (window.scrollY > 50) {
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 50);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const handleUrlParams = () => {
      const pathname = window.location.pathname;

      if (pathname === '/mis-turnos' || pathname === '/mis-turnos/') {
        window.location.replace('/?tab=reservas&subtab=mis-turnos');
        return;
      }

      if (pathname.startsWith('/club/')) {
        const parts = pathname.split('/');
        const clubId = parts[2];
        if (clubId) {
          // Changed to redirect to reservas tab with clubId
          window.location.replace(`/?tab=reservas&clubId=${clubId}`);
          return;
        }
      }

      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      const subtab = params.get('subtab');
      const requestId = params.get('requestId');
      const highlightClub = params.get('highlightClubId');
      const login = params.get('login');

      if (login === 'true') {
        setIsLoginModalOpen(true);
      }

      if (tab) {
        setActiveTab(tab);
        if (tab === 'admin' && subtab) {
          setAdminSection(subtab);
        } else if (tab === 'reservas' && subtab) {
          setBookingSubTab(subtab as any);
        }
      }
      if (requestId) {
        setHighlightBookingRequestId(requestId);
      }
      if (highlightClub) {
        setHighlightClubId(highlightClub);
        // Limpiar completamente la URL para que no queden rastros de tab ni IDs
        window.history.replaceState({}, '', window.location.pathname);
      }
      if (params.get('login') === 'true') {
        setIsLoginModalOpen(true);
        window.history.replaceState({}, '', window.location.pathname);
      }
    };

    handleUrlParams();
    window.addEventListener('popstate', handleUrlParams);
    return () => window.removeEventListener('popstate', handleUrlParams);
  }, []);


  const [filterOrganizer, setFilterOrganizer] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [expandedPublicTournamentId, setExpandedPublicTournamentId] = useState<string | null>(null);
  const [shouldHighlightValidation, setShouldHighlightValidation] = useState(false);
  const [sortHistoryDesc, setSortHistoryDesc] = useState(true);

  const [playersLoaded, setPlayersLoaded] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // States to coordinate initial load and prevent layout flicker
  const [authLoaded, setAuthLoaded] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [matchesLoaded, setMatchesLoaded] = useState(false);
  const [tournamentsLoaded, setTournamentsLoaded] = useState(false);
  const [clubsLoaded, setClubsLoaded] = useState(false);

  const appMountedTime = useRef(Date.now());
  const isInitialAuthCheck = useRef(true);

  const [welcomeMessage, setWelcomeMessage] = useState<{ type: 'new' | 'returning' | 'admin' | 'superadmin', name: string } | null>(null);
  const [currentWelcomeBanner, setCurrentWelcomeBanner] = useState(0);

  const [isAdminModeActive, setIsAdminModeActive] = useState<boolean>(() => {
    return localStorage.getItem('is_admin_mode_active') !== 'false';
  });

  const toggleAdminMode = () => {
    const nextState = !isAdminModeActive;
    setIsAdminModeActive(nextState);
    localStorage.setItem('is_admin_mode_active', String(nextState));
    
    if (nextState && isOrganizerModeActive) {
      setIsOrganizerModeActive(false);
      localStorage.setItem('is_organizer_mode_active', 'false');
    }

    if (!nextState && activeTab === 'admin') {
      setActiveTab('social');
    }
  };

  const [isOrganizerModeActive, setIsOrganizerModeActive] = useState<boolean>(() => {
    return localStorage.getItem('is_organizer_mode_active') === 'true';
  });

  const toggleOrganizerMode = () => {
    const nextState = !isOrganizerModeActive;
    setIsOrganizerModeActive(nextState);
    localStorage.setItem('is_organizer_mode_active', String(nextState));
    if (!nextState && activeTab === 'admin') { // Admin tab doubles as Organizer tab visually
      setActiveTab('social');
    }
  };

  type AppRole = 'superadmin' | 'admin' | 'co_admin' | 'empleado' | 'gestor_turnos' | 'gestor_torneos' | 'user';

  const [realUserRole, setRealUserRole] = useState<AppRole>('user');
  const setUserRole = (roleOrFn: AppRole | ((prev: AppRole) => AppRole)) => {
    setRealUserRole(roleOrFn);
  };
  const userRole = (realUserRole === 'superadmin' && !isAdminModeActive) ? 'user' : realUserRole;

  const [hoveredPlayerStates, setHoveredPlayerStates] = useState<Record<string, string | null>>({});

  const [realUserProfile, setRealUserProfile] = useState<UserProfile | null>(null);
  const setUserProfile = (profileOrFn: UserProfile | null | ((prev: UserProfile | null) => UserProfile | null)) => {
    setRealUserProfile(profileOrFn);
  };
  // Note: userProfile useMemo has been moved below players state definition.
  const [showVerificationBlock, setShowVerificationBlock] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginModalInitialView, setLoginModalInitialView] = useState<'login' | 'register'>('login');
  const [showPreLaunchModal, setShowPreLaunchModal] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);


  const [bypassConstruction, setBypassConstruction] = useState(() => {
    return localStorage.getItem('bypass_construction') === 'true';
  });

  const [logoClicked, setLogoClicked] = useState(() => {
    return localStorage.getItem('logoClicked') === 'true';
  });

  const [selectedMatchDetails, setSelectedMatchDetails] = useState<Match | null>(null);
  const [selectedMatchMode, setSelectedMatchMode] = useState<'chat' | 'result'>('chat');
  const [highlightedMatchId, setHighlightedMatchId] = useState<string | null>(null);
  const [viewingPlayer, setViewingPlayer] = useState<Player | null>(null);
  const [viewingSede, setViewingSede] = useState<UserProfile | null>(null);
  const [gridMode, setGridMode] = useState<'mi_grilla' | 'publica'>('mi_grilla');

  const [confirmDeleteMatchId, setConfirmDeleteMatchId] = useState<string | null>(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  const [globalCity, setGlobalCity] = useState<string>('');

  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [news, setNews] = useState<News[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [sedesPublicas, setSedesPublicas] = useState<UserProfile[]>([]);
  const [registeredUsers, setRegisteredUsers] = useState<UserProfile[]>([]);


  const [notifications, setNotifications] = useState<any[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [highlightNotifId, setHighlightNotifId] = useState<string | null>(null);
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);
  const [sessionConflictError, setSessionConflictError] = useState<string | null>(null);
  const [pendingTakeover, setPendingTakeover] = useState<{ firebaseUser: any; profile: UserProfile } | null>(null);
  const [notificationsLimit, setNotificationsLimit] = useState(5);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const [privateChats, setPrivateChats] = useState<any[]>([]);
  const [supportChats, setSupportChats] = useState<any[]>([]);
  const [showPrivateMessages, setShowPrivateMessages] = useState(false);
  const [privateChatsLimit, setPrivateChatsLimit] = useState(5);

  const [privateMessagesTab, setPrivateMessagesTab] = useSessionState<'chats' | 'requests'>('rpt2_privateMessagesTab', 'chats');
  const [activePrivateChatRecipientId, setActivePrivateChatRecipientId] = useState<string | null>(null);
  const [activePrivateChatRecipientName, setActivePrivateChatRecipientName] = useState<string>('');
  const [activePrivateChatRecipientPhoto, setActivePrivateChatRecipientPhoto] = useState<string>('');
  const [activePrivateChatRecipientPhone, setActivePrivateChatRecipientPhone] = useState<string>('');
  const [activePrivateChatIsSupport, setActivePrivateChatIsSupport] = useState<boolean>(false);
  const [pendingBookingRequestsCount, setPendingBookingRequestsCount] = useState<number>(0);
  const messagesRef = useRef<HTMLDivElement>(null);


  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsTab, setSettingsTab] = useSessionState<'general' | 'notifications'>('rpt2_settingsTab', 'general');

  const [config, setConfig] = useState<Config>({
    pageName: 'Roca Padel Tour',
    showPageName: true,
    pageNameSize: 'text-2xl',
    logoSize: 'h-14 w-14 md:h-20 md:w-20',
    primaryColor: '#eab308',
    secondaryColor: '#fbbf24',
    primaryTextColor: '#ffffff',
    secondaryTextColor: '#a1a1aa',
    bgColor: '#020617',
    cardColor: '#0f172a',
    bgOpacity: 85,
    cardOpacity: 70,
    exportBorderWidth: 0,
    exportBorderColor: '#eab308',
    logoUrl: '',
    bgUrl: 'https://images.unsplash.com/photo-1622204554308-5925bb0902fb?q=80&w=2000&auto=format&fit=crop',
    exportWithBg: true,
    exportBgUrl: '',
    exportBgColor: '',
    exportShowSponsors: false,
    adminPin: 'padel2026',
    superAdminPin: 'super2026',
    showWelcomeMessage: true,
    welcomeBanners: [
      'https://images.unsplash.com/photo-1554068865-c3ce14e1fa62?q=80&w=1500&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1622204554308-5925bb0902fb?q=80&w=1500&auto=format&fit=crop'
    ],
    welcomeBannersDelay: 5000,
    fontPrimary: 'system-ui, sans-serif',
    fontSecondary: 'system-ui, sans-serif',
    resultCardSize: 'md',
    sponsorsCarouselSpeed: 30,
    sponsorsTitleOpacity: 20,

    feature_background: true,
    feature_welcome: true,
    feature_identity: true,
    feature_colors: true,
    feature_stats: true,
    feature_medals: true,
    feature_news: true,
    feature_cardSize: true,
    feature_fonts: true,
    feature_clubs: true,
    autoValidationEnabled: false,
    autoValidationDelay: 3,
  });

  const handleOpenLoginModal = React.useCallback((view: 'login' | 'register' = 'login') => {
    if (config.registrationsEnabled === false && !bypassConstruction) {
      setShowPreLaunchModal(true);
      return;
    }
    setLoginModalInitialView(view);
    setIsLoginModalOpen(true);
  }, [config.registrationsEnabled, bypassConstruction]);

  const [bgLoaded, setBgLoaded] = React.useState(false);

  // Redirigir a usuarios sin sesión que intentan ver un club
  useEffect(() => {
    if (!isInitializing && !user) {
      if (activeTab === 'clubes' && highlightClubId) {
        localStorage.setItem('pendingClubRedirect', highlightClubId);
        setActiveTab('inicio');
        setIsLoginModalOpen(true);
        window.history.replaceState({}, '', '/?tab=inicio&login=true');
      }
    }
  }, [isInitializing, user, activeTab, highlightClubId]);

  // Recuperar redirección después del login
  useEffect(() => {
    if (!isInitializing && user) {
      const pendingId = localStorage.getItem('pendingClubRedirect');
      if (pendingId) {
        localStorage.removeItem('pendingClubRedirect');
        setActiveTab('clubes');
        setHighlightClubId(pendingId);
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [isInitializing, user]);

  // Limpiar el club resaltado cuando el usuario cambia de pestaña
  useEffect(() => {
    if (activeTab !== 'clubes' && highlightClubId) {
      setHighlightClubId(null);
    }
  }, [activeTab, highlightClubId]);

  React.useEffect(() => {
    if (config?.bgUrl) {
      setBgLoaded(false);
      const img = new Image();
      img.src = config.bgUrl;
      img.onload = () => setBgLoaded(true);
      img.onerror = () => setBgLoaded(true);
    } else {
      setBgLoaded(true);
    }
  }, [config?.bgUrl]);

  const userProfile = useMemo(() => {
    if (!realUserProfile) return null;

    const isSuperAdmin = realUserProfile.email === 'agunavarro1989@gmail.com' || realUserProfile.role === 'superadmin';

    // Find player profile of the current user
    const player = players.find(p => p.id === realUserProfile.uid);

    const computedRole = (isSuperAdmin && !isAdminModeActive) ? 'user' : realUserProfile.role;
    const computedPhotoUrl = isSuperAdmin
      ? (isAdminModeActive ? (config?.superAdminPhotoUrl || config?.logoUrl || realUserProfile.photoUrl) : (player?.photoUrl || realUserProfile.photoUrl))
      : realUserProfile.photoUrl;
    const computedName = isSuperAdmin
      ? (isAdminModeActive ? (config?.superAdminName || 'Super Admin') : (player?.name || realUserProfile.name))
      : realUserProfile.name;

    return {
      ...realUserProfile,
      role: computedRole,
      photoUrl: computedPhotoUrl,
      name: computedName,
      isOrganizer: player?.isOrganizer,
      organizerProfile: player?.organizerProfile
    };
  }, [realUserProfile, isAdminModeActive, players, config]);

  useEffect(() => {
    if (!user || (userProfile && (userProfile.role === 'admin' || userProfile.role === 'superadmin'))) {
      setGlobalMyTurnosCount(0);
      return;
    }

    const todayStr = new Date().toISOString().split('T')[0];
    
    const bookingsQ = query(
      collection(db, getCollectionPath('bookings')),
      where('playerId', '==', user.uid)
    );

    const requestsQ = query(
      collection(db, getCollectionPath('bookingRequests')),
      where('playerId', '==', user.uid)
    );

    let bCount = 0;
    let rCount = 0;

    const unsubB = onSnapshot(bookingsQ, (snap) => {
      bCount = snap.docs.filter(d => {
         const data = d.data();
         return data.date >= todayStr && data.status !== 'cancelled' && data.status !== 'rejected' && data.status !== 'paid' && data.status !== 'completed';
      }).length;
      setGlobalMyTurnosCount(bCount + rCount);
    });

    const unsubR = onSnapshot(requestsQ, (snap) => {
      rCount = snap.docs.filter(d => {
         const data = d.data();
         return data.status === 'pending' && data.date >= todayStr;
      }).length;
      setGlobalMyTurnosCount(bCount + rCount);
    });

    return () => {
      unsubB();
      unsubR();
    };
  }, [user, userProfile]);

  const filteredNotifications = useMemo(() => {
    if (!realUserProfile) return [];

    const applyPreferences = (notifList: any[]) => {
      const prefs = realUserProfile.notificationPreferences || {};
      return notifList.filter(n => {
        const t = (n.type || 'general').toLowerCase();
        let category = 'social';
        if (t.includes('chat')) category = 'chat';
        else if (t.includes('booking') || t.includes('turno') || t.includes('reserva')) category = 'bookings';
        else if (t.includes('tournament') || t.includes('match') || t.includes('liga') || t.includes('torneo') || t.includes('admin_match_validation')) category = 'tournaments';
        else if (t.includes('post') || t.includes('comment') || t.includes('mention') || t.includes('social')) category = 'social';
        else if (t.includes('marketing') || t.includes('promo') || t.includes('alert')) category = 'marketing';

        let pref = 'push_and_app';
        
        const isClubNotif = !!n.isStaffCopy || realUserProfile.role === 'admin';

        if (isClubNotif) {
          if (category === 'chat' && prefs.club_chat !== undefined) pref = prefs.club_chat;
          else if (category === 'social' && prefs.club_social !== undefined) pref = prefs.club_social;
          else if (category === 'tournaments' && prefs.club_tournaments !== undefined) pref = prefs.club_tournaments;
          else if (category === 'bookings' && prefs.club_bookings !== undefined) pref = prefs.club_bookings;
          else if (category === 'marketing' && prefs.marketing !== undefined) pref = prefs.marketing;
        } else {
          if (category === 'chat' && prefs.chat !== undefined) pref = prefs.chat;
          else if (category === 'social' && prefs.social !== undefined) pref = prefs.social;
          else if (category === 'tournaments' && prefs.tournaments !== undefined) pref = prefs.tournaments;
          else if (category === 'bookings' && prefs.bookings !== undefined) pref = prefs.bookings;
          else if (category === 'marketing' && prefs.marketing !== undefined) pref = prefs.marketing;
        }
        return pref !== 'off' && (pref as any) !== false;
      });
    };

    // Only apply the superadmin split logic if this is the superadmin user
    if (realUserRole !== 'superadmin' && realUserProfile.role !== 'superadmin') {
      return applyPreferences(notifications);
    }

    return applyPreferences(notifications.filter((n: any) => {
      const isAdminNotif = [
        'player_validation',
        'admin_validation',
        'tournament_registration',
        'admin_category_request',
        'booking_request'
      ].includes(n.type) || (n.type === 'support_chat_message' && n.playerId !== realUserProfile.uid);

      if (isAdminModeActive) {
        return isAdminNotif;
      } else {
        return !isAdminNotif;
      }
    }));
  }, [notifications, realUserProfile, isAdminModeActive, realUserRole]);

  useEffect(() => {
    if (!userProfile) return;
    const checkDelayedMatches = () => {
      matches.forEach(match => {
        if (match.status !== 'pending') return;
        const tournament = tournaments.find(t => t.id === match.tournamentId);
        if (!tournament) return;
        const { isActive, endDate } = getMatchTemporalState(match, tournament);
        
        if (isActive && endDate && endDate.getTime() < Date.now()) {
          const lastSent = localStorage.getItem(`notif_sent_${match.id}_${userProfile.uid}`);
          if (lastSent && Date.now() - parseInt(lastSent) < 86400000) return; // 1 notification per day

          const getTeam = (teamId?: string) => teams.find((t) => t.id === teamId);
          const t1 = getTeam(match.team1Id);
          const t2 = getTeam(match.team2Id);
          
          const organizerId = tournament.organizerId;
          const pIds = match.participantIds || [t1?.player1Id, t1?.player2Id, t2?.player1Id, t2?.player2Id].filter(Boolean) as string[];
          if (organizerId && !pIds.includes(organizerId)) pIds.push(organizerId);

          if (pIds.includes(userProfile.uid)) {
            localStorage.setItem(`notif_sent_${match.id}_${userProfile.uid}`, Date.now().toString());
            try {
              addDoc(collection(db, 'notifications'), {
                userId: userProfile.uid,
                title: '⚠️ Partido Demorado',
                body: `El plazo para jugar el partido entre ${t1?.name || 'Equipo 1'} y ${t2?.name || 'Equipo 2'} ha vencido. Si no se juega a la brevedad, un administrador podrá tomar medidas (Walkover, quita de puntos, etc).`,
                type: 'warning',
                matchId: match.id,
                tournamentId: match.tournamentId || tournament?.id,
                createdAt: Date.now(),
                read: false
              });
            } catch (e) {
              console.error("Error sending delayed notification:", e);
            }
          }
        }
      });
    };

    // Run once on load
    checkDelayedMatches();

    // Run every 10 minutes
    const interval = setInterval(checkDelayedMatches, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userProfile, matches, tournaments, teams]);

  const filteredPrivateChats = useMemo(() => {
    if (!realUserProfile) return [];

    // Only apply for superadmin
    if (realUserRole !== 'superadmin' && realUserProfile.role !== 'superadmin') {
      return privateChats;
    }

    // Map supportChats to private chat structure
    const mappedSupportChats = supportChats.map(sc => {
      const p = players.find(x => x.id === sc.id);
      const name = p?.name || sc.userName || 'Jugador de Soporte';
      const photoUrl = p?.photoUrl || '';
      return {
        id: `support_${sc.id}`,
        participantIds: [realUserProfile.uid, sc.id],
        participantNames: {
          [sc.id]: name
        },
        participantPhotos: {
          [sc.id]: photoUrl
        },
        lastMessageSenderId: sc.lastSenderId || '',
        lastMessage: sc.lastMessage || '',
        updatedAt: sc.lastMessageAt || 0,
        isSupport: true,
        unreadCount: {
          [realUserProfile.uid]: sc.unreadByAdmin ? 1 : 0
        },
        playerId: sc.id
      };
    });

    const combinedChats = [...privateChats, ...mappedSupportChats];
    combinedChats.sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0));

    return combinedChats.filter((chat: any) => {
      const isMatchChat = chat.id.startsWith('match_') || Boolean(chat.matchId);
      const isSupportChat = chat.isSupport === true;

      if (isAdminModeActive) {
        // Admin mode: only see support/admin chats, never match chats or friend chats
        return isSupportChat && !isMatchChat;
      } else {
        // Player mode: see player chats (match chats, direct chats with friends)
        // Never see other players' support chats with us
        return !isSupportChat || isMatchChat;
      }
    });
  }, [privateChats, supportChats, realUserProfile, isAdminModeActive, players, realUserRole]);
  const [prizes, setPrizes] = useState<RankingPrize[]>([]);
  const [editingPrizeData, setEditingPrizeData] = useState<{ groupKey: string, position: number, currentPrize?: RankingPrize } | null>(null);
  const [selectedPrizeDetail, setSelectedPrizeDetail] = useState<RankingPrize | null>(null);

  const [rankingConfig, setRankingConfig] = useState<RankingConfig>({
    champion: 100,
    runnerUp: 50,
    thirdPlace: 30,
    semifinalist: 25,
    participation: 2,
    win20: 40,
    win21: 30,
    loss12: 10,
    loss02: 5,
    streakThreshold: 3,
    streakMultiplier: 1.5
  });

  useEffect(() => {
    if (userRole === 'superadmin' && config.autoCleanupCommunity) {
      const runCleanup = async () => {
        const days = config.autoCleanupDays || 0;
        const hours = config.autoCleanupHours || 0;

        // Safety check to avoid accidental zero-out
        if (days === 0 && hours === 0) return;

        // Ensure we don't run more than once every 5 minutes to avoid spamming Firestore
        const lastRun = parseInt(localStorage.getItem('last_auto_cleanup_ts') || '0', 10);
        const now = Date.now();
        if (now - lastRun < 5 * 60 * 1000) return;

        // Mark as run immediately to prevent race conditions
        localStorage.setItem('last_auto_cleanup_ts', now.toString());

        const millisToSubtract = (days * 24 * 60 * 60 * 1000) + (hours * 60 * 60 * 1000);
        const cutoffTime = Date.now() - millisToSubtract;

        try {
          console.log('🤖 Robot de Limpieza: Iniciando escaneo...');
          const q = query(collection(db, 'posts'), where('createdAt', '<', cutoffTime));
          const snapshot = await getDocs(q);

          let deletedCount = 0;
          for (const document of snapshot.docs) {
            const data = document.data();
            if (data.imagesUrl && Array.isArray(data.imagesUrl)) {
              for (const url of data.imagesUrl) {
                try {
                  await deleteObject(ref(storage, url));
                } catch (e) {
                  console.error('Error al borrar imagen antigua:', e);
                }
              }
            }
            await deleteDoc(doc(db, 'posts', document.id));
            deletedCount++;
          }
          if (deletedCount > 0) {
            console.log(`🤖 Robot de Limpieza: Completado. ${deletedCount} publicaciones borradas.`);
          }
        } catch (e) {
          console.error('🤖 Error en Robot de Limpieza:', e);
        }
      };

      // Ejecutar inmediatamente (si pasaron los 5 min)
      runCleanup();

      // Configurar intervalo para que escanee cada 10 minutos
      const interval = setInterval(runCleanup, 10 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [userRole, config.autoCleanupCommunity, config.autoCleanupDays, config.autoCleanupHours, db]);

  // Asegura que Gestión de Reservas ('turnos') esté seleccionada por defecto al entrar al panel como administrador
  useEffect(() => {
    if (activeTab === 'admin' && userRole === 'admin') {
      const params = new URLSearchParams(window.location.search);
      // Solo sobreescribimos si no hay un subtab explícito en la URL
      if (!params.get('subtab')) {
        setAdminSection('turnos');
      }
    }
  }, [activeTab, userRole]);

  useEffect(() => {
    if (userProfile && (userProfile.role === 'admin' || userProfile.role === 'superadmin')) {
      setBypassConstruction(true);
      localStorage.setItem('bypass_construction', 'true');
    }
  }, [userProfile]);

  // Auto-show PreLaunchModal after 3 seconds for guests
  useEffect(() => {
    if (!user && configLoaded && config.registrationsEnabled === false && !bypassConstruction && activeTab === 'inicio') {
      const timer = setTimeout(() => {
        setShowPreLaunchModal(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [user, configLoaded, config.registrationsEnabled, bypassConstruction, activeTab]);

  useEffect(() => {
    if (!showPrivateMessages) setPrivateChatsLimit(5);
  }, [showPrivateMessages]);

  useEffect(() => {
    if (!showNotifications) setNotificationsLimit(5);
  }, [showNotifications]);

  // Handle openNotifs URL parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldOpenNotifs = params.get('openNotifs') === 'true';
    const highlightNotif = params.get('highlightNotif');

    if (shouldOpenNotifs || highlightNotif) {
      if (shouldOpenNotifs) setShowNotifications(true);
      if (highlightNotif) {
        setHighlightNotifId(highlightNotif);
        setTimeout(() => {
          const el = document.getElementById(`notif-${highlightNotif}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500); // wait for modal to open and render
        setTimeout(() => setHighlightNotifId(null), 5000); // clear after 5s
      }

      // Remove params from URL to avoid reopening on refresh
      const newUrl = window.location.pathname + window.location.search
        .replace(/&?openNotifs=true/g, '')
        .replace(/&?highlightNotif=[^&]+/g, '')
        .replace(/\?$/, '');
      window.history.replaceState({}, '', newUrl || '/');
    }

    const handleOpenNotifsEvent = () => setShowNotifications(true);
    window.addEventListener('openNotifications', handleOpenNotifsEvent);
    return () => window.removeEventListener('openNotifications', handleOpenNotifsEvent);
  }, []);



  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (messagesRef.current && !messagesRef.current.contains(event.target as Node)) {
        setShowPrivateMessages(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    if (showNotifications || showPrivateMessages || showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, showPrivateMessages, showProfileMenu]);

  // Concurrent session heartbeat and active listener
  useEffect(() => {
    if (!user) return;

    // 1. Real-time takeover detection using onSnapshot
    const userDocRef = doc(db, getCollectionPath('users'), user.uid);
    const unsubSession = onSnapshot(userDocRef, async (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const activeSessionId = data.activeSessionId;
        const localSessionId = window.localStorage.getItem('app_session_id');

        // If another session became active on another device/browser
        if (activeSessionId && localSessionId && activeSessionId !== localSessionId) {
          // Sign out immediately
          await signOut(auth);
          setUser(null);
          setUserProfile(null);
          setUserRole('user');
          setProfileLoaded(false);
          localStorage.removeItem('bypass_construction');
          setBypassConstruction(false);
          setSessionConflictError('Tu sesión ha sido iniciada en otro dispositivo. Se ha cerrado la sesión en este dispositivo.');
        }
      }
    }, (error) => {
      console.error("Session snapshot error:", error);
    });

    // 2. Heartbeat inteligente (optimizado) - Solo actualiza cada 5 mins y solo si hubo actividad
    let lastActivityTime = Date.now();

    const updateActivity = () => { lastActivityTime = Date.now(); };
    window.addEventListener('mousemove', updateActivity, { passive: true });
    window.addEventListener('keydown', updateActivity, { passive: true });
    window.addEventListener('touchstart', updateActivity, { passive: true });
    window.addEventListener('scroll', updateActivity, { passive: true });

    // Enviar cada 5 minutos (300000 ms)
    const interval = setInterval(async () => {
      const now = Date.now();
      // Solo hacer el update a Firebase si el usuario interactuó con la app en los últimos 5 minutos
      if (now - lastActivityTime <= 300000) {
        const localSessionId = window.localStorage.getItem('app_session_id');
        if (localSessionId) {
          try {
            await setDoc(userDocRef, {
              lastActiveAt: now
            }, { merge: true });
          } catch (e) {
            console.error("Heartbeat error updating session:", e);
          }
        }
      }
    }, 300000);

    return () => {
      unsubSession();
      clearInterval(interval);
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, [user]);

  // Listener for pending booking requests (Admin/Superadmin only)
  useEffect(() => {
    if (!user || !['admin', 'co_admin', 'empleado', 'gestor_turnos', 'superadmin'].includes(userRole)) {
      setPendingBookingRequestsCount(0);
      return;
    }

    let q;
    if (userRole === 'superadmin') {
      q = query(collection(db, getCollectionPath('bookingRequests')), where('status', '==', 'pending'));
    } else {
      const targetClubId = userProfile?.clubId || user.uid;
      q = query(
        collection(db, getCollectionPath('bookingRequests')),
        where('clubId', '==', targetClubId),
        where('status', '==', 'pending')
      );
    }

    const unsub = onSnapshot(q, (snap) => {
      setPendingBookingRequestsCount(snap.size);
    }, (error) => {
      console.error("Booking requests snapshot error:", error);
    });

    return () => unsub();
  }, [user, userRole, getCollectionPath, userProfile]);

  const currentPlayerDoc = players.find(p => p.id === user?.uid);

  // Initialize globalCity
  useEffect(() => {
    if (!globalCity) {
      if (currentPlayerDoc?.city) setGlobalCity(currentPlayerDoc.city);
      else if (userProfile?.city) setGlobalCity(userProfile.city);
      else if (ALL_CITIES.length > 0) setGlobalCity(ALL_CITIES[0].id);
    }
  }, [currentPlayerDoc?.city, userProfile?.city, ALL_CITIES, globalCity]);

  const isSuspendedActive = !!(
    (userProfile?.isSuspended && (!userProfile.suspendedUntil || userProfile.suspendedUntil > Date.now())) ||
    (currentPlayerDoc?.isSuspended && (!currentPlayerDoc.suspendedUntil || currentPlayerDoc.suspendedUntil > Date.now()))
  );
  const needsProfileSetup = !!(user && profileLoaded && playersLoaded && userRole === 'user' && (!userProfile?.isProfileComplete || !currentPlayerDoc));
  const needsAdminOnboarding = !!(user && profileLoaded && userRole === 'admin' && !userProfile?.isAdminOnboardingComplete);

  // Detect dynamically if the user is on a mobile portrait device to avoid showing the install banner on desktop
  const [isPortraitMobile, setIsPortraitMobile] = useState(() => {
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isPortrait = window.innerHeight > window.innerWidth;
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobileSize = window.innerWidth <= 1024;
    return isPortrait && (isMobileUA || (hasTouch && isMobileSize));
  });

  useEffect(() => {
    const handleResize = () => {
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isPortrait = window.innerHeight > window.innerWidth;
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isMobileSize = window.innerWidth <= 1024;
      setIsPortraitMobile(isPortrait && (isMobileUA || (hasTouch && isMobileSize)));
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const { isInstalled } = usePWAInstall();
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  useEffect(() => {
    // -1. Si NO es un dispositivo móvil en orientación vertical (portrait), NUNCA mostrar la guía de instalación
    if (!isPortraitMobile) {
      setShowInstallGuide(false);
      return;
    }

    // 0. Si el usuario necesita completar el perfil / onboarding, NUNCA mostrar la guía de instalación
    if (needsProfileSetup || needsAdminOnboarding) {
      setShowInstallGuide(false);
      return;
    }

    // 1. SOLO mostrar si el usuario está autenticado y tiene perfil registrado/completo
    if (!user || !userProfile) {
      setShowInstallGuide(false);
      return;
    }

    // 2. Si ya está instalada, no mostrar NUNCA
    if (isInstalled) {
      setShowInstallGuide(false);
      return;
    }

    // 3. Lógica de re-aparición cada 24 horas (respetar la decisión del usuario)
    const lastDismissed = localStorage.getItem('pwa_install_guide_last_dismissed');
    if (lastDismissed) {
      const lastTime = parseInt(lastDismissed, 10);
      const now = Date.now();
      const HOURS_24_MS = 24 * 60 * 60 * 1000;
      if (now - lastTime < HOURS_24_MS) {
        setShowInstallGuide(false);
        return;
      }
    }

    // 4. Inicializar timestamps para primer ingreso
    let firstEnter = localStorage.getItem('pwa_first_enter_timestamp');
    if (!firstEnter) {
      firstEnter = Date.now().toString();
      localStorage.setItem('pwa_first_enter_timestamp', firstEnter);
    }

    const firstEnterTime = parseInt(firstEnter, 10);
    const registrationTime = parseInt(localStorage.getItem('pwa_registration_timestamp') || '0', 10);
    const latestEventTime = Math.max(firstEnterTime, registrationTime);

    const requiredDelay = 15000; // 15 segundos

    const elapsed = Date.now() - latestEventTime;
    const finalDelay = Math.max(0, requiredDelay - elapsed);

    // Si el evento más reciente (ingreso o registro) ocurrió hace menos de 2 segundos,
    // ocultamos preventivamente el banner para respetar el temporizador de 15 segundos.
    if (elapsed < 2000) {
      setShowInstallGuide(false);
    }

    const timer = setTimeout(() => {
      // Doble verificación justo antes de mostrar
      const isStillInstalled =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: minimal-ui)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        (window.navigator as any).standalone === true;

      if (!isStillInstalled) {
        setShowInstallGuide(true);
      }
    }, finalDelay);

    return () => clearTimeout(timer);
  }, [user, userProfile, isInstalled, needsProfileSetup, needsAdminOnboarding, isPortraitMobile]);


  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('padel_notifications_sound');
    return saved ? JSON.parse(saved) : true;
  });

  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    localStorage.setItem('padel_notifications_sound', JSON.stringify(newState));
  };

  // --- Notification sound: single subtle pop ---
  const prevUnreadCountRef = useRef(0);
  useEffect(() => {
    const unreadCount = filteredNotifications.filter(n => !n.read).length;
    if (unreadCount > prevUnreadCountRef.current && soundEnabled) {
      try {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = new AudioContext();
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(520, now);
        osc.frequency.exponentialRampToValueAtTime(280, now + 0.15);
        gain.gain.setValueAtTime(0.09, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(now); osc.stop(now + 0.2);
      } catch (e) {
        console.warn("Audio not supported or blocked");
      }
    }
    prevUnreadCountRef.current = unreadCount;
  }, [filteredNotifications, soundEnabled]);

  // --- Chat message sound: soft low pop ---
  const prevChatUnreadRef = useRef(0);
  useEffect(() => {
    const totalChatUnread = filteredPrivateChats.reduce((acc, chat) => acc + (chat.unreadCount?.[user?.uid] || 0), 0);
    if (totalChatUnread > prevChatUnreadRef.current && soundEnabled) {
      try {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = new AudioContext();
        const now = ctx.currentTime;
        // Single warm pop - C4 to G4
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(262, now);
        osc.frequency.exponentialRampToValueAtTime(392, now + 0.12);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(now); osc.stop(now + 0.3);
      } catch (e) {
        console.warn("Audio not supported or blocked");
      }
    }
    prevChatUnreadRef.current = totalChatUnread;
  }, [filteredPrivateChats, soundEnabled, user?.uid]);




  // Real-time sync for current user profile
  useEffect(() => {
    if (!user?.uid) return;

    const unsub = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as UserProfile;
        
        // --- FIREBASE CACHE OVERWRITE PROTECTION ---
        if (!navigator.onLine) {
          const cachedStr = window.localStorage.getItem(`cached_profile_${user.uid}`);
          if (cachedStr) {
            try {
              const cached = JSON.parse(cachedStr) as UserProfile;
              let cacheIsBetter = false;
              if (!data.role) {
                cacheIsBetter = true; // El documento de caché de Firebase está roto/incompleto (ej. por un merge offline)
              } else {
                if (data.role === 'admin' && !data.isAdminOnboardingComplete && cached.isAdminOnboardingComplete) cacheIsBetter = true;
                if (data.role === 'user' && !data.isProfileComplete && cached.isProfileComplete) cacheIsBetter = true;
              }
              
              if (cacheIsBetter) {
                console.warn("🛡️ MODO OFFLINE: onSnapshot devolvió caché antigua de Firebase. Manteniendo el perfil completo actual.");
                return; // Ignorar esta actualización para no romper el onboarding
              }
            } catch(e) {}
          }
        }
        
        // Mantener la caché local actualizada para el modo sin conexión
        window.localStorage.setItem(`cached_profile_${user.uid}`, JSON.stringify(data));
        
        setUserProfile(prev => {
          // Only update if there are actual changes to avoid loops
          if (JSON.stringify(prev) !== JSON.stringify(data)) {
            return data;
          }
          return prev;
        });
      }
    }, (error) => {
      console.error("User snapshot error:", error);
    });

    return () => unsub();
  }, [user?.uid]);

  // Sincronizar token FCM al iniciar solo si ya tiene permisos Y lo tiene habilitado en su perfil.
  // Además, si está en la app instalada (PWA en modo standalone) y el permiso de notificaciones está en 'default'
  // pero ya tiene las notificaciones habilitadas en su perfil, solicitamos el permiso automáticamente para heredar la configuración.
  useEffect(() => {
    if (!user?.uid || !userProfile?.notificationsEnabled) return;

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;

    if (Notification.permission === 'granted') {
      import('./lib/notifications').then(({ requestNotificationPermission }) => {
        requestNotificationPermission(user.uid, false);
      });
    } else if (Notification.permission === 'default' && isStandalone) {
      console.log('PWA detectada en modo standalone con permisos en default. Solicitando de forma automática...');
      import('./lib/notifications').then(({ requestNotificationPermission }) => {
        requestNotificationPermission(user.uid, false);
      });
    }
  }, [user?.uid, userProfile?.notificationsEnabled]);

  // Handle foreground notifications
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    getMessagingInstance().then(m => {
      if (m) {
        unsubscribe = onMessage(m, (payload) => {
          console.log('Mensaje recibido en primer plano: ', payload);

          if (payload.notification && Notification.permission === 'granted') {
            // Show a native notification since the browser doesn't do it automatically in foreground
            const notificationTitle = payload.notification.title || 'Roca Padel Tour';
            const notificationOptions = {
              body: payload.notification.body,
              icon: '/pwa-192x192.png',
              badge: '/pwa-192x192.png',
              tag: payload.data?.type || 'general',
              data: payload.data
            };

            try {
              if (navigator.serviceWorker && navigator.serviceWorker.ready) {
                navigator.serviceWorker.ready.then(registration => {
                  registration.showNotification(notificationTitle, notificationOptions);
                });
              } else {
                const n = new Notification(notificationTitle, notificationOptions);
                n.onclick = () => {
                  window.focus();
                  if (payload.data?.url) {
                    window.location.href = payload.data.url;
                  }
                };
              }
            } catch (e) {
              console.warn("Could not show foreground notification:", e);
            }
          }
        });
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [supportChatPlayer, setSupportChatPlayer] = useState<Player | null>(null);
  const [isJoiningTournament, setIsJoiningTournament] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [compactNav, setCompactNav] = useState(false);
  const lastScrollY = useRef(0);
  const [showFloatingBack, setShowFloatingBack] = useState(false);
  const [bottomOffset, setBottomOffset] = useState('calc(5.8rem + env(safe-area-inset-bottom, 0px))');
  const [isBannerFixed, setIsBannerFixed] = useState(false);

  // Player filters
  const [searchPlayers, setSearchPlayers] = useState('');
  const [filterPlayersGender, setFilterPlayersGender] = useState('Todas');
  const [filterPlayersCategory, setFilterPlayersCategory] = useState('Todas');
  const [filterCircuit, setFilterCircuit] = useState('global');
  const [isCircuitDropdownOpen, setIsCircuitDropdownOpen] = useState(false);
  const [rankingPages, setRankingPages] = useState<Record<string, number>>({});
  const [highlightedPlayerId, setHighlightedPlayerId] = useState<string | null>(null);
  const isGoingToMyPositionRef = useRef(false);

  const handleGoToMyPosition = () => {
    if (!user || !userProfile) return;
    const currentPlayer = players.find(p => p.id === user.uid);
    if (!currentPlayer) {
      alert('Tu perfil de jugador no fue encontrado en el ranking.');
      return;
    }

    isGoingToMyPositionRef.current = true;
    setSearchPlayers('');
    setFilterPlayersCategory('Todas');
    setFilterPlayersGender('Todas');

    const groupKey = 'General';
    const allNonGuestsInGroup = players
      .filter((p) => !p.isGuest)
      .sort((a, b) => (b.points || 0) - (a.points || 0));
    const playerIndex = allNonGuestsInGroup.findIndex(p => p.id === currentPlayer.id);
    if (playerIndex !== -1) {
      const itemsPerPage = 10;
      const targetPage = Math.floor(playerIndex / itemsPerPage) + 1;
      setRankingPages(prev => ({ ...prev, [groupKey]: targetPage }));
      setHighlightedPlayerId(currentPlayer.id);
      setTimeout(() => {
        const element = document.getElementById(`player-row-${currentPlayer.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
      setTimeout(() => {
        setHighlightedPlayerId(null);
      }, 5000);
    } else {
      alert('No se pudo determinar tu posición en el ranking general.');
      isGoingToMyPositionRef.current = false;
    }
  };

  const handleSavePrize = async (text: string, imageUrl: string, description?: string) => {
    if (!editingPrizeData) return;
    const { groupKey, position } = editingPrizeData;
    const prizeId = `${groupKey}_${position}`.replace(/\s+/g, '_');

    try {
      const prizeRef = doc(db, getCollectionPath('ranking_prizes'), prizeId);
      if (!text && !imageUrl && !description) {
        await deleteDoc(prizeRef);
      } else {
        await setDoc(prizeRef, {
          id: prizeId,
          groupKey,
          position,
          text,
          imageUrl,
          description: description || '',
          updatedAt: Date.now()
        }, { merge: true });
      }
    } catch (err) {
      console.error('Error saving prize:', err);
      throw err;
    }
  };

  const handleDeletePrize = async () => {
    if (!editingPrizeData) return;
    const { groupKey, position } = editingPrizeData;
    const prizeId = `${groupKey}_${position}`.replace(/\s+/g, '_');

    try {
      const prizeRef = doc(db, getCollectionPath('ranking_prizes'), prizeId);
      await deleteDoc(prizeRef);
    } catch (err) {
      console.error('Error deleting prize:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (isGoingToMyPositionRef.current) {
      isGoingToMyPositionRef.current = false;
      return;
    }
    setRankingPages({});
  }, [searchPlayers, filterPlayersGender, filterPlayersCategory]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const diff = currentScrollY - lastScrollY.current;
      
      if (Math.abs(diff) > 10) {
        if (diff > 0 && currentScrollY > 60) {
          setCompactNav(true);
        } else if (diff < 0) {
          setCompactNav(false);
        }
        lastScrollY.current = currentScrollY;
      }
      
      if (currentScrollY < 20) {
        setCompactNav(false);
        lastScrollY.current = currentScrollY;
      }
      setShowScrollTop(window.scrollY > 400);
      // Se muestra el botón flotante móvil después de haber recorrido más scroll down (450px)
      setShowFloatingBack(window.scrollY > 450);

      // Bloqueo dinámico con el borde superior del footer para evitar colisiones
      const footer = document.querySelector('footer');
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const visibleFooterHeight = window.innerHeight - footerRect.top;
        if (visibleFooterHeight > 0) {
          // Usamos CSS max() para evitar que el botón baje de su línea base de 5.8rem
          setBottomOffset(`calc(max(5.8rem, ${visibleFooterHeight}px + 10px) + env(safe-area-inset-bottom, 0px))`);
        } else {
          setBottomOffset('calc(5.8rem + env(safe-area-inset-bottom, 0px))');
        }
      } else {
        setBottomOffset('calc(5.8rem + env(safe-area-inset-bottom, 0px))');
      }

      // Bloqueo dinámico del banner de solicitudes con el borde inferior del menú de navegación
      const nav = document.querySelector('nav');
      if (nav) {
        const navHeight = nav.offsetHeight;
        // Si scrollY es mayor o igual que la altura del menú de navegación, el banner se vuelve fijo
        setIsBannerFixed(window.scrollY >= navHeight);
      } else {
        setIsBannerFixed(window.scrollY >= 96);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    // Inicializar valores en el primer render para evitar saltos visuales
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'grilla') {
      if (searchGrilla.trim() !== '') {
        const query = searchGrilla.toLowerCase().trim();
        const pendingMatches = matches.filter(m => m.status === 'pending' || m.status === 'disputed' || m.status === 'impugned');
        const matchedTournaments: string[] = [];

        pendingMatches.forEach(m => {
          const t1 = teams.find(t => t.id === m.team1Id)?.name?.toLowerCase() || '';
          const t2 = teams.find(t => t.id === m.team2Id)?.name?.toLowerCase() || '';
          if (t1.includes(query) || t2.includes(query)) {
            const tName = m.tournamentName || 'Partidos Amistosos / Manuales';
            matchedTournaments.push(tName);

            const rName = (m.round || '').toLowerCase();
            const isElim = rName.includes('8vos') || rName.includes('4tos') ||
              rName.includes('semi') || rName.includes('final') ||
              rName.includes('eliminaci');
            if (isElim) {
              matchedTournaments.push(`${tName}::Elimination`);
            }
          }
        });

        const uniqueMatched = Array.from(new Set(matchedTournaments));
        setExpandedBanners(uniqueMatched);
        if (uniqueMatched.length > 0) {
          setSelectedBanner(uniqueMatched[0]);
        }
      }
    }
  }, [searchGrilla, activeTab, matches, teams]);

  useEffect(() => {
    if (activeTab === 'torneos' && searchTournaments.trim() !== '') {
      const query = searchTournaments.toLowerCase().trim();
      const matched = tournaments.find(t => t.name?.toLowerCase().includes(query));
      if (matched) {
        setExpandedPublicTournamentId(matched.id);
      }
    }
  }, [searchTournaments, activeTab, tournaments]);



  const intensity = config.bgParallaxIntensity !== undefined ? config.bgParallaxIntensity : 50;
  // Use a fixed ratio based on intensity (e.g. 0.05 to 0.2)
  const ratio = 0.05 + (intensity / 100) * 0.15;

  const [maxScroll, setMaxScroll] = useState(0);

  useEffect(() => {
    const updateMaxScroll = () => {
      setMaxScroll(Math.max(0, document.documentElement.scrollHeight - window.innerHeight));
    };
    updateMaxScroll();

    // Watch for size changes in the body using ResizeObserver
    const observer = new ResizeObserver(updateMaxScroll);
    observer.observe(document.body);
    window.addEventListener('resize', updateMaxScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateMaxScroll);
    };
  }, []);

  const rawBgY = useTransform(scrollY, (y) => -y * ratio);
  const bgY = useSpring(rawBgY, { stiffness: 100, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    let rafId: number;
    const update3D = () => {
      const wrappers = document.querySelectorAll('.sponsor-card-wrapper');
      const centerX = window.innerWidth / 2;
      const baseScale = config.sponsorsCardScale !== undefined ? config.sponsorsCardScale / 100 : 1;
      const intensity = config.sponsors3DIntensity !== undefined ? config.sponsors3DIntensity / 100 : 0.35;
      const use3D = config.sponsors3DEffect === true;
      const useGlow = config.sponsorsGlowEffect === true;
      const glowAmt = config.sponsorsGlowAmount !== undefined ? config.sponsorsGlowAmount : 50;
      const glowColor = config.sponsorsGlowColor || config.sponsorsBgColor || '#ffffff';

      wrappers.forEach(w => {
        const rect = w.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const dist = Math.abs(centerX - itemCenter);

        const maxDist = window.innerWidth / 4;
        const effectRatio = Math.max(0, 1 - dist / maxDist);
        const glowRatio = Math.pow(effectRatio, 2);

        const card = w.querySelector('.sponsor-card');
        const img = w.querySelector('.sponsor-card img, .sponsor-card svg');

        if (card) {
          const scale3D = use3D ? 1 + effectRatio * intensity : 1;
          const finalScale = baseScale * scale3D;
          (card as HTMLElement).style.transform = `scale(${finalScale})`;
          (card as HTMLElement).style.zIndex = Math.round(finalScale * 100).toString();
        }

        if (img) {
          let filterString = '';
          if (useGlow) {
            const currentGlow = glowAmt * glowRatio;
            if (currentGlow > 0) {
              filterString = `drop-shadow(0px 0px ${currentGlow}px ${glowColor})`;
            }
          }

          const isHovered = w.matches(':hover');
          if (isHovered) {
            filterString += ' brightness(1.25)';
          }

          (img as HTMLElement).style.filter = filterString.trim();

          if (card) (card as HTMLElement).style.boxShadow = 'none';
        }
      });
      rafId = requestAnimationFrame(update3D);
    };
    rafId = requestAnimationFrame(update3D);
    return () => {
      cancelAnimationFrame(rafId);
      document.querySelectorAll('.sponsor-card-wrapper .sponsor-card').forEach(c => {
        (c as HTMLElement).style.transform = 'none';
        (c as HTMLElement).style.boxShadow = 'none';
      });
      document.querySelectorAll('.sponsor-card-wrapper .sponsor-card img, .sponsor-card-wrapper .sponsor-card svg').forEach(i => {
        (i as HTMLElement).style.filter = '';
      });
    };
  }, [config.sponsors3DEffect, config.sponsors3DIntensity, config.sponsorsCardScale, config.sponsorsGlowEffect, config.sponsorsGlowAmount, config.sponsorsGlowColor]);

  useEffect(() => {
    if (config.welcomeBanners && config.welcomeBanners.length > 1 && config.showWelcomeMessage !== false && activeTab === 'inicio') {
      const interval = setInterval(() => {
        setCurrentWelcomeBanner((prev) => (prev + 1) % config.welcomeBanners!.length);
      }, config.welcomeBannersDelay || 5000);
      return () => clearInterval(interval);
    }
  }, [config.welcomeBanners, config.welcomeBannersDelay, config.showWelcomeMessage, activeTab]);



  useEffect(() => {
    const handleSwitchToReservas = () => {
      setActiveTab('reservas');
    };
    const handleSwitchToPartidos = () => {
      setActiveTab('partidos');
    };
    const handleChangeBookingSubTab = (e: any) => {
      if (e.detail?.tab) {
        setBookingSubTab(e.detail.tab);
      }
    };
    window.addEventListener('switch-to-reservas', handleSwitchToReservas);
    window.addEventListener('switch-to-partidos', handleSwitchToPartidos);
    window.addEventListener('changeBookingSubTab', handleChangeBookingSubTab);
    return () => {
      window.removeEventListener('switch-to-reservas', handleSwitchToReservas);
      window.removeEventListener('switch-to-partidos', handleSwitchToPartidos);
      window.removeEventListener('changeBookingSubTab', handleChangeBookingSubTab);
    };
  }, []);

  useEffect(() => {
    let firedOnce = false;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Show splash screen on any major auth change to prevent UI flickering
      if (!firedOnce) {
        setIsInitializing(true);
      }
      const startTime = Date.now();
      const minSplashTime = 1500; // Minimum 1.5 seconds to appreciate the brand splash

      if (firebaseUser) {
        // Fetch profile
        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          let profileSnap = null;
          try {
            profileSnap = await getDoc(userDocRef);
          } catch (err) {
            console.warn("Offline o error obteniendo perfil:", err);
          }
          
          let profile = profileSnap?.exists() ? (profileSnap.data() as UserProfile) : null;
          
          if (!profile) {
            const cached = window.localStorage.getItem(`cached_profile_${firebaseUser.uid}`);
            if (cached) {
               profile = JSON.parse(cached);
               console.error(`Cargado desde localStorage puro: ${JSON.stringify(profile)}`);
            } else {
               console.error(`No hay nada en localStorage para ${firebaseUser.uid}`);
            }
          } else {
            // --- FIREBASE CACHE OVERWRITE PROTECTION ---
            if (!navigator.onLine) {
              const cachedStr = window.localStorage.getItem(`cached_profile_${firebaseUser.uid}`);
              if (cachedStr) {
                try {
                  const cached = JSON.parse(cachedStr) as UserProfile;
                  console.error(`Debug Offline: profile.admin=${profile.isAdminOnboardingComplete}, cached.admin=${cached.isAdminOnboardingComplete}`);
                  let cacheIsBetter = false;
                  if (!profile.role) {
                    cacheIsBetter = true; // El documento de caché de Firebase está roto/incompleto (ej. por un merge offline)
                  } else {
                    if (profile.role === 'admin' && !profile.isAdminOnboardingComplete && cached.isAdminOnboardingComplete) cacheIsBetter = true;
                    if (profile.role === 'user' && !profile.isProfileComplete && cached.isProfileComplete) cacheIsBetter = true;
                  }
                  
                  if (cacheIsBetter) {
                    console.warn("🛡️ MODO OFFLINE: getDoc devolvió caché antigua de Firebase. Rescatando perfil de localStorage.");
                    console.error("Protección Offline: getDoc rescatado de localStorage");
                    profile = cached;
                  }
                } catch(e) {}
              } else {
                console.error("Debug Offline: NO HAY CACHED STR");
              }
            } else {
                // If they simulate offline by just disconnecting but navigator.onLine is true?
            }
            window.localStorage.setItem(`cached_profile_${firebaseUser.uid}`, JSON.stringify(profile));
          }

          // Check if this user needs a role upgrade
          const isSuperAdmin = firebaseUser.email === 'agunavarro1989@gmail.com';
          const isAdminPattern = firebaseUser.email?.includes('agunavarro') && !isSuperAdmin;

          let targetRole = profile?.role || 'user';
          let updateNeeded = !profile;

          if (isSuperAdmin) {
            targetRole = 'superadmin';
          } else if (isAdminPattern) {
            targetRole = 'admin';
          } else if (firebaseUser.email) {
            const emailKey = firebaseUser.email.toLowerCase().trim();
            // 1. Check Staff Whitelist
            const staffRoleSnap = await getDoc(doc(db, 'staff_roles', emailKey)).catch(() => null);
            if (staffRoleSnap?.exists()) {
              targetRole = staffRoleSnap.data().role;
              let staffClubId = staffRoleSnap.data().clubId || '';
              if (!staffClubId) {
                const adminUsersSnap = await getDocs(query(collection(db, 'users'), where('role', 'in', ['admin', 'superadmin']))).catch(() => null);
                if (adminUsersSnap && !adminUsersSnap.empty) {
                  staffClubId = adminUsersSnap.docs[0].id;
                }
              }
              if (profile) {
                if ((profile as any).clubId !== staffClubId) {
                  updateNeeded = true;
                  (profile as any).clubId = staffClubId;
                }
              }
            } else {
              // 2. Check Pre-registered Sedes (Invitations)
              const pendingSnap = await getDoc(doc(db, 'pre_registered_sedes', emailKey)).catch(() => null);
              if (pendingSnap?.exists()) {
                targetRole = 'admin';
                const pendingData = pendingSnap.data();
                // Create profile and consume invitation
                const updatedProfile: UserProfile = {
                  ...profile,
                  uid: firebaseUser.uid,
                  email: firebaseUser.email || '',
                  name: pendingData.name || firebaseUser.displayName || 'Nueva Sede',
                  role: 'admin',
                  phone: pendingData.phone || '',
                  instagram: pendingData.instagram || '',
                  address: pendingData.address || '',
                  location: pendingData.location || '',
                  createdAt: profile?.createdAt || Date.now()
                };
                setDoc(doc(db, 'users', firebaseUser.uid), updatedProfile, { merge: true }).catch(err => console.warn('Offline profile create pending', err));
                deleteDoc(doc(db, 'pre_registered_sedes', emailKey)).catch(err => console.warn('Offline invite delete pending', err));
                profile = updatedProfile as UserProfile;
                window.localStorage.setItem(`cached_profile_${firebaseUser.uid}`, JSON.stringify(profile));
                updateNeeded = false; // Already handled
              } else if (['co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos'].includes(profile?.role || '')) {
                // If they were staff but are no longer in staff_roles, downgrade to user
                targetRole = 'user';
              }
            }
          }

          if (profile && (profile.role !== targetRole || (['co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos'].includes(targetRole) && !profile.clubId))) {
            updateNeeded = true;
          }

          if (updateNeeded) {
            let staffClubId = profile ? (profile as any).clubId : undefined;
            if (targetRole === 'user') {
              staffClubId = '';
            } else if (firebaseUser.email && ['co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos'].includes(targetRole)) {
              const staffRoleSnap = await getDoc(doc(db, 'staff_roles', firebaseUser.email.toLowerCase().trim())).catch(() => null);
              if (staffRoleSnap?.exists()) {
                staffClubId = staffRoleSnap.data().clubId || '';
                if (!staffClubId) {
                  const adminUsersSnap = await getDocs(query(collection(db, 'users'), where('role', 'in', ['admin', 'superadmin']))).catch(() => null);
                  if (adminUsersSnap && !adminUsersSnap.empty) {
                    staffClubId = adminUsersSnap.docs[0].id;
                  }
                }
              }
            }
            const updatedProfile: any = {
              ...profile,
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: profile?.name || firebaseUser.displayName || (isSuperAdmin ? 'Super Admin' : (isAdminPattern ? 'Agustin Navarro' : 'Usuario Nuevo')),
              role: targetRole as any,
              clubId: staffClubId !== undefined ? staffClubId : ((profile as any)?.clubId || ''),
              createdAt: profile?.createdAt || Date.now()
            };
            setDoc(doc(db, 'users', firebaseUser.uid), updatedProfile, { merge: true }).catch(err => {
              console.warn("Aviso: No se pudo sincronizar el rol en el servidor, pendiente para cuando haya conexión:", err);
            });
            profile = updatedProfile as UserProfile;
            window.localStorage.setItem(`cached_profile_${firebaseUser.uid}`, JSON.stringify(profile));
          }

          if (profile) {
            // Verificar si hay sesión activa en otro dispositivo (sesiones concurrentes)
            const lastActive = profile.lastActiveAt || 0;
            const activeSessionId = profile.activeSessionId;
            let localSessionId = window.localStorage.getItem('app_session_id');

            // El latido ahora es cada 5 mins, por lo que damos 6 mins de margen (360000 ms)
            if (activeSessionId && (Date.now() - lastActive < 360000) && activeSessionId !== localSessionId) {
              setPendingTakeover({ firebaseUser, profile });
              setIsLoginModalOpen(false);
              setAuthLoaded(true);
              setIsInitializing(false);
              return;
            }

            // Generar o usar un ID de sesión único local si no existe
            if (!localSessionId) {
              localSessionId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
              window.localStorage.setItem('app_session_id', localSessionId);
            }

            // Actualizar la sesión en Firestore sin hacer await para que no bloquee offline
            setDoc(doc(db, getCollectionPath('users'), firebaseUser.uid), {
              activeSessionId: localSessionId,
              lastActiveAt: Date.now()
            }, { merge: true }).catch(e => console.warn('Offline session update pending'));

            // SET ALL STATES TOGETHER PRIOR TO DISMISSING INITIALIZING
            setUser(firebaseUser);
            setUserProfile(profile);
            setUserRole(profile.role);
            setProfileLoaded(true);

            // Check email verification (exempt superadmin)
            const isSuperAdmin = firebaseUser.email === 'agunavarro1989@gmail.com';
            setShowVerificationBlock(!firebaseUser.emailVerified && !!firebaseUser.email && !isSuperAdmin);

            const isStaff = ['admin', 'superadmin', 'co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos'].includes(profile.role);
            const urlParams = new URLSearchParams(window.location.search);
            const hasTabParam = urlParams.has('tab');
            const currentSessionTabRaw = window.sessionStorage.getItem('rpt2_activeTab');
            let currentSessionTabParsed = null;
            try {
              if (currentSessionTabRaw) currentSessionTabParsed = JSON.parse(currentSessionTabRaw);
            } catch (e) {
              currentSessionTabParsed = currentSessionTabRaw;
            }
            const hasSessionTab = !!currentSessionTabParsed;
            const justLoggedIn = window.sessionStorage.getItem('justLoggedIn') === 'true';
            const isFromInicio = currentSessionTabParsed === 'inicio' || currentSessionTabParsed === 'social';

            // Set correct tab based on role IF this is the initial load OR if login just happened from 'inicio'
            // and ONLY if there are no explicit tab parameters in the URL AND (no session tab is saved or just logged in from inicio)
            if (!hasTabParam && (!hasSessionTab || (justLoggedIn && isFromInicio))) {
              if (isStaff) {
                if (profile.role === 'superadmin') {
                  const isAdminMode = window.localStorage.getItem('is_admin_mode_active') === 'true';
                  setActiveTab(isAdminMode ? 'admin' : 'social');
                } else {
                  setActiveTab('admin');
                }
                if (!profile.initialRedirectDone && profile.isAdminOnboardingComplete) {
                  // Mark first admin redirect done in DB and local state
                  const updatedProfile = { ...profile, initialRedirectDone: true };
                  setUserProfile(updatedProfile);
                  setDoc(doc(db, getCollectionPath('users'), firebaseUser.uid), {
                    initialRedirectDone: true
                  }, { merge: true }).catch(err => console.error("Error setting initialRedirectDone:", err));
                }
              } else if (profile.isProfileComplete) {
                if (profile.initialRedirectDone) {
                  // Already completed initial session, always go to Comunidad (social feed)
                  setActiveTab('social');
                } else {
                  // First time post-onboarding, mark redirect done and go to Inicio (social)
                  setActiveTab('social');
                  const updatedProfile = { ...profile, initialRedirectDone: true };
                  setUserProfile(updatedProfile);
                  setDoc(doc(db, getCollectionPath('users'), firebaseUser.uid), {
                    initialRedirectDone: true
                  }, { merge: true }).catch(err => console.error("Error setting initialRedirectDone:", err));
                }
              } else if (justLoggedIn) {
                setActiveTab('perfil');
              }
            }

            if (justLoggedIn) {
              window.sessionStorage.removeItem('justLoggedIn');

              if (isStaff) {
                setWelcomeMessage({
                  type: profile.role === 'superadmin' ? 'superadmin' : 'admin',
                  name: profile.name // Full name for admins (Clubs)
                });
                setTimeout(() => setWelcomeMessage(null), 3000);
              } else if (profile.isProfileComplete) {
                setWelcomeMessage({ type: 'returning', name: profile.name.split(' ')[0] });
                setTimeout(() => setWelcomeMessage(null), 2500);
              }
            }
          }
        } catch (error) {
          console.error("Error in auth initialization:", error);
        } finally {
          setAuthLoaded(true);
          if (!isInitialAuthCheck.current) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, minSplashTime - elapsedTime);

            setTimeout(() => {
              setIsInitializing(false);
              firedOnce = true;
            }, remainingTime);
          }
        }
      } else {
        // Handle Logout or No User
        setUser(null);
        setUserRole('user');
        setUserProfile(null);
        setProfileLoaded(false);
        setAuthLoaded(true);
        setShowVerificationBlock(false);

        if (!isInitialAuthCheck.current) {
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minSplashTime - elapsedTime);

          setTimeout(() => {
            setIsInitializing(false);
            firedOnce = true;
          }, remainingTime);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Sincronización en tiempo real de cambios o revocación de roles de staff
  useEffect(() => {
    if (!user || !user.email) return;
    const emailKey = user.email.toLowerCase().trim();
    const unsub = onSnapshot(doc(db, 'staff_roles', emailKey), async (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const newRole = data.role;
        let newClubId = data.clubId || '';
        if (!newClubId) {
          const adminUsersSnap = await getDocs(query(collection(db, 'users'), where('role', 'in', ['admin', 'superadmin']))).catch(() => null);
          if (adminUsersSnap && !adminUsersSnap.empty) {
            newClubId = adminUsersSnap.docs[0].id;
          }
        }
        setUserProfile(p => p ? { ...p, role: newRole, clubId: newClubId } : null);
        setDoc(doc(db, 'users', user.uid), { role: newRole, clubId: newClubId }, { merge: true }).catch(() => { });
        setUserRole(newRole);
      } else {
        setUserRole(prev => {
          if (['co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos'].includes(prev)) {
            alert("Te han revocado los permisos");
            setUserProfile(p => p ? { ...p, role: 'user', clubId: '' } : null);
            setActiveTab('inicio');
            setDoc(doc(db, 'users', user.uid), { role: 'user', clubId: '' }, { merge: true }).catch(() => { });
            return 'user';
          }
          return prev;
        });
      }
    }, (error) => {
      // Ignorar error de permisos, es normal para usuarios no staff
    });
    return () => unsub();
  }, [user]);

  // Unified coordinator for initial data load to prevent layout/theme flicker
  useEffect(() => {
    if (authLoaded && configLoaded && playersLoaded && matchesLoaded && tournamentsLoaded && clubsLoaded) {
      const elapsedTime = Date.now() - appMountedTime.current;
      const minSplashTime = 1500;
      const remainingTime = Math.max(0, minSplashTime - elapsedTime);

      const timer = setTimeout(() => {
        setIsInitializing(false);
        isInitialAuthCheck.current = false;
      }, remainingTime);

      return () => clearTimeout(timer);
    }
  }, [authLoaded, configLoaded, playersLoaded, matchesLoaded, tournamentsLoaded, clubsLoaded]);

  // Global fallback to prevent freeze if Firestore/network fails
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      setIsInitializing(false);
      isInitialAuthCheck.current = false;
    }, 5000);
    return () => clearTimeout(fallbackTimer);
  }, []);

  // URL Parameter Handling for shared links
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tournamentId = params.get('t');
    const matchId = params.get('match');
    let consumed = false;

    if (tournamentId) {
      if (tournaments.length > 0) {
        const t = tournaments.find(tour => tour.id === tournamentId);
        if (t) {
          if (!user) {
            setGuestTournamentViewId(tournamentId);
          } else {
            setActiveTab('torneos');
            setExpandedPublicTournamentId(tournamentId);
          }
          consumed = true;
        }
      }
    }

    if (matchId) {
      if (matches.length > 0) {
        const m = matches.find(match => match.id === matchId);
        if (m) {
          setSelectedMatchDetails(m);
          consumed = true;
        }
      }
    }

    if (consumed) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [tournaments, matches, user]);

  useEffect(() => {
    // Error handler wrapper for snapshots
    const handleSnapError = (col: string) => (error: any) => {
      // Mark as loaded even on error to prevent loading screen freeze
      if (col === 'players') setPlayersLoaded(true);
      if (col === 'clubs') setClubsLoaded(true);
      if (col === 'settings') setConfigLoaded(true);
      if (col === 'matches') setMatchesLoaded(true);
      if (col === 'tournaments') setTournamentsLoaded(true);

      // Ignore permission-denied errors if we are currently logging out or unmounting
      if (error.code === 'permission-denied') {
        const user = auth.currentUser;
        if (!user) return; // Silently ignore if no user (logout case)

        console.error(`Error in ${col} listener:`, error);
        console.warn("Permission denied details:", {
          collection: col,
          uid: user?.uid,
          email: user?.email,
          authReady: !!user
        });
      } else {
        console.error(`Error in ${col} listener:`, error);
      }
    };

    // Players and News are global (everyone can see them)
    const unsubPlayers = onSnapshot(collection(db, getCollectionPath('players')),
      (snapshot) => {
        setPlayers(snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as any) })));
        setPlayersLoaded(true);
      },
      handleSnapError('players')
    );
    const unsubClubs = onSnapshot(collection(db, getCollectionPath('clubs')),
      (snapshot) => {
        setClubs(snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as any) })));
        setClubsLoaded(true);
      },
      handleSnapError('clubs')
    );
    const unsubSedesPublicas = onSnapshot(query(collection(db, getCollectionPath('users')), where('role', '==', 'admin')),
      (snapshot) => setSedesPublicas(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as any as UserProfile)).filter(sede => !sede.isHidden)),
      handleSnapError('users-sedes')
    );
    let unsubRegisteredUsers = () => { };
    if (user) {
      unsubRegisteredUsers = onSnapshot(collection(db, getCollectionPath('users')),
        (snapshot) => setRegisteredUsers(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as any as UserProfile))),
        handleSnapError('users-registered')
      );
    }
    const unsubConfig = onSnapshot(doc(db, getCollectionPath('settings'), 'main'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setConfig((prev) => ({ ...prev, ...data }));
        }
        setConfigLoaded(true);
      },
      handleSnapError('settings')
    );

    const unsubRankingConfig = onSnapshot(doc(db, getCollectionPath('ranking_config'), 'points_config'),
      (docSnap) => {
        if (docSnap.exists()) {
          setRankingConfig(docSnap.data() as RankingConfig);
        }
      },
      handleSnapError('ranking_config')
    );

    const unsubPrizes = onSnapshot(collection(db, getCollectionPath('ranking_prizes')),
      (snapshot) => {
        setPrizes(snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as any) })));
      },
      handleSnapError('ranking_prizes')
    );

    return () => {
      unsubPlayers();
      unsubClubs();
      unsubSedesPublicas();
      unsubRegisteredUsers();
      unsubConfig();
      unsubRankingConfig();
      unsubPrizes();
    };
  }, [user?.uid]); // Re-run when user changes to properly handle permissions

  // --- FASE B: Optimización de Descargas Globales (Lazy Loading) ---
  useEffect(() => {
    let unsubNews = () => {};
    let unsubTournaments = () => {};
    let unsubTeams = () => {};
    let unsubMatches = () => {};

    // 1. Noticias: Solo se descargan si el usuario está en Inicio, Social (Noticias) o es Admin
    if (activeTab === 'inicio' || activeTab === 'social' || activeTab === 'admin') {
      unsubNews = onSnapshot(collection(db, getCollectionPath('news')),
        (snapshot) => setNews(snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as any) }))),
        () => {} // Ignorar errores de permisos si ocurren
      );
    }

    // 2. Torneos, Equipos y Partidos: 
    // Los descargamos si están en Torneos, Partidos, Perfil, Social (dashboard público), Inicio, o si es Administrador.
    const isAdminOrHistory = activeTab === 'historial' || activeTab === 'admin' || ['superadmin', 'admin', 'co_admin', 'gestor_turnos'].includes(userRole);
    const isGeneralActive = activeTab === 'torneos' || activeTab === 'partidos' || activeTab === 'perfil' || activeTab === 'social' || activeTab === 'inicio';

    if (isAdminOrHistory || isGeneralActive) {
      
      // FASE C: Descargar SOLO los torneos activos (isArchived === false) para usuarios normales
      const tournamentsQuery = isAdminOrHistory 
        ? collection(db, getCollectionPath('tournaments')) // Admins o Historial ven TODO
        : query(collection(db, getCollectionPath('tournaments')), where('isArchived', '==', false)); // Usuarios normales solo ven lo activo

      unsubTournaments = onSnapshot(tournamentsQuery,
        (snapshot) => {
          setTournaments(snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as any) })));
          setTournamentsLoaded(true);
        },
        () => {} 
      );
      unsubTeams = onSnapshot(collection(db, getCollectionPath('teams')),
        (snapshot) => setTeams(snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as any) }))),
        () => {}
      );
      unsubMatches = onSnapshot(collection(db, getCollectionPath('matches')),
        (snapshot) => {
          setMatches(snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as any) })));
          setMatchesLoaded(true);
        },
        () => {}
      );
    } else {
      // Si no los descargamos, marcamos como cargados para no bloquear pantallas de loading
      setTournamentsLoaded(true);
      setMatchesLoaded(true);
    }

    return () => {
      unsubNews();
      unsubTournaments();
      unsubTeams();
      unsubMatches();
    };
  }, [activeTab, user?.uid, userRole]);

  useEffect(() => {
    const handleChangeTab = (e: any) => {
      if (e.detail?.tab) {
        setActiveTab(e.detail.tab);
      }
    };
    window.addEventListener('changeTab', handleChangeTab);
    return () => window.removeEventListener('changeTab', handleChangeTab);
  }, []);

  // Deep linking for shared tournaments
  // Combined deep linking logic for shared items
  useEffect(() => {
    // Only proceed if we have the necessary data
    if (tournaments.length === 0 || matches.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const tId = params.get('t');
    const mId = params.get('match');
    if (!tId && !mId) return;

    let consumed = false;

    // Handle Tournament Deep Link
    if (tId) {
      const tournament = tournaments.find(t => t.id === tId);
      if (tournament) {
        setExpandedPublicTournamentId(tId);
        const isRegistration = !tournament.status || ['Registro', 'Borrador', 'Lobby'].includes(tournament.status);
        const isHistory = tournament.isArchived || tournament.status === 'Finalizado';
        if (isHistory) {
          setActiveTab('historial');
          setHistorialTab('torneos');
        } else {
          setActiveTab('torneos');
          if (isRegistration) setTorneosTab('inscripciones');
          else setTorneosTab('activos');
        }
        consumed = true;
      }
    }

    // Handle Match Deep Link
    if (mId) {
      const match = matches.find(m => m.id === mId);
      if (match) {
        // 1. Set View States
        setActiveTab('grilla');
        setGridMode('publica');
        setSelectedMatchDetails(null);

        // 2. Expand all necessary UI banners
        const tName = match.tournamentName || 'Torneo';
        const bannersToExpand = [tName];
        const tournamentObj = tournaments.find(t => t.id === match.tournamentId);

        if (tournamentObj?.type === 'liga' && match.zoneName && match.zoneName !== 'Zona Única') {
          bannersToExpand.push(`${tName}::${match.zoneName}`);
        }
        const rName = (match.round || '').toLowerCase();
        const isElim = rName.includes('8vos') || rName.includes('4tos') ||
          rName.includes('semi') || rName.includes('final') ||
          rName.includes('eliminaci');
        if (isElim) bannersToExpand.push(`${tName}::Elimination`);

        setExpandedBanners(prev => [...new Set([...prev, ...bannersToExpand])]);

        // 3. Robust scrolling with retry
        let attempts = 0;
        const interval = setInterval(() => {
          const element = document.querySelector(`[data-match-id="${mId}"]`);
          if (element) {
            clearInterval(interval);
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setHighlightedMatchId(mId);
              window.history.replaceState({}, document.title, window.location.pathname);
              setTimeout(() => setHighlightedMatchId(null), 2500);
            }, 150);
          } else if (attempts++ >= 20) {
            clearInterval(interval);
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }, 400);
        consumed = true;
      }
    }

    if (consumed) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [tournaments, matches]);


  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setPrivateChats([]);
      return;
    }
    const qNotifications = query(
      collection(db, getCollectionPath('notifications')),
      where('userId', '==', user.uid)
    );
    const unsubNotifications = onSnapshot(qNotifications, snapshot => {
      const notifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      notifs.sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));
      setNotifications(notifs);
    }, error => {
      console.error('Error fetching notifications:', error);
    });

    const qChats = query(
      collection(db, getCollectionPath('private_chats')),
      where('participantIds', 'array-contains', user.uid)
    );
    const unsubChats = onSnapshot(qChats, snapshot => {
      const chats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      chats.sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0));
      setPrivateChats(chats);
    }, error => {
      console.error('Error fetching private chats:', error);
    });

    return () => {
      unsubNotifications();
      unsubChats();
    };
  }, [user?.uid]);

  // Listen to support_chats if user is superadmin or admin
  useEffect(() => {
    if (!user || !realUserRole || (realUserRole !== 'superadmin' && realUserRole !== 'admin')) {
      setSupportChats([]);
      return;
    }

    const q = query(
      collection(db, getCollectionPath('support_chats'))
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const chats = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      });
      setSupportChats(chats);
    }, (err) => {
      console.error("Error listening to support chats:", err);
    });

    return () => unsub();
  }, [user?.uid, realUserRole]);

  // Keep selected match details in sync with live matches
  useEffect(() => {
    if (selectedMatchDetails) {
      const updatedMatch = matches.find(m => m.id === selectedMatchDetails.id);
      if (updatedMatch && JSON.stringify(updatedMatch) !== JSON.stringify(selectedMatchDetails)) {
        setSelectedMatchDetails(updatedMatch);
      }
    }
  }, [matches, selectedMatchDetails]);

  const handleLoginSuccess = () => {
    window.sessionStorage.setItem('justLoggedIn', 'true');
  };

  const handleAdminLogout = async () => {
    if (user) {
      try {
        await setDoc(doc(db, getCollectionPath('users'), user.uid), {
          activeSessionId: null,
          lastActiveAt: null
        }, { merge: true });
      } catch (err) {
        console.error("Error clearing session on logout:", err);
      }
    }
    await signOut(auth);
    setUser(null);
    setUserProfile(null);
    setUserRole('user');
    window.sessionStorage.removeItem('justLoggedIn');
    localStorage.removeItem('bypass_construction');
    setBypassConstruction(false);
    setActiveTab('inicio');
  };

  const toggleBanner = (id: string) => {
    let nextExpanded: string[] = [];

    setExpandedBanners(prev => {
      const isAlreadyExpanded = prev.includes(id);

      if (isAlreadyExpanded) {
        if (!id.includes('::')) {
          nextExpanded = prev.filter(key => key !== id && !key.startsWith(`${id}::`));
        } else {
          nextExpanded = prev.filter(key => key !== id);
        }
      } else {
        if (id.includes('::')) {
          const tournamentName = id.split('::')[0];
          nextExpanded = [tournamentName, id];
        } else {
          nextExpanded = [id];
        }
      }

      // Update selection based on expansion
      const isNowExpanded = nextExpanded.includes(id);
      setSelectedBanner(isNowExpanded ? id : null);

      return nextExpanded;
    });
  };

  // GrillaMatchCard moved to module scope (above App) to prevent re-creation on every render

  const openPlayerDetails = useCallback((playerId: string | null) => {
    if (!playerId) return;
    const player = players.find((p) => p.id === playerId);
    if (player) setViewingPlayer(player);
  }, [players]);

  const guestRegisterClick = useCallback(() => {
    handleOpenLoginModal('register');
  }, [handleOpenLoginModal]);

  const guestLoginClick = useCallback(() => {
    handleOpenLoginModal('login');
  }, [handleOpenLoginModal]);

  const handlePrizeClick = useCallback((gKey: string, pos: number, tPrize: RankingPrize | undefined) => {
    if (userRole === 'superadmin') {
      setEditingPrizeData({
        groupKey: gKey,
        position: pos,
        currentPrize: tPrize
      });
    } else if (tPrize) {
      setSelectedPrizeDetail(tPrize);
    }
  }, [userRole]);

  const getTeam = (teamId: string) =>
    teams.find((t) => t.id === teamId) || {
      name: 'Equipo Desconocido',
      player1Id: '',
      player2Id: '',
      player3Id: '',
      photoUrl: '',
      phone: '',
    };
  const getPlayer = (pId: string) => players.find((p) => p.id === pId);
  const getTeamPhone = (team: any) => {
    return team?.phone || null;
  };

  const handleJoinTournament = async (tournamentId: string, partnerId: string) => {
    if (!user || !userProfile) {
      handleOpenLoginModal('login');
      return;
    }

    const t = tournaments.find(tour => tour.id === tournamentId);
    if (!t) return;

    setIsJoiningTournament(true);
    try {
      // Find existing team or create one
      let teamId = '';
      const existingTeam = teams.find(team =>
        (team.player1Id === user.uid && team.player2Id === partnerId) ||
        (team.player1Id === partnerId && team.player2Id === user.uid)
      );

      if (existingTeam) {
        teamId = existingTeam.id;
      } else {
        const p1 = userProfile;
        const p2InPlayers = players.find(p => p.id === partnerId);

        // Fallback: try to get partner name from users collection if not in players
        let p2Name = p2InPlayers?.name;
        if (!p2Name) {
          try {
            const partnerDoc = await getDoc(doc(db, getCollectionPath('users'), partnerId));
            if (partnerDoc.exists()) {
              p2Name = partnerDoc.data().name || 'Compañero';
            }
          } catch (e) {
            console.warn('Could not fetch partner from users:', e);
          }
        }
        if (!p2Name) throw new Error("Partner not found");

        const newTeamRef = doc(collection(db, getCollectionPath('teams')));
        teamId = newTeamRef.id;
        await setDoc(newTeamRef, {
          id: teamId,
          name: `${p1.name} & ${p2Name}`,
          player1Id: user.uid,
          player2Id: partnerId,
          organizerId: user.uid,
          createdAt: Date.now()
        }).catch(err => handleFirestoreError(err, 'create', `teams/${teamId}`));
      }

      if (t.pendingTeamIds?.includes(teamId) || t.registeredTeamIds?.includes(teamId)) {
        setIsJoiningTournament(false);
        return;
      }

      const tournamentRef = doc(db, 'tournaments', tournamentId);
      await updateDoc(tournamentRef, {
        pendingTeamIds: [...(t.pendingTeamIds || []), teamId]
      }).catch(err => handleFirestoreError(err, 'update', `tournaments/${tournamentId}`));

      // Notification for the organizer
      const notifIdOrganizer = doc(collection(db, 'notifications')).id;
      await setDoc(doc(db, 'notifications', notifIdOrganizer), {
        id: notifIdOrganizer,
        userId: t.organizerId,
        type: 'tournament_registration',
        title: 'Nueva Pre-Inscripción de Pareja',
        body: `${userProfile.name} quiere unirse a ${t.name} con su pareja.`,
        matchId: tournamentId,
        read: false,
        createdAt: Date.now(),
        createdBy: user.uid
      }).catch(err => handleFirestoreError(err, 'create', `notifications/${notifIdOrganizer}`));

      const notifIdPartner = doc(collection(db, 'notifications')).id;
      await setDoc(doc(db, 'notifications', notifIdPartner), {
        id: notifIdPartner,
        userId: partnerId,
        type: 'tournament_invitation',
        title: 'Invitación a Torneo',
        body: `${userProfile.name} se ha pre-inscripto contigo en ${t.name}.`,
        matchId: tournamentId,
        read: false,
        createdAt: Date.now(),
        createdBy: user.uid
      }).catch(err => handleFirestoreError(err, 'create', `notifications/${notifIdPartner}`));

    } catch (err) {
      console.error("Error joining tournament:", err);
    } finally {
      setIsJoiningTournament(false);
    }
  };

  const handleCancelJoinTournament = async (tournamentId: string) => {
    if (!user || !userProfile) return;
    try {
      const t = tournaments.find(tour => tour.id === tournamentId);
      if (!t || !t.pendingTeamIds) return;

      const teamId = t.pendingTeamIds.find(tid => {
        const team = teams.find(tm => tm.id === tid);
        return team && (team.player1Id === user.uid || team.player2Id === user.uid);
      });

      if (!teamId) return;

      try {
        const tournamentRef = doc(db, getCollectionPath('tournaments'), tournamentId);
        await updateDoc(tournamentRef, {
          pendingTeamIds: t.pendingTeamIds.filter(id => id !== teamId)
        });
        console.log("Successfully updated tournament pendingTeamIds");
      } catch (err) {
        console.error("Error updating tournament pendingTeamIds:", err);
        throw err;
      }

      try {
        const notifsRef = collection(db, 'notifications');
        const q = query(
          notifsRef,
          where('type', '==', 'tournament_registration'),
          where('matchId', '==', tournamentId)
        );
        console.log("Fetching notifications to delete...");
        const querySnapshot = await getDocs(q);
        console.log("Found notifications:", querySnapshot.docs.length);

        const deletions = querySnapshot.docs.map(async (docSnap) => {
          const notifData = docSnap.data();
          if (notifData.type === 'tournament_registration' && notifData.body.includes(userProfile.name)) {
            console.log("Deleting notification:", docSnap.id);
            return deleteDoc(doc(db, 'notifications', docSnap.id));
          }
        });
        await Promise.all(deletions);
        console.log("Successfully deleted notifications");
      } catch (err) {
        console.error("Error with notifications (getDocs/deleteDoc):", err);
        throw err;
      }
    } catch (err) {
      console.error("Error canceling tournament join:", err);
    }
  };

  const handleOpenChatWithOrganizer = (organizerId: string) => {
    const organizer = sedesPublicas.find(s => s.uid === organizerId) || players.find(p => p.id === organizerId);
    if (organizer) {
      setActivePrivateChatRecipientId(organizer.uid || organizer.id);
      setActivePrivateChatRecipientName(organizer.name || 'Organizador');
      setActivePrivateChatRecipientPhoto(organizer.photoUrl || '');
    } else {
      setActivePrivateChatRecipientId(organizerId);
      setActivePrivateChatRecipientName('Organizador');
      setActivePrivateChatRecipientPhoto('');
    }
  };

  const handleClearAllResults = async () => {
    const completed = matches.filter((m) => m.status === 'completed');
    for (const match of completed) {
      await deleteDoc(doc(db, getCollectionPath('matches'), match.id));
    }
    setConfirmClearAll(false);
  };

  const shareResultAsImage = async (match: Match, team1: any, team2: any, action: 'download' | 'whatsapp') => {
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    const headerHeight = (config.exportWithBg !== false && config.logoUrl) ? 220 : 100;

    // Header + Scoreboard Card + Footer
    canvas.height = headerHeight + 850 + (config.exportShowSponsors ? 250 : 0);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    await drawCanvasBg(ctx, canvas, config, true);
    const headerY = await drawCanvasHeader(ctx, canvas, config);

    const fontSecondary = config.fontSecondary || 'sans-serif';
    const isW1 = match.winnerId === team1.id;
    const isW2 = match.winnerId === team2.id;
    const accent = config.secondaryColor || '#fbbf24';
    const primary = config.primaryColor || '#eab308';

    // 1. MATCH INFO BAR
    const infoY = headerY + 80;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = primary;
    ctx.font = `black 28px "${fontSecondary}"`;
    ctx.globalAlpha = 0.4;
    ctx.fillText('• OFFICIAL MATCH REPORT •', canvas.width / 2, infoY);
    ctx.globalAlpha = 1;

    // 2. SCOREBOARD CARD FRAME
    const cardW = 1000;
    const cardH = 500;
    const cardX = (canvas.width - cardW) / 2;
    const cardY = infoY + 60;
    const cornerRadius = 60;

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 40;
    ctx.fillStyle = getRgba(config.cardColor || '#0f172a', 95);
    ctx.beginPath();
    // @ts-ignore
    ctx.roundRect(cardX, cardY, cardW, cardH, cornerRadius);
    ctx.fill();

    // Subtle border
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // 3. TOURNAMENT BADGE ON CARD
    const badgeW = 350;
    const badgeH = 60;
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.beginPath();
    // @ts-ignore
    ctx.roundRect(canvas.width / 2 - badgeW / 2, cardY, badgeW, badgeH, [0, 0, 20, 20]);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 24px "${fontSecondary}"`;
    ctx.fillText(match.tournamentName || (config.pageName ? config.pageName.toUpperCase() : 'ROCA PADEL TOUR'), canvas.width / 2, cardY + 30);

    // 4. TEAMS & SCORE GRID
    const centerCardY = cardY + cardH / 2 + 20;
    const logoSize = 180;
    const teamGap = 150;

    const drawLogo = async (url: string, x: number, y: number, size: number) => {
      try {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = url;
        await new Promise((res) => { img.onload = res; img.onerror = res; });
        if (!img.complete) return;

        ctx.save();
        ctx.beginPath();
        ctx.arc(x + size / 2, y, size / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.fill();
        const s = Math.max(img.width, img.height);
        const scale = size / s;
        ctx.drawImage(img, x + (size - img.width * scale) / 2, y - size / 2 + (size - img.height * scale) / 2, img.width * scale, img.height * scale);
        ctx.restore();
      } catch (e) { console.warn(e); }
    };

    // Draw Score Box
    const scoreBoxW = 280;
    const scoreBoxH = 140;
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.beginPath();
    // @ts-ignore
    ctx.roundRect(canvas.width / 2 - scoreBoxW / 2, centerCardY - 70, scoreBoxW, scoreBoxH, 30);
    ctx.fill();
    ctx.strokeStyle = primary;
    ctx.lineWidth = 4;
    ctx.stroke();

    const scoreStr = [match.s1, match.s2, match.s3].filter(Boolean).join(' ') || match.score || '0-0';
    ctx.fillStyle = accent;
    ctx.font = `900 85px "${fontSecondary}"`; // Use main secondary font for score power
    ctx.fillText(scoreStr, canvas.width / 2, centerCardY);
    ctx.fillStyle = primary;
    ctx.font = `bold 18px "${fontSecondary}"`;
    ctx.globalAlpha = 0.3;
    ctx.fillText('FINAL RESULT', canvas.width / 2, centerCardY + 45);
    ctx.globalAlpha = 1;

    // Team 1
    const t1X = canvas.width / 2 - teamGap - logoSize;
    if (team1.photoUrl) await drawLogo(team1.photoUrl, t1X, centerCardY, logoSize);
    ctx.textAlign = 'center';
    ctx.fillStyle = isW1 ? accent : '#ffffff';
    ctx.font = isW1 ? `900 42px "${fontSecondary}"` : `bold 34px "${fontSecondary}"`;
    ctx.fillText(team1.name + (isW1 ? ' 🏆' : ''), t1X + logoSize / 2, centerCardY + logoSize / 2 + 50);

    // Team 2
    const t2X = canvas.width / 2 + teamGap;
    if (team2.photoUrl) await drawLogo(team2.photoUrl, t2X, centerCardY, logoSize);
    ctx.textAlign = 'center';
    ctx.fillStyle = isW2 ? accent : '#ffffff';
    ctx.font = isW2 ? `900 42px "${fontSecondary}"` : `bold 34px "${fontSecondary}"`;
    ctx.fillText(team2.name + (isW2 ? ' 🏆' : ''), t2X + logoSize / 2, centerCardY + logoSize / 2 + 50);

    // 5. FOOTER OF CARD (Matchday)
    ctx.fillStyle = 'rgba(255,255,255,0.03)';
    ctx.fillRect(cardX, cardY + cardH - 80, cardW, 80);
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = 0.4;
    ctx.font = `bold 22px "${fontSecondary}"`;
    ctx.textAlign = 'left';
    ctx.fillText('DATA LOG_DATE: ' + (match.createdAt ? new Date(match.createdAt).toLocaleDateString() : 'NO_DATA'), cardX + 40, cardY + cardH - 40);
    ctx.textAlign = 'right';
    ctx.fillStyle = primary;
    ctx.fillText(match.round || match.zoneName || 'MATCHDAY 01', cardX + cardW - 40, cardY + cardH - 40);
    ctx.globalAlpha = 1;

    await drawExportFooter(ctx, canvas, config, clubs);
    drawExportBorder(ctx, canvas, config);
    await handleShareCanvas(canvas, `acta-partido-${match.round || 'resultado'}.png`, action);
  };

  const isAdminOrSuper = ['admin', 'superadmin', 'co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos'].includes(userRole || '') || (userProfile?.isOrganizer && isOrganizerModeActive);

  const cardSizeClasses = {
    sm: { pad: 'p-4 pt-6', text: 'text-base md:text-lg', icon: 'w-5 h-5', resultText: 'text-xl' },
    md: { pad: 'p-6 pt-8', text: 'text-lg md:text-xl', icon: 'w-6 h-6', resultText: 'text-2xl' },
    lg: { pad: 'p-8 pt-10', text: 'text-xl md:text-2xl', icon: 'w-8 h-8', resultText: 'text-3xl' },
  }[config.resultCardSize || 'md'];

  if (isInitializing) {
    return (
      <div className="fixed inset-0 z-[200] bg-[#020617] flex items-center justify-center">
        <div className="relative flex flex-col items-center justify-center">
          <motion.div
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1.06, opacity: 1 }}
            transition={{
              scale: {
                duration: 1.8,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              },
              opacity: {
                duration: 0.5,
                ease: "easeOut"
              }
            }}
            className="flex items-center justify-center"
          >
            <img
              src="/pwa-192x192.png"
              alt="Logo"
              className="h-28 w-28 md:h-32 md:w-32 object-contain rounded-2xl"
            />
          </motion.div>
        </div>
      </div>
    );
  }

  if (needsAdminOnboarding && user) {
    if (!navigator.onLine) {
       console.error(`Mostrando Admin Onboarding OFFLINE! userRole=${userRole}, profileLoaded=${profileLoaded}, userProfile=${JSON.stringify(userProfile)}`);
    }
    return (
      <div className="min-h-screen font-sans relative overflow-x-hidden theme-text theme-font-primary">
        <style>{`
          :root { 
            --primary-color: ${config.primaryColor || '#eab308'};
            --secondary-color: ${config.secondaryColor || '#fbbf24'};
            --primary-text-color: ${config.primaryTextColor || '#ffffff'};
            --secondary-text-color: ${config.secondaryTextColor || '#a1a1aa'};
            ${(() => {
            const hex = config.glowColor || '#ffffff';
            let r = 255, g = 255, b = 255;
            if (hex.startsWith('#')) {
              let h = hex.slice(1);
              if (h.length === 3) h = h.split('').map(x => x + x).join('');
              const num = parseInt(h, 16);
              r = (num >> 16) & 255;
              g = (num >> 8) & 255;
              b = num & 255;
            }
            return '--glow-rgb: ' + r + ', ' + g + ', ' + b + '; ';
          })()}
            ${(() => {
            const largeMap: Record<string, string> = { 'none': '0px', 'sm': '1rem', 'md': '1.5rem', 'lg': '2rem', 'xl': '2.5rem', '2xl': '3rem', '3xl': '3.5rem', '4xl': '4rem', 'full': '9999px' };
            return '--radius-large: ' + (largeMap[config.largeBorderRadius || '3xl'] || '2.5rem') + '; ';
          })()}
            ${(() => {
            if (!config.borderRadius) return '';
            const radiusValues: Record<string, string> = {
              'none': '0px',
              'sm': '0.3rem',
              'md': '0.5rem',
              'lg': '0.8rem',
              'xl': '1rem',
              '2xl': '1.5rem',
              '3xl': '2rem',
              'full': '9999px'
            };
            const r = radiusValues[config.borderRadius] || '1rem';
            let rootRadius = '';
            ['sm', 'md', 'lg', 'xl', '2xl', '3xl'].forEach(k => {
              rootRadius += '--radius-' + k + ': ' + r + '; ';
            });
            if (config.borderRadius === 'none') {
              rootRadius += '--radius-full: 0px; ';
            } else if (config.borderRadius === 'full') {
              rootRadius += '--radius-full: 9999px; ';
            }
            return rootRadius;
          })()}
          }
          .theme-font-primary { font-family: ${config.fontPrimary || 'system-ui, sans-serif'}; }
          h1, h2, h3, h4, h5, h6, .font-black, .theme-font-secondary { font-family: ${config.fontSecondary || config.fontPrimary || 'system-ui, sans-serif'}; }
          body { color: ${config.primaryTextColor || '#ffffff'}; }
          .theme-text { color: ${config.primaryTextColor || '#ffffff'}; }
          .theme-secondary-text-color { color: ${config.secondaryTextColor || '#a1a1aa'}; }
          .theme-title-text { color: ${config.primaryColor || '#eab308'}; }
          .theme-bg-base { background-color: ${getRgba(config.bgColor || '#020617', config.bgOpacity !== undefined ? config.bgOpacity : 85)}; }
          .theme-bg-card { background-color: ${getRgba(config.cardColor || '#0f172a', config.cardOpacity !== undefined ? config.cardOpacity : 70)}; }
          .theme-primary-text { color: ${config.primaryColor || '#eab308'}; }
          .theme-primary-bg { background-color: ${config.primaryColor || '#eab308'}; color: #000; }
          .theme-primary-border { border-color: ${config.primaryColor || '#eab308'}; }
          .theme-secondary-text { color: ${config.secondaryColor || '#fbbf24'}; }
          .theme-secondary-bg { background-color: ${config.secondaryColor || '#fbbf24'}; color: #000; }
          .theme-secondary-border { border-color: ${config.secondaryColor || '#fbbf24'}; }
          .theme-accent-text { color: ${config.secondaryColor || '#fbbf24'}; }
          .theme-accent-bg { background-color: ${config.secondaryColor || '#fbbf24'}; color: #000; }
          .theme-accent-border { border-color: ${config.secondaryColor || '#fbbf24'}; }
        `}</style>
        <AnimatePresence>
          <AdminOnboardingModal
            user={{ uid: user.uid, email: user.email, displayName: user.displayName }}
            db={db}
            config={config}
            onComplete={(updatedProfile) => {
              setUserProfile(updatedProfile);
              setWelcomeMessage({ type: 'admin', name: updatedProfile.name.split(' ')[0] });
              setTimeout(() => setWelcomeMessage(null), 3500);
            }}
          />
        </AnimatePresence>
      </div>
    );
  }



  const isUserStaffOrAdmin = userProfile && ['superadmin', 'admin', 'co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos'].includes(userProfile.role);
  const showUnderConstruction = (IS_UNDER_CONSTRUCTION || config.maintenanceMode) && !isInitializing && !bypassConstruction && !isUserStaffOrAdmin;

  if (showUnderConstruction) {
    return (
      <>
        <UnderConstruction
          logoUrl={config.logoUrl}
          primaryColor={config.primaryColor}
          instagram={config.instagram}
          phone={config.phone}
          onAdminLoginClick={() => {
            setLoginModalInitialView('login');
            setIsLoginModalOpen(true);
          }}
        />
        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onSuccess={handleLoginSuccess}
          initialView={loginModalInitialView}
          config={config}
        />
      </>
    );
  }

  const renderSponsorsCarousel = (isFixedBottom = false, hideTitle = false) => {
    if (config.feature_clubs === false || clubs.filter(c => c.type === 'sponsor').length === 0) return null;

    return (
      <div className={`${isFixedBottom ? 'lg:hidden fixed bottom-0 left-0 right-0 z-[90] bg-gradient-to-b from-slate-800 to-slate-950 border-t border-white/10 py-2.5' : `${hideTitle ? 'mb-[-12px] mt-3 max-w-5xl mx-auto px-4 md:px-8' : 'mb-0 mt-2 max-w-full'} relative overflow-hidden w-full`}`}>
        {!isFixedBottom && !hideTitle && (
          <div className="flex items-center justify-center gap-4 mb-4 mt-2 w-full relative">
            <div className="h-px bg-white opacity-50 flex-1 max-w-[30px] md:max-w-[50px]" />
            <h3
              className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.5em] text-center italic m-0 opacity-50"
              style={{ color: config.sponsorsTextColor || '#ffffff' }}
            >
              {config.sponsorsCarouselTitle || 'PARTNERS OFICIALES'}
            </h3>
            <div className="h-px bg-white opacity-50 flex-1 max-w-[30px] md:max-w-[50px]" />
          </div>
        )}

        {/* Infinite Carousel Logo Scroller */}
        <div
          className={`flex overflow-hidden w-full max-w-full ${isFixedBottom ? 'py-0.5' : hideTitle ? 'py-1' : 'pb-2 pt-2'} sponsor-carousel-container`}
          style={!isFixedBottom && config.sponsorsEdgeFade !== false ? {
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
            maskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)'
          } : undefined}
        >
          <motion.div
            animate={{ x: [0, "-50%"] }}
            transition={{
              duration: config.sponsorsCarouselSpeed || 30,
              repeat: Infinity,
              ease: "linear"
            }}
            className="flex items-center whitespace-nowrap min-w-max"
            style={{
              gap: hideTitle ? '24px' : (config.sponsorsGap !== undefined ? `${config.sponsorsGap}px` : '20px'),
              paddingRight: hideTitle ? '24px' : (config.sponsorsGap !== undefined ? `${config.sponsorsGap}px` : '20px'),
              willChange: 'transform',
            }}
          >
            {[...clubs.filter(c => c.type === 'sponsor'), ...clubs.filter(c => c.type === 'sponsor'), ...clubs.filter(c => c.type === 'sponsor'), ...clubs.filter(c => c.type === 'sponsor')].map((c, i) => {
              const isSmall = config.sponsorsCardSize === 'sm' || isFixedBottom;
              const isLarge = !isFixedBottom && config.sponsorsCardSize === 'lg';
              const hasOuterBox = config.sponsorsOuterBox !== false && !isFixedBottom;
              const hasInnerBox = config.sponsorsInnerBox !== false && !isFixedBottom;
              const hasGlow = config.sponsorsGlowEffect === true;
              const showText = config.sponsorsShowText !== false;

              const glowAmt = config.sponsorsGlowAmount !== undefined ? config.sponsorsGlowAmount : 50;
              const outerOpac = config.sponsorsOuterBoxOpacity !== undefined ? config.sponsorsOuterBoxOpacity : 3;
              const innerOpac = config.sponsorsInnerBoxOpacity !== undefined ? config.sponsorsInnerBoxOpacity : 5;

              const content = (
                <div
                  key={`${c.id}-${i}`}
                  className={`sponsor-card group flex items-center transition-all cursor-pointer hover:bg-theme-primary/5 ${hideTitle ? 'opacity-[0.65] hover:opacity-100 duration-300' : ''} ${isSmall
                      ? (hideTitle ? 'px-2 py-0.5' : 'px-4 py-2')
                      : isLarge
                        ? (hideTitle ? 'px-5 py-2' : 'px-10 py-5')
                        : (hideTitle ? 'px-4 py-1' : 'px-7.5 py-3.5')
                    } ${hasOuterBox ? 'rounded-2xl border border-white/5 hover:border-theme-primary/30' : ''}`}
                  style={{
                    backgroundColor: hasOuterBox ? getRgba(config.sponsorsBgColor || '#ffffff', outerOpac) : 'transparent',
                    boxShadow: 'none',
                    gap: showText ? (isLarge ? '1.25rem' : '0.75rem') : '0'
                  }}
                >
                  <div
                    className={`${isSmall
                        ? (hideTitle ? 'w-[20px] h-[20px] rounded-lg p-0.5' : 'w-[37px] h-[37px] rounded-lg p-1.5')
                        : isLarge
                          ? (hideTitle ? 'w-[58px] h-[58px] rounded-2xl p-1.5' : 'w-[106px] h-[106px] rounded-2xl p-3.5')
                          : (hideTitle ? 'w-[34px] h-[34px] rounded-2xl p-1' : 'w-[64px] h-[64px] rounded-2xl p-2.5')
                      } flex items-center justify-center group-hover:scale-110 transition-transform ${hasInnerBox ? 'bg-white/5 border border-white/5' : ''
                      }`}
                    style={{
                      backgroundColor: hasInnerBox ? getRgba(config.sponsorsBgColor || '#ffffff', innerOpac) : 'transparent'
                    }}
                  >
                    {c.photoUrl ? (
                      <FadeInImage src={c.photoUrl} alt={c.name} className="w-full h-full object-contain transition-all duration-300" noPlaceholderBg={true} />
                    ) : (
                      <Star className="opacity-20" size={isSmall ? (hideTitle ? 12 : 14) : isLarge ? (hideTitle ? 25 : 28) : (hideTitle ? 18 : 20)} />
                    )}
                  </div>
                  {showText && (
                    <div className="flex flex-col">
                      <span
                        className={`font-black tracking-tighter`}
                        style={{
                          color: config.sponsorsTextColor || 'var(--theme-title)',
                          fontSize: `${(isSmall ? 10 : isLarge ? 18 : 12) * (config.sponsorsTextScale !== undefined ? config.sponsorsTextScale / 100 : 1)}px`
                        }}
                      >{c.name}</span>
                      {!isFixedBottom && config.sponsorsShowSubtitle !== false && (
                        <span
                          className={`font-bold opacity-30 uppercase tracking-widest mt-0.5`}
                          style={{
                            color: config.sponsorsTextColor || '#ffffff',
                            fontSize: `${(isSmall ? 7 : isLarge ? 10 : 8) * (config.sponsorsTextScale !== undefined ? config.sponsorsTextScale / 100 : 1)}px`
                          }}
                        >
                          {config.sponsorsCarouselSubtitle || 'Sponsor Oficial'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );

              if (c.link) {
                return (
                  <a
                    key={`${c.id}-link-${i}`}
                    href={c.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sponsor-card-wrapper"
                  >
                    {content}
                  </a>
                );
              }

              return <div key={`${c.id}-wrap-${i}`} className="sponsor-card-wrapper">{content}</div>;
            })}
          </motion.div>
        </div>
      </div>
    );
  };

  if (window.location.pathname === '/auth/action' || window.location.pathname === '/__/auth/action') {
    return <AuthActionHandler config={config} />;
  }

  return (
    <div className="min-h-screen font-sans relative overflow-x-hidden theme-text theme-font-primary">
      {import.meta.env.MODE === 'development' && (
        <div className="bg-emerald-500 text-black py-0.5 px-4 font-black uppercase text-[8px] tracking-widest text-center flex items-center justify-center gap-2 z-[100] relative shadow-[0_4px_20px_rgba(16,185,129,0.3)]">
          <span>🧪 ENTORNO DE PRUEBAS (STAGING)</span>
        </div>
      )}
      {(IS_UNDER_CONSTRUCTION || config.maintenanceMode) && (
        <div className="bg-amber-500 text-black py-2 px-4 font-black uppercase text-[10px] sm:text-xs tracking-wider text-center flex items-center justify-center gap-2 z-[100] sticky top-0 shadow-[0_4px_20px_rgba(245,158,11,0.3)] animate-in slide-in-from-top duration-300">
          <span>⚠️ MODO EN CONSTRUCCIÓN / MANTENIMIENTO ACTIVO</span>
          <span className="opacity-80 hidden sm:inline">- Los visitantes ven la pantalla de mantenimiento</span>
        </div>
      )}
      {isSuspendedActive && (
        <div className="bg-amber-500 text-black py-2.5 px-4 font-black uppercase text-[10px] sm:text-xs tracking-wider text-center flex items-center justify-center gap-2 z-[90] sticky top-0 shadow-[0_4px_20px_rgba(245,158,11,0.25)] animate-in slide-in-from-top duration-300">
          <span>🚫 Cuenta inhabilitada</span>
          {(userProfile?.suspendedUntil || currentPlayerDoc?.suspendedUntil) ? (
            <span className="opacity-80">hasta el {new Date(userProfile?.suspendedUntil || currentPlayerDoc!.suspendedUntil!).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          ) : (
            <span className="opacity-80">permanentemente por el administrador</span>
          )}
        </div>
      )}
      {showInstallGuide && (
        <InstallGuide
          logoUrl={config.logoUrl}
          onClose={() => {
            setShowInstallGuide(false);
            localStorage.setItem('pwa_install_guide_last_dismissed', Date.now().toString());
          }}
        />
      )}
      <AnimatePresence>

        {isEditingProfile && user && userProfile && currentPlayerDoc && (
          <EditProfileModal
            user={userProfile}
            player={currentPlayerDoc}
            onComplete={(newProfile) => {
              setUserProfile(newProfile);
              setIsEditingProfile(false);
            }}
            onCancel={() => setIsEditingProfile(false)}
            logoUrl={config.logoUrl}
            clubs={sedesPublicas}
          />
        )}
      </AnimatePresence>

      {/* WELCOME OVERLAY */}
      <AnimatePresence>
        {welcomeMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="fixed inset-0 z-[120] flex items-center justify-center pointer-events-none"
          >
            <div className="absolute inset-0 bg-[#020617]/40 backdrop-blur-sm" />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 1.05, opacity: 0, y: -20 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative z-10 text-center flex flex-col items-center"
            >
              {welcomeMessage.type === 'superadmin' ? (
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full theme-primary-bg flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(var(--glow-rgb), 0.5)]">
                    <ShieldCheck size={40} className="text-black" />
                  </div>
                  <h2 className="text-3xl md:text-5xl font-black uppercase italic tracking-tighter text-white drop-shadow-2xl mb-4">
                    Ingresaste como<br />
                    <span className="theme-primary-text">SUPER ADMIN</span>
                  </h2>
                  <p className="text-[10px] md:text-xs font-black theme-primary-text uppercase tracking-[0.4em] bg-theme-primary/10 px-6 py-2 rounded-full border border-theme-primary/20">
                    Máximo Nivel de Acceso
                  </p>
                </div>
              ) : welcomeMessage.type === 'admin' ? (
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full theme-primary-bg/20 flex items-center justify-center mb-4">
                    <Check size={32} className="theme-primary-text" />
                  </div>
                  <h2 className="text-3xl md:text-5xl font-black uppercase italic tracking-tighter text-white drop-shadow-2xl mb-4">
                    ¡Hola, <span className="theme-primary-text">{welcomeMessage.name}</span>!
                  </h2>
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-xs md:text-sm font-black theme-primary-text uppercase tracking-[0.2em] bg-theme-primary/10 px-4 py-1.5 rounded-full border border-theme-primary/20">
                      Panel de Control
                    </p>
                    <p className="text-[10px] md:text-xs font-bold text-white/60 uppercase tracking-widest mt-2">
                      Has ingresado con privilegios de administrador
                    </p>
                  </div>
                </div>
              ) : welcomeMessage.type === 'new' ? (
                <>
                  <div className="w-24 h-24 rounded-full theme-primary-bg/20 flex items-center justify-center mb-6 animate-pulse">
                    <UserIcon size={48} className="theme-primary-text" />
                  </div>
                  <h2 className="text-4xl md:text-6xl font-black uppercase italic tracking-tighter text-white drop-shadow-2xl mb-4">
                    ¡Bienvenido,<br /><span className="theme-primary-text">{welcomeMessage.name}</span>!
                  </h2>
                  <p className="text-sm md:text-lg font-bold opacity-70 uppercase tracking-widest max-w-sm mx-auto">
                    Tu perfil está listo. Empieza tu camino en {config.pageName || 'Roca Padel Tour'}.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full theme-primary-bg/20 flex items-center justify-center mb-4">
                    <Check size={32} className="theme-primary-text" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter text-white drop-shadow-lg mb-2">
                    ¡Hola de nuevo, <span className="theme-primary-text">{welcomeMessage.name}</span>!
                  </h2>
                  <p className="text-xs md:text-sm font-bold opacity-60 uppercase tracking-widest">
                    Sesión iniciada con éxito
                  </p>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        :root { 
          --primary-color: ${config.primaryColor || '#eab308'};
          --secondary-color: ${config.secondaryColor || '#fbbf24'};
          --primary-text-color: ${config.primaryTextColor || '#ffffff'};
          --secondary-text-color: ${config.secondaryTextColor || '#a1a1aa'};
          ${(() => {
          const hex = config.glowColor || '#ffffff';
          let r = 255, g = 255, b = 255;
          if (hex.startsWith('#')) {
            let h = hex.slice(1);
            if (h.length === 3) h = h.split('').map(x => x + x).join('');
            const num = parseInt(h, 16);
            r = (num >> 16) & 255;
            g = (num >> 8) & 255;
            b = num & 255;
          }
          return '--glow-rgb: ' + r + ', ' + g + ', ' + b + '; ';
        })()}
          ${(() => {
          const largeMap: Record<string, string> = { 'none': '0px', 'sm': '1rem', 'md': '1.5rem', 'lg': '2rem', 'xl': '2.5rem', '2xl': '3rem', '3xl': '3.5rem', '4xl': '4rem', 'full': '9999px' };
          return '--radius-large: ' + (largeMap[config.largeBorderRadius || '3xl'] || '2.5rem') + '; ';
        })()}
          ${(() => {
          if (!config.borderRadius) return '';
          const radiusValues: Record<string, string> = {
            'none': '0px',
            'sm': '0.3rem',
            'md': '0.5rem',
            'lg': '0.8rem',
            'xl': '1rem',
            '2xl': '1.5rem',
            '3xl': '2rem',
            'full': '9999px'
          };
          const r = radiusValues[config.borderRadius] || '1rem';
          let rootRadius = '';
          ['sm', 'md', 'lg', 'xl', '2xl', '3xl'].forEach(k => {
            rootRadius += '--radius-' + k + ': ' + r + '; ';
          });
          if (config.borderRadius === 'none') {
            rootRadius += '--radius-full: 0px; ';
          } else if (config.borderRadius === 'full') {
            rootRadius += '--radius-full: 9999px; ';
          }
          return rootRadius;
        })()}
        }
        .theme-font-primary { font-family: ${config.fontPrimary || 'system-ui, sans-serif'}; }
        h1, h2, h3, h4, h5, h6, .font-black, .theme-font-secondary { font-family: ${config.fontSecondary || config.fontPrimary || 'system-ui, sans-serif'}; }
        body { color: ${config.primaryTextColor || '#ffffff'}; }
        .theme-text { color: ${config.primaryTextColor || '#ffffff'}; }
        .theme-secondary-text-color { color: ${config.secondaryTextColor || '#a1a1aa'}; }
        .theme-title-text { color: ${config.primaryColor || '#eab308'}; }
        .theme-bg-base { background-color: ${getRgba(config.bgColor || '#020617', config.bgOpacity !== undefined ? config.bgOpacity : 85)}; }
        .theme-bg-card { background-color: ${getRgba(config.cardColor || '#0f172a', config.cardOpacity !== undefined ? config.cardOpacity : 70)}; }
        .theme-primary-text { color: ${config.primaryColor || '#eab308'}; }
        .theme-primary-bg { background-color: ${config.primaryColor || '#eab308'}; color: #000; }
        .theme-primary-border { border-color: ${config.primaryColor || '#eab308'}; }
        .theme-secondary-text { color: ${config.secondaryColor || '#fbbf24'}; }
        .theme-secondary-bg { background-color: ${config.secondaryColor || '#fbbf24'}; color: #000; }
        .theme-secondary-border { border-color: ${config.secondaryColor || '#fbbf24'}; }
        .theme-accent-text { color: ${config.secondaryColor || '#fbbf24'}; }
        .theme-accent-bg { background-color: ${config.secondaryColor || '#fbbf24'}; color: #000; }
        .theme-accent-border { border-color: ${config.secondaryColor || '#fbbf24'}; }
        .theme-nav-bg { 
          background-color: ${config.headerType === 'color' && config.headerBgColor ? getRgba(config.headerBgColor, config.headerOpacity !== undefined ? config.headerOpacity : 90) : 'rgba(0,0,0,0.6)'}; 
          ${config.headerType === 'image' && config.headerBgUrl ? `background-image: url('${config.headerBgUrl}'); background-size: cover; background-position: center; border-bottom: none;` : ''}
        }
        @keyframes logo-shine {
          0%   { transform: translateX(-100%) skewX(-18deg); }
          100% { transform: translateX(400%) skewX(-18deg); }
        }
        .logo-shine-bar {
          animation: logo-shine 3.5s ease-in-out infinite;
          animation-delay: 1.2s;
        }
        @keyframes strong-ping {
          0% { transform: scale(1); opacity: 1; }
          70%, 100% { transform: scale(2.5); opacity: 0; }
        }
        .animate-strong-ping {
          animation: strong-ping 1.2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>

      {createPortal(
        <div className={`fixed inset-0 z-[-10] pointer-events-none overflow-hidden select-none bg-black`}>
          <AnimatePresence>
            {bgLoaded && (!config.bgType || config.bgType === 'image') && config.bgUrl && (
              config.bgParallax ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.5, ease: 'easeInOut' }}
                  className={`absolute top-0 left-0 right-0 w-full bg-cover bg-top bg-no-repeat origin-top ${config.bgBlur ? 'blur-sm scale-[1.02]' : ''}`}
                  style={{
                    height: `calc(100vh + ${maxScroll * ratio}px)`,
                    backgroundImage: `url(${config.bgUrl})`,
                    y: bgY
                  }}
                />
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.5, ease: 'easeInOut' }}
                  className={`absolute inset-0 bg-cover bg-center bg-no-repeat ${config.bgBlur ? 'blur-sm scale-105' : ''}`}
                  style={{ backgroundImage: `url(${config.bgUrl})` }}
                />
              )
            )}
          </AnimatePresence>
          <div className={`absolute inset-0 ${config.bgBlur ? 'backdrop-blur-md' : ''} theme-bg-base`} style={{ opacity: (config.bgType === 'image' && config.bgUrl) ? 0.85 : 1 }} />
          <AnimatePresence>
            {!bgLoaded && config.bgUrl && (
              <motion.div
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5, ease: 'easeInOut' }}
                className="absolute inset-0 bg-black z-10"
              />
            )}
          </AnimatePresence>
        </div>,
        document.body
      )}

      <div className="relative z-10 w-full min-h-screen flex flex-col">
        {/* Spacer to prevent layout shift because nav is fixed (only on mobile) */}
        <div className={`${!user ? 'h-14' : 'h-20'} block md:hidden`} />
        <nav className={`bg-[#0a0a0a]/70 backdrop-blur-md border-b border-white/5 fixed md:relative top-0 left-0 right-0 w-full z-[100] ios-safe-padding transition-all duration-300`}>
          {config.headerType === 'image' && config.headerBgUrl && (
            <div className="absolute inset-0 bg-black" style={{ opacity: (100 - (config.headerOpacity || 100)) / 100 }}></div>
          )}
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 relative z-10">
            <div className={`flex justify-between items-center w-full ${!user ? 'h-14 md:h-16' : 'h-20 md:h-24'} relative gap-2 lg:gap-4 transition-all duration-300`}>
              <div className="flex justify-start items-center shrink-0">
                <div className="flex items-center space-x-2 md:space-x-4 cursor-pointer group shrink-0" onClick={() => {
                  setActiveTab('social');
                  if (!logoClicked) {
                    setLogoClicked(true);
                    localStorage.setItem('logoClicked', 'true');
                  }
                }}>
                  <div className="relative">
                    {config.logoUrl || config.logoUrlHorizontal ? (
                      <>
                        {/* Logo cuadrado: visible en móvil (y en escritorio si no hay horizontal) */}
                        <img
                          src={config.logoUrl}
                          alt="Logo"
                          className={`${config.logoUrl && config.logoUrlHorizontal ? 'md:hidden' : ''} ${!user ? 'h-8 w-8 md:h-10 md:w-10' : (config.logoSize || 'h-14 w-14 md:h-20 md:w-20')} object-contain drop-shadow-[0_0_10px_rgba(var(--glow-rgb), 0.3)] transition-all group-hover:scale-110`}
                        />
                        {/* Logo horizontal: visible solo en escritorio cuando está configurado */}
                        {config.logoUrlHorizontal && (
                          <img
                            src={config.logoUrlHorizontal}
                            alt="Logo"
                            className={`hidden md:block ${!user ? 'h-7 md:h-8' : 'h-10 md:h-12'} w-auto max-w-[220px] object-contain drop-shadow-[0_0_10px_rgba(var(--glow-rgb), 0.3)] transition-all group-hover:scale-105`}
                          />
                        )}
                        {/* Overlay de barrido metálico — usa mask-image para respetar la transparencia del PNG */}
                        {config.logoShineEffect && (() => {
                          const shineBar = (
                            <div
                              className="logo-shine-bar"
                              style={{
                                position: 'absolute',
                                top: '-20%',
                                left: 0,
                                width: '40%',
                                height: '140%',
                                background: 'linear-gradient(105deg, transparent 10%, rgba(255,255,255,0.7) 45%, rgba(255,255,255,0.25) 62%, transparent 85%)',
                                filter: 'blur(3px)',
                              }}
                            />
                          );
                          const maskBase: React.CSSProperties = {
                            position: 'absolute',
                            inset: 0,
                            pointerEvents: 'none',
                            overflow: 'hidden',
                            zIndex: 10,
                            maskSize: 'contain',
                            maskRepeat: 'no-repeat',
                            maskPosition: 'center',
                          };
                          return (
                            <>
                              {/* Máscara con logo cuadrado — visible en móvil */}
                              {config.logoUrl && (
                                <div
                                  aria-hidden="true"
                                  className={config.logoUrl && config.logoUrlHorizontal ? 'md:hidden' : ''}
                                  style={{
                                    ...maskBase,
                                    maskImage: `url(${config.logoUrl})`,
                                    WebkitMaskImage: `url(${config.logoUrl})`,
                                    WebkitMaskSize: 'contain',
                                    WebkitMaskRepeat: 'no-repeat',
                                    WebkitMaskPosition: 'center',
                                  }}
                                >
                                  {shineBar}
                                </div>
                              )}
                              {/* Máscara con logo horizontal — visible en escritorio */}
                              {config.logoUrlHorizontal && (
                                <div
                                  aria-hidden="true"
                                  className="hidden md:block"
                                  style={{
                                    ...maskBase,
                                    maskImage: `url(${config.logoUrlHorizontal})`,
                                    WebkitMaskImage: `url(${config.logoUrlHorizontal})`,
                                    WebkitMaskSize: 'contain',
                                    WebkitMaskRepeat: 'no-repeat',
                                    WebkitMaskPosition: 'center',
                                  }}
                                >
                                  {shineBar}
                                </div>
                              )}
                            </>
                          );
                        })()}

                      </>
                    ) : (
                      <div className={`${config.logoSize || 'h-14 w-14 md:h-20 md:w-20'} rounded-xl theme-primary-bg flex items-center justify-center shadow-lg transition-transform group-hover:rotate-12`}>
                        <Trophy className="w-5 h-5 md:w-6 md:h-6" />
                      </div>
                    )}

                    {/* Punto de luz activo (Opción 1) */}
                    {user && !logoClicked && (
                      <div className="absolute top-0.5 right-0 w-1.5 h-1.5 flex z-20">
                        <span className="animate-strong-ping absolute inline-flex h-full w-full rounded-full theme-primary-bg"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 theme-primary-bg"></span>
                      </div>
                    )}

                    <div className="absolute -inset-1 theme-primary-bg blur-lg opacity-10 group-hover:opacity-20 transition-opacity rounded-full" />
                  </div>
                  {config.showPageName && (
                    <div className="hidden md:block lg:hidden xl:block leading-none">
                      <span className={`font-black ${config.pageNameSize || 'text-base md:text-xl'} tracking-tighter uppercase italic theme-title-text theme-font-secondary`}>
                        {config.pageName || 'Roca Padel Tour'}
                      </span>
                      <div className="text-[7px] md:text-[8px] font-bold text-white/30 uppercase tracking-[0.4em] mt-0.5 ml-0.5">
                        {config.pageSubtitle || 'Official Platform'}
                      </div>
                    </div>
                  )}
                </div>

                {user && (
                  <div className="relative ml-2 md:ml-4 shrink-0 flex items-center justify-center bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 md:py-2 max-w-[105px] md:max-w-[130px] hover:bg-white/10 transition-colors">
                    <div className="flex items-center justify-center gap-1.5 pointer-events-none w-full">
                      <MapPin size={12} className="opacity-50 theme-primary-text shrink-0" />
                      <span className="font-bold text-[10px] md:text-xs text-white truncate text-center leading-tight">
                        {globalCity === 'Todas las ciudades' ? 'Todas' : (ALL_CITIES.find(c => c.id === globalCity)?.name || 'Ciudad')}
                      </span>
                    </div>
                    <select
                      value={globalCity}
                      onChange={(e) => setGlobalCity(e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer text-black"
                    >
                      {userProfile?.role === 'superadmin' && (
                        <option value="Todas las ciudades" className="bg-slate-900 text-amber-400 font-black">Todas</option>
                      )}
                      {ALL_CITIES.map(c => (
                        <option key={c.id} value={c.id} className="bg-slate-900 text-white">{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* DESKTOP NAV */}
              <div className="hidden lg:flex items-center justify-center gap-0.5 lg:gap-1 xl:gap-4 flex-1 min-w-0 px-1 lg:px-2 mx-auto">
                {[
                  { id: 'social', label: 'Inicio', icon: <Home size={18} /> },
                  { id: 'reservas', label: 'Reservas', icon: <Clock size={18} /> },
                  { id: 'torneos', label: 'Torneos', icon: <Trophy size={18} /> },
                  { id: 'jugadores', label: 'Ranking', icon: <Medal size={18} /> },
                  { id: 'mas', label: 'Más', icon: <Menu size={18} />, isDropdown: true, subItems: [
                    { id: 'historial', label: 'Historial', icon: <History size={16} /> },
                    { id: 'clubes', label: 'Clubes', icon: <MapPin size={16} /> },
                    { id: 'herramientas', label: 'Super 8/12', icon: <LayoutList size={16} /> }
                  ] },
                ].map((item, index) => (
                  <React.Fragment key={item.id}>
                    {(item as any).isDropdown ? (
                      <div className="relative group z-10 flex items-center h-full">
                        <button
                          className={`relative px-1.5 lg:px-2 xl:px-5 py-2 rounded-xl text-[10px] lg:text-xs xl:text-sm transition-all duration-300 flex items-center justify-center gap-1 lg:gap-1.5 xl:gap-2 outline-none focus:outline-none focus:ring-0 ${!user ? 'cursor-default' : ''} ${(item as any).subItems?.some((sub: any) => sub.id === activeTab)
                              ? 'text-white font-black scale-105 shadow-sm'
                              : 'text-white/50 hover:text-white/90 hover:bg-white/5 hover:scale-[1.02]'
                            }`}
                        >
                          <span
                            className="transition-all duration-300 flex items-center justify-center shrink-0"
                            style={(item as any).subItems?.some((sub: any) => sub.id === activeTab) ? {
                              color: config.primaryColor || '#eab308',
                              filter: `drop-shadow(0 0 6px ${config.primaryColor || '#eab308'}) drop-shadow(0 0 2px ${config.primaryColor || '#eab308'})`
                            } : {
                              opacity: 0.75
                            }}
                          >
                            {item.icon}
                          </span>
                          <span className="tracking-wide whitespace-nowrap">{item.label}</span>
                          <ChevronDown size={14} className="opacity-50 group-hover:rotate-180 transition-transform duration-300" />
                          {(item as any).subItems?.some((sub: any) => sub.id === activeTab) && (
                            <div
                              className="absolute -bottom-1 left-[15%] right-[15%] h-0.5 rounded-t-full shadow-[0_-2px_8px_rgba(var(--glow-rgb),0.8)]"
                              style={{ backgroundColor: config.primaryColor || '#eab308', filter: 'brightness(1.2)' }}
                            />
                          )}
                        </button>
                        <div className="absolute top-full right-0 mt-2 w-48 bg-[#0f172a]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 translate-y-2 group-hover:translate-y-0 z-50 overflow-hidden">
                          {(item as any).subItems?.map((sub: any) => (
                            <button
                              key={sub.id}
                              onClick={() => {
                                if (!user && sub.id === 'historial') {
                                  handleOpenLoginModal('login');
                                  return;
                                }
                                setActiveTab(sub.id);
                              }}
                              className={`w-full px-4 py-3 flex items-center gap-3 text-sm transition-colors ${activeTab === sub.id ? 'text-white bg-white/10 font-bold' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                            >
                              <span style={{ color: activeTab === sub.id ? (config.primaryColor || '#eab308') : 'inherit' }} className={activeTab === sub.id ? 'drop-shadow-md' : ''}>
                                {sub.icon}
                              </span>
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <button
                      onClick={() => {
                        if (!user && (item.id === 'partidos')) {
                          handleOpenLoginModal('login');
                          return;
                        }
                        setActiveTab(item.id);
                      }}

                      title={!user && (item.id === 'partidos') ? "Ingresa para acceder a esta sección" : undefined}
                      className={`relative px-1.5 lg:px-2 xl:px-5 py-2 rounded-xl text-[10px] lg:text-xs xl:text-sm transition-all duration-300 flex items-center justify-center gap-1 lg:gap-1.5 xl:gap-2 group z-10 outline-none focus:outline-none focus:ring-0 shrink ${!user && (item.id === 'partidos') ? 'cursor-default' : ''} ${(activeTab === item.id || (!user && item.id === 'social' && activeTab === 'inicio'))
                          ? 'text-white font-black scale-105 shadow-sm'
                          : 'text-white/50 hover:text-white/90 hover:bg-white/5 hover:scale-[1.02]'
                        }`}
                    >
                      <span
                        className="transition-all duration-300 flex items-center justify-center shrink-0"
                        style={activeTab === item.id ? {
                          color: config.primaryColor || '#eab308',
                          filter: `drop-shadow(0 0 6px ${config.primaryColor || '#eab308'}) drop-shadow(0 0 2px ${config.primaryColor || '#eab308'})`
                        } : {
                          opacity: 0.75
                        }}
                      >
                        {item.id === 'reservas' ? (
                          <div
                            className="w-[18px] h-[18px] bg-current"
                            style={{
                              maskImage: 'url(/grid-icon.png)',
                              maskSize: 'contain',
                              maskRepeat: 'no-repeat',
                              maskPosition: 'center',
                              WebkitMaskImage: 'url(/grid-icon.png)',
                              WebkitMaskSize: 'contain',
                              WebkitMaskRepeat: 'no-repeat',
                              WebkitMaskPosition: 'center',
                            }}
                          />
                        ) : item.id === 'partidos' ? (
                          <div
                            className="w-[18px] h-[18px] bg-current"
                            style={{
                              maskImage: 'url(/partidos-icon2.png)',
                              maskSize: 'contain',
                              maskRepeat: 'no-repeat',
                              maskPosition: 'center',
                              WebkitMaskImage: 'url(/partidos-icon2.png)',
                              WebkitMaskSize: 'contain',
                              WebkitMaskRepeat: 'no-repeat',
                              WebkitMaskPosition: 'center',
                            }}
                          />
                        ) : item.icon}
                      </span>
                      <span className="tracking-wide whitespace-nowrap">{item.label}</span>
                      {activeTab === item.id && (
                        /* Premium Glassy backplate sliding background */
                        <div
                          className="absolute inset-0 rounded-xl bg-white/[0.08] border border-white/10 -z-10 shadow-[0_4px_12px_rgba(0,0,0,0.15)] animate-in fade-in zoom-in-95 duration-200"
                        />
                      )}
                    </button>
                    )}
                    {index < 4 && (
                      <div className="w-[1px] h-4 bg-white/10 self-center shrink-0 rounded-full mx-1 xl:mx-2" />
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div className="flex items-center justify-end shrink-0">
                <div className="hidden lg:block h-6 w-px bg-white/10 mx-4" />

                <div className="flex items-center gap-1 md:gap-2">
                  {user ? (
                    <div className="flex items-center gap-2 md:gap-3">


                      <div className="relative" ref={messagesRef}>
                        <button
                          onClick={() => {
                            setShowPrivateMessages(!showPrivateMessages);
                            setShowNotifications(false);
                          }}
                          className={`relative p-2 rounded-full transition-colors ${showPrivateMessages ? 'bg-white text-black hover:bg-gray-200 shadow-[0_0_15px_rgba(255,255,255,0.5)]' : 'hover:bg-white/10 text-white/70 hover:text-white'}`}
                        >
                          <MessageCircle size={20} className={showPrivateMessages ? "fill-black" : ""} />
                          {filteredPrivateChats.reduce((acc, chat) => acc + (chat.unreadCount?.[user.uid] || 0), 0) > 0 && (
                            <div className="absolute -top-0.5 -right-0.5 px-1 min-w-[14px] h-[14px] bg-red-500 rounded-full border-2 border-slate-900 flex items-center justify-center text-[8px] font-black leading-none text-white shadow-[0_0_6px_rgba(239,68,68,0.7)]">
                              {filteredPrivateChats.reduce((acc, chat) => acc + (chat.unreadCount?.[user.uid] || 0), 0)}
                            </div>
                          )}
                        </button>

                        {showPrivateMessages && (
                          <div className="fixed lg:absolute inset-0 lg:inset-auto lg:right-0 lg:top-full lg:mt-2 z-[300] flex items-start justify-center lg:block pt-20 lg:pt-0 px-4 lg:px-0 pb-4 lg:pb-0 pointer-events-none">
                            <motion.div
                              initial={{ scale: 0.9, opacity: 0, y: -20 }}
                              animate={{ scale: 1, opacity: 1, y: 0 }}
                              exit={{ scale: 0.9, opacity: 0, y: -20 }}
                              className="w-full max-w-md lg:w-[450px] bg-[#0f172a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] lg:max-h-[70vh] pointer-events-auto"
                            >
                              <div className="p-3 border-b border-white/10 flex justify-between items-center bg-black/20">
                                <h2 className="text-sm font-black uppercase tracking-widest text-white theme-font-secondary">Mensajes Privados</h2>
                                <button onClick={() => setShowPrivateMessages(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-white/50 hover:text-white">
                                  <X size={16} />
                                </button>
                              </div>
                              <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                                {filteredPrivateChats.length === 0 && (
                                  <div className="text-center opacity-40 text-xs py-10">No tienes mensajes.</div>
                                )}
                                {filteredPrivateChats.slice(0, privateChatsLimit).map(chat => {
                                  const isGroup = chat.type === 'group' || chat.id.startsWith('match_');
                                  const otherParticipantId = isGroup ? null : chat.participantIds.find((id: string) => id !== user.uid);
                                  const otherParticipantName = isGroup ? (chat.title || 'Chat de Partido') : (chat.participantNames?.[otherParticipantId!] || 'Usuario Desconocido');
                                  const otherParticipantPhoto = isGroup ? null : chat.participantPhotos?.[otherParticipantId!];
                                  const unreadCount = chat.unreadCount?.[user.uid] || 0;

                                  return (
                                    <motion.div
                                      key={chat.id}
                                      drag={chat.isSupport ? undefined : "x"}
                                      dragConstraints={{ left: 0, right: 0 }}
                                      dragElastic={0.5}
                                      onDragEnd={(_e, info) => {
                                        if (!chat.isSupport && Math.abs(info.offset.x) > 120) {
                                          deleteDoc(doc(db, getCollectionPath('private_chats'), chat.id));
                                        }
                                      }}
                                      onClick={() => {
                                        if (chat.isSupport) {
                                          const p = players.find(x => x.id === chat.playerId);
                                          if (p) {
                                            setSupportChatPlayer(p);
                                          } else {
                                            setSupportChatPlayer({
                                              id: chat.playerId,
                                              name: otherParticipantName,
                                              photoUrl: otherParticipantPhoto || ''
                                            } as any);
                                          }
                                          setShowPrivateMessages(false);
                                        } else if (isGroup && chat.matchId) {
                                          const match = matches.find(m => m.id === chat.matchId);
                                          if (match) {
                                            setSelectedMatchDetails(match);
                                            setShowPrivateMessages(false);
                                          }
                                        } else if (otherParticipantId) {
                                          setActivePrivateChatRecipientId(otherParticipantId);
                                          setActivePrivateChatRecipientName(otherParticipantName);
                                          setActivePrivateChatRecipientPhoto(otherParticipantPhoto || '');
                                          setShowPrivateMessages(false);
                                        }
                                      }}
                                      className={`p-3 rounded-xl border relative overflow-hidden transition-all duration-300 group hover:scale-[1.02] cursor-pointer flex items-center gap-3 ${unreadCount > 0 ? 'border-theme-primary bg-theme-primary/20 shadow-[0_0_15px_rgba(var(--glow-rgb),0.3)] hover:bg-theme-primary/30' : 'border-white/10 bg-black/40 opacity-70 hover:opacity-100 hover:border-white/30 hover:bg-white/5'}`}
                                    >
                                      {isGroup ? (
                                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30 shrink-0">
                                          <Users size={20} className="text-emerald-400" />
                                        </div>
                                      ) : otherParticipantPhoto ? (
                                        <FadeInImage src={otherParticipantPhoto} alt={otherParticipantName} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                                      ) : (
                                        <div className="w-10 h-10 rounded-full theme-primary-bg/20 flex items-center justify-center border border-white/10 shrink-0">
                                          <UserIcon size={16} className="theme-primary-text" />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <div className={`font-bold text-sm truncate flex items-center gap-1.5 ${unreadCount > 0 ? 'text-white' : 'text-white/80'}`}>
                                          <span>{otherParticipantName}</span>
                                        </div>
                                        <div className={`text-xs truncate ${unreadCount > 0 ? 'text-white/90 font-medium' : 'text-white/50'}`}>
                                          {chat.lastMessageSenderId === user.uid ? 'Tú: ' : ''}{chat.lastMessage || 'Nuevo chat'}
                                        </div>
                                      </div>
                                      {unreadCount > 0 && (
                                        <div className="w-5 h-5 rounded-full theme-primary-bg flex items-center justify-center text-black text-[10px] font-bold shrink-0">
                                          {unreadCount}
                                        </div>
                                      )}
                                      {!chat.isSupport && (
                                        <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (await confirm('¿Estás seguro de que deseas eliminar este chat?')) {
                                              deleteDoc(doc(db, getCollectionPath('private_chats'), chat.id));
                                            }
                                          }}
                                          className="p-1 opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition-all bg-black/40 rounded-md ml-1"
                                          title="Eliminar chat"
                                        >
                                          <Trash2 size={10} />
                                        </button>
                                      )}
                                    </motion.div>
                                  );
                                })}
                                {filteredPrivateChats.length > privateChatsLimit && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setPrivateChatsLimit(prev => prev + 5); }}
                                    className="w-full py-2 text-xs font-bold text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition-colors mt-2"
                                  >
                                    Ver más
                                  </button>
                                )}
                              </div>
                            </motion.div>
                          </div>
                        )}
                      </div>

                      <div className="relative" ref={notificationsRef}>
                        <button
                          onClick={() => {
                            setShowNotifications(!showNotifications);
                            setShowPrivateMessages(false);
                          }}
                          className={`relative p-2 rounded-full transition-colors ${showNotifications ? 'bg-white text-black hover:bg-gray-200 shadow-[0_0_15px_rgba(255,255,255,0.5)]' : 'hover:bg-white/10 text-white/70 hover:text-white'}`}
                        >
                          <Bell size={20} className={showNotifications ? "fill-black" : ""} />
                          {filteredNotifications.filter(n => !n.read).length > 0 && (
                            <div className="absolute -top-0.5 -right-0.5 px-1 min-w-[14px] h-[14px] bg-red-500 rounded-full border-2 border-slate-900 flex items-center justify-center text-[8px] font-black leading-none text-white shadow-[0_0_6px_rgba(239,68,68,0.7)]">
                              {filteredNotifications.filter(n => !n.read).length}
                            </div>
                          )}
                        </button>

                        {showNotifications && (
                          <div className="fixed lg:absolute inset-0 lg:inset-auto lg:right-0 lg:top-full lg:mt-2 z-[300] flex items-start justify-center lg:block pt-20 lg:pt-0 px-4 lg:px-0 pb-4 lg:pb-0 pointer-events-none">
                            <motion.div
                              initial={{ scale: 0.9, opacity: 0, y: -20 }}
                              animate={{ scale: 1, opacity: 1, y: 0 }}
                              exit={{ scale: 0.9, opacity: 0, y: -20 }}
                              className="w-full max-w-md lg:w-[450px] bg-[#0f172a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] lg:max-h-[70vh] pointer-events-auto"
                            >
                              <div className="p-3 border-b border-white/10 flex justify-between items-center bg-black/20">
                                <div className="flex items-center gap-2">
                                  <h2 className="text-sm font-black uppercase tracking-widest text-white theme-font-secondary">Notificaciones</h2>
                                </div>
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={async () => {
                                      if (!userProfile?.notificationsEnabled && Notification.permission === 'denied') {
                                        setIsBlockedModalOpen(true);
                                        return;
                                      }
                                      const { requestNotificationPermission, disableNotifications } = await import('./lib/notifications');
                                      if (userProfile?.notificationsEnabled) {
                                        await disableNotifications(user.uid);
                                      } else {
                                        await requestNotificationPermission(user.uid);
                                      }
                                    }}
                                    className={`px-1.5 py-0.5 rounded-md transition-all flex items-center gap-1 ${userProfile?.notificationsEnabled
                                      ? 'text-yellow-400'
                                      : 'theme-primary-bg text-black shadow-[0_0_10px_rgba(var(--glow-rgb),0.3)] animate-pulse'
                                      }`}
                                    title={userProfile?.notificationsEnabled ? "Alertas Push Activas" : "¡Activa las Alertas Push!"}
                                  >
                                    {userProfile?.notificationsEnabled ? <BellRing size={14} /> : <Bell size={14} />}
                                    {!userProfile?.notificationsEnabled && (
                                      <span className="text-[8px] font-black uppercase tracking-tighter">Activar</span>
                                    )}
                                  </button>
                                  <button
                                    onClick={async () => {
                                      for (const n of filteredNotifications) {
                                        await deleteDoc(doc(db, 'notifications', n.id));
                                      }
                                      setShowNotifications(false);
                                    }}
                                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-white/50 hover:text-white"
                                    title="Archivar Todas"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                  <button onClick={() => setShowNotifications(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-white/50 hover:text-white">
                                    <X size={16} />
                                  </button>
                                </div>
                              </div>
                              <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                                {filteredNotifications.length === 0 && (
                                  <div className="text-center opacity-40 text-xs py-10">No tienes notificaciones.</div>
                                )}
                                {filteredNotifications.slice(0, notificationsLimit).map(notif => (
                                  <motion.div
                                    key={notif.id}
                                    id={`notif-${notif.id}`}
                                    drag="x"
                                    dragConstraints={{ left: 0, right: 0 }}
                                    dragElastic={0.5}
                                    onDragEnd={(_e, info) => {
                                      if (Math.abs(info.offset.x) > 120) {
                                        deleteDoc(doc(db, 'notifications', notif.id));
                                      }
                                    }}
                                    className={`p-3 rounded-xl border relative overflow-hidden transition-all duration-300 group hover:scale-[1.02] cursor-pointer ${!notif.read ? 'border-theme-primary bg-theme-primary/20 shadow-[0_0_15px_rgba(var(--glow-rgb),0.3)] hover:bg-theme-primary/30 hover:shadow-[0_0_20px_rgba(var(--glow-rgb),0.5)]' : 'border-white/10 bg-black/40 opacity-70 hover:opacity-100 hover:border-white/30 hover:bg-white/5 active:scale-95'} ${highlightNotifId === notif.id ? 'ring-2 ring-yellow-400 shadow-[0_0_25px_rgba(234,179,8,0.5)] ring-offset-2 ring-offset-black scale-[1.02]' : ''}`}
                                  >
                                    <div
                                      className="cursor-pointer pr-6"
                                      onClick={async () => {
                                        if (!notif.read && notif.type !== 'match_invite') {
                                          await updateDoc(doc(db, 'notifications', notif.id), { read: true });
                                        }
                                        setShowNotifications(false);

                                        // Si la notificación contiene una URL de redirección, la procesamos dinámicamente
                                        if (notif.url) {
                                          try {
                                            const urlObj = new URL(notif.url, window.location.origin);
                                            const tab = urlObj.searchParams.get('tab');
                                            const subtab = urlObj.searchParams.get('subtab');
                                            const requestId = urlObj.searchParams.get('requestId');

                                            if (tab) {
                                              setActiveTab(tab);
                                              if (tab === 'admin' && subtab) {
                                                setAdminSection(subtab);
                                              } else if (tab === 'reservas' && subtab) {
                                                if (subtab === 'mis-peticiones') {
                                                  setBookingSubTab('mis-turnos' as any);
                                                } else {
                                                  setBookingSubTab(subtab as any);
                                                }
                                              }
                                            }
                                            if (requestId) {
                                              setHighlightBookingRequestId(requestId);
                                            }
                                          } catch (err) {
                                            console.error("Error al procesar la redirección interna:", err);
                                          }
                                          return;
                                        }

                                        if (['post_comment', 'comment_reply', 'post_mention', 'comment_mention'].includes(notif.type)) {
                                          setShowNotifications(false);
                                          setActiveTab('social');
                                          if (notif.postId) {
                                            setTimeout(() => {
                                              const el = document.getElementById(`post-${notif.postId}`);
                                              if (el) {
                                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                el.classList.add('ring-4', 'ring-theme-primary', 'transition-all', 'duration-500', 'rounded-3xl');
                                                setTimeout(() => el.classList.remove('ring-4', 'ring-theme-primary'), 2000);
                                              }
                                            }, 500);
                                          }
                                          return;
                                        }

                                        if (notif.type === 'chat_request') {
                                          setShowNotifications(false);
                                          setShowPrivateMessages(true);
                                          setPrivateMessagesTab('requests');
                                          return;
                                        }

                                        if (notif.type === 'chat_request_accepted') {
                                          setShowNotifications(false);
                                          setShowPrivateMessages(true);
                                          setPrivateMessagesTab('chats');
                                          return;
                                        }

                                        
                                        if (notif.type === 'booking_request' || notif.type === 'deposit_required') {
                                          setShowNotifications(false);
                                          setActiveTab('reservas');
                                          setBookingSubTab('mis-turnos' as any);
                                          if (notif.requestId) {
                                            setHighlightBookingRequestId(notif.requestId);
                                          }
                                          return;
                                        }

                                        if (notif.type === 'player_validation') {
                                          setActiveTab('perfil');
                                          setShouldHighlightValidation(true);
                                          setTimeout(() => setShouldHighlightValidation(false), 4000);
                                          return;
                                        }

                                        if (notif.type === 'admin_validation') {
                                          setActiveTab('admin');
                                          setAdminSection('validaciones');
                                          return;
                                        }

                                        if (notif.type === 'admin_category_request') {
                                          setActiveTab('admin');
                                          setAdminSection('categorias');
                                          return;
                                        }

                                        if (notif.type === 'support_chat_message') {
                                          const pId = notif.playerId || (userRole === 'user' ? user?.uid : null);
                                          const p = players.find(x => x.id === pId);
                                          if (p) setSupportChatPlayer(p);
                                          return;
                                        }

                                        const isTournamentStart = notif.type === 'tournament_started' ||
                                          (notif.type === 'tournament_registration' && notif.body?.toLowerCase().includes('comenzado'));

                                        if (isTournamentStart) {
                                          const tournament = tournaments.find(t => t.id === notif.matchId);
                                          const tName = tournament?.name || 'Torneo';

                                          setActiveTab('torneos');
                                          setTorneosTab('mi_fixture' as any);
                                          setTimeout(() => {
                                            toggleBanner(tName);
                                            setTimeout(() => {
                                              const el = document.querySelector(`[data-grilla-tournament-id="${tName}"]`);
                                              if (el) {
                                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                el.classList.add('nav-highlight');
                                                setTimeout(() => el.classList.remove('nav-highlight'), 2500);
                                              }
                                            }, 450);
                                          }, 150);
                                        } else if (notif.type === 'tournament_invitation') {
                                          const t = tournaments.find(tour => tour.id === notif.matchId);
                                          const isRegistration = !t || !t.status || ['Registro', 'Borrador', 'Lobby'].includes(t.status);
                                          
                                          setActiveTab('torneos');
                                          if (isRegistration) {
                                            setTorneosTab('inscripciones' as any);
                                          } else if (t.status === 'Finalizado' || t.isArchived) {
                                            setTorneosTab('historial' as any);
                                          } else {
                                            setTorneosTab('activos' as any);
                                          }

                                          // Small delay to let tab render, then expand & scroll
                                          setTimeout(() => {
                                            setExpandedPublicTournamentId(notif.matchId);
                                            setTimeout(() => {
                                              const el = document.querySelector(`[data-tournament-id="${notif.matchId}"]`);
                                              if (el) {
                                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                el.classList.add('nav-highlight');
                                                setTimeout(() => el.classList.remove('nav-highlight'), 2500);
                                              }
                                            }, 450);
                                          }, 200);
                                        } else if (notif.type === 'tournament_registration') {
                                          if (['admin', 'superadmin', 'co_admin', 'gestor_torneos'].includes(userRole)) {
                                            setActiveTab('admin');
                                            setTimeout(() => {
                                              window.dispatchEvent(new CustomEvent('openAdminTournament', { detail: notif.matchId }));
                                            }, 200);
                                          } else {
                                            setActiveTab('torneos');
                                            // Small delay to let tab render, then expand & scroll
                                            setTimeout(() => {
                                              setExpandedPublicTournamentId(notif.matchId);
                                              setTimeout(() => {
                                                const el = document.querySelector(`[data-tournament-id="${notif.matchId}"]`);
                                                if (el) {
                                                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                  el.classList.add('nav-highlight');
                                                  setTimeout(() => el.classList.remove('nav-highlight'), 2500);
                                                }
                                              }, 350);
                                            }, 100);
                                          }
                                        } else if (notif.type === 'warning') {
                                          const match = matches.find(m => m.id === notif.matchId);
                                          const tournamentObj = tournaments.find(t => t.id === match?.tournamentId || t.id === notif.tournamentId);
                                          const tName = match?.tournamentName || 'Torneo';

                                          const isParticipant = match && userProfile && (() => {
                                            const t1 = teams.find(t => t.id === match.team1Id);
                                            const t2 = teams.find(t => t.id === match.team2Id);
                                            return [t1?.player1Id, t1?.player2Id, t1?.player3Id, t2?.player1Id, t2?.player2Id, t2?.player3Id].includes(userProfile.uid);
                                          })();

                                          if (isParticipant) {
                                            setActiveTab('torneos');
                                            setTorneosTab('mi_fixture' as any);
                                            
                                            const bannersToExpand = [tName];
                                            if (tournamentObj?.type === 'liga' && match?.zoneName && match.zoneName !== 'Zona Única') {
                                              bannersToExpand.push(`${tName}::${match.zoneName}`);
                                            }
                                            const rName = (match?.round || '').toLowerCase();
                                            const isElim = rName.includes('8vos') || rName.includes('4tos') ||
                                              rName.includes('semi') || rName.includes('final') ||
                                              rName.includes('eliminaci');
                                            if (isElim) bannersToExpand.push(`${tName}::Elimination`);

                                            setExpandedBanners(prev => [...new Set([...prev, ...bannersToExpand])]);

                                            setTimeout(() => {
                                              let attempts = 0;
                                              const interval = setInterval(() => {
                                                const el = document.querySelector(`[data-match-id="${notif.matchId}"]`);
                                                if (el) {
                                                  clearInterval(interval);
                                                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                  el.classList.add('nav-highlight');
                                                  setTimeout(() => el.classList.remove('nav-highlight'), 3000);
                                                } else if (attempts++ >= 25) {
                                                  clearInterval(interval);
                                                }
                                              }, 150);
                                            }, 400);
                                          } else {
                                            if (['admin', 'superadmin', 'co_admin', 'gestor_torneos'].includes(userRole)) {
                                              setActiveTab('admin');
                                              setTimeout(() => {
                                                window.dispatchEvent(new CustomEvent('openAdminTournament', { detail: tournamentObj?.id }));
                                              }, 200);
                                            } else {
                                              setActiveTab('torneos');
                                              setTorneosTab('activos' as any);
                                              setTimeout(() => {
                                                setExpandedPublicTournamentId(tournamentObj?.id || null);
                                              }, 100);
                                            }
                                          }
                                        } else if (notif.type === 'match_impugned' && ['admin', 'superadmin', 'co_admin', 'gestor_torneos'].includes(userRole)) {
                                          // Admin sees impugned → go to admin panel, disputas sub-tab
                                          setActiveTab('admin');
                                          setTimeout(() => {
                                            window.dispatchEvent(new CustomEvent('navigateToDisputas', { detail: notif.matchId }));
                                          }, 200);
                                        } else if (notif.type === 'admin_match_validation' && ['admin', 'superadmin', 'co_admin', 'gestor_torneos'].includes(userRole)) {
                                          setActiveTab('admin');
                                          setTimeout(() => {
                                            window.dispatchEvent(new CustomEvent('navigateToValidaciones', { detail: notif.matchId }));
                                          }, 200);
                                        } else if (notif.type === 'support_chat_message') {
                                          const playerToChat = players.find(p => p.id === notif.playerId);
                                          if (playerToChat) {
                                            setSupportChatPlayer(playerToChat);
                                          }
                                        } else if (notif.type === 'super8_finished') {
                                          setShowNotifications(false);
                                          setActiveTab('perfil');
                                          setTimeout(() => {
                                            window.dispatchEvent(new CustomEvent('highlightSuper8'));
                                          }, 350);
                                        } else if (notif.type === 'match_reported' || notif.type === 'match_impugned' || notif.type === 'match_ready') {
                                          setShowNotifications(false);
                                          const match = matches.find(m => m.id === notif.matchId);
                                          if (match && notif.type !== 'match_ready') {
                                            if (match.tournamentId) {
                                              setActiveTab('torneos');
                                              setTimeout(() => {
                                                setExpandedPublicTournamentId(match.tournamentId);
                                                setTimeout(() => {
                                                  const el = document.querySelector(`[data-tournament-id="${match.tournamentId}"]`);
                                                  if (el) {
                                                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                    el.classList.add('nav-highlight');
                                                    setTimeout(() => el.classList.remove('nav-highlight'), 2500);
                                                  }
                                                  setSelectedMatchDetails(match);
                                                }, 350);
                                              }, 100);
                                            } else {
                                              setSelectedMatchDetails(match);
                                            }
                                          } else {
                                            setActiveTab('partidos');
                                            setTimeout(() => {
                                              const matchIdToOpen = notif.type === 'match_ready' ? notif.data?.matchId : notif.matchId;
                                              const onlyHighlight = notif.type === 'match_ready';
                                              window.dispatchEvent(new CustomEvent('openPublicMatch', { detail: { matchId: matchIdToOpen, onlyHighlight } }));
                                            }, 100);
                                          }
                                        } else {
                                          // chat_message
                                          const match = matches.find(m => m.id === notif.matchId);
                                          if (match) {
                                            // Navigate to torneos tab first so context is visible
                                            if (match.tournamentId) {
                                              setActiveTab('torneos');
                                              setTimeout(() => {
                                                setExpandedPublicTournamentId(match.tournamentId);
                                                setTimeout(() => {
                                                  const el = document.querySelector(`[data-tournament-id="${match.tournamentId}"]`);
                                                  if (el) {
                                                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                    el.classList.add('nav-highlight');
                                                    setTimeout(() => el.classList.remove('nav-highlight'), 2500);
                                                  }
                                                  setSelectedMatchDetails(match);
                                                }, 350);
                                              }, 100);
                                            } else {
                                              setSelectedMatchDetails(match);
                                            }
                                          }
                                        }
                                      }}
                                    >
                                      <div className="flex justify-between items-start gap-2 mb-1 pr-1">
                                        <div className={`font-bold text-xs flex items-center gap-1.5 ${!notif.read ? 'text-white' : 'text-white/70'}`}>
                                          {notif.type === 'match_invite' && <span>🎾</span>}
                                          {notif.title}
                                        </div>
                                        {notif.points && (
                                          <div className="bg-[#a4e61b]/10 border border-[#a4e61b]/30 px-1.5 py-0.5 rounded-lg text-[#a4e61b] font-black text-[10px] italic shrink-0 leading-none shadow-[0_0_8px_rgba(164,230,27,0.2)]">
                                            +{notif.points}
                                          </div>
                                        )}
                                      </div>
                                      <div className={`text-[10px] mb-2 leading-tight ${!notif.read ? 'text-white/80' : 'text-white/50'}`}>{notif.body}</div>

                                      {/* BOTONES DE ACCIÓN IN-APP PARA CONFIRMACIÓN DE ASISTENCIA */}
                                      {notif.type === 'booking_confirmation_request' && notif.bookingId && !notif.read && (
                                        <div className="flex gap-1.5 mt-2 mb-1" onClick={(e) => e.stopPropagation()}>
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              try {
                                                const bookingRef = doc(db, getCollectionPath('bookings'), notif.bookingId);
                                                const bookingSnap = await getDoc(bookingRef);
                                                if (bookingSnap.exists()) {
                                                  await updateDoc(bookingRef, {
                                                    playerConfirmed: true,
                                                    status: 'confirmed',
                                                    confirmationStatus: 'confirmed',
                                                    confirmationRequested: false,
                                                    updatedAt: Date.now()
                                                  });
                                                  const bData = bookingSnap.data();
                                                  if (bData.isFixed) {
                                                    const origId = notif.bookingId.split('_projected_')[0].split('_materialized_')[0];
                                                    if (origId !== notif.bookingId) {
                                                      const origRef = doc(db, getCollectionPath('bookings'), origId);
                                                      const origSnap = await getDoc(origRef);
                                                      if (origSnap.exists()) {
                                                        const origData = origSnap.data();
                                                        const excl = origData.excludedDates || [];
                                                        if (!excl.includes(bData.date)) {
                                                          await updateDoc(origRef, { excludedDates: [...excl, bData.date], updatedAt: Date.now() });
                                                        }
                                                      }
                                                    }
                                                  }
                                                } else {
                                                  alert('No se encontró la reserva.');
                                                }
                                                // Mark notification as read
                                                await updateDoc(doc(db, getCollectionPath('notifications'), notif.id), { read: true });
                                                alert('¡Asistencia confirmada!');
                                              } catch (err) { console.error(err); alert('Error al confirmar.'); }
                                            }}
                                            className="flex-1 bg-[#C5FF4A] hover:bg-[#d4ff6a] text-black font-black text-[9px] py-2 rounded-lg transition-all uppercase tracking-wider active:scale-95 flex items-center justify-center gap-1"
                                          >
                                            <Check size={11} /> Asistiré
                                          </button>
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              if (!await confirm('¿No podrás asistir a este turno?')) return;
                                              try {
                                                const bookingRef = doc(db, getCollectionPath('bookings'), notif.bookingId);
                                                const bookingSnap = await getDoc(bookingRef);
                                                if (bookingSnap.exists()) {
                                                  const bData = bookingSnap.data();
                                                  if (bData.isFixed) {
                                                    const origId = notif.bookingId.split('_projected_')[0].split('_materialized_')[0];
                                                    const origRef = doc(db, getCollectionPath('bookings'), origId);
                                                    const origSnap = await getDoc(origRef);
                                                    if (origSnap.exists()) {
                                                      const origData = origSnap.data();
                                                      const excl = origData.excludedDates || [];
                                                      if (!excl.includes(bData.date)) {
                                                        await updateDoc(origRef, { excludedDates: [...excl, bData.date], updatedAt: Date.now() });
                                                      }
                                                    }
                                                    await updateDoc(bookingRef, { status: 'cancelled', cancellationReason: 'Jugador no asistirá esta fecha', confirmationRequested: false, updatedAt: Date.now() });
                                                  } else {
                                                    await updateDoc(bookingRef, { status: 'cancelled', cancellationReason: 'Rechazado por el jugador', confirmationRequested: false, updatedAt: Date.now() });
                                                  }
                                                }
                                                // Mark notification as read
                                                await updateDoc(doc(db, getCollectionPath('notifications'), notif.id), { read: true });
                                                alert('Turno cancelado para esta fecha.');
                                              } catch (err) { console.error(err); alert('Error al cancelar.'); }
                                            }}
                                            className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 font-black text-[9px] py-2 rounded-lg transition-all uppercase tracking-wider border border-red-500/30 active:scale-95 flex items-center justify-center gap-1"
                                          >
                                            <X size={11} /> No podré
                                          </button>
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              // Mark notification as read
                                                await updateDoc(doc(db, getCollectionPath('notifications'), notif.id), { read: true });
                                              setShowNotifications(false);
                                              setActiveTab('reservas');
                                              setBookingSubTab('mis-turnos' as any);
                                            }}
                                            className="px-2.5 bg-white/5 hover:bg-white/10 text-zinc-300 font-black text-[9px] py-2 rounded-lg transition-all uppercase tracking-wider border border-white/10 active:scale-95 flex items-center justify-center gap-1"
                                          >
                                            <ChevronRight size={11} />
                                          </button>
                                        </div>
                                      )}
                                      {notif.type === 'match_invite' && notif.data?.matchId && !notif.read && (
                                        <div className="flex gap-1.5 mt-2 mb-1" onClick={(e) => e.stopPropagation()}>
                                          <button
                                            onClick={async () => {
                                              const matchId = notif.data.matchId;
                                              // Mark notification as read when clicking the button
                                              await updateDoc(doc(db, getCollectionPath('notifications'), notif.id), { read: true });

                                              if (window.location.pathname !== '/') {
                                                localStorage.setItem('pendingMatchInvite', matchId + '|' + notif.id + '|' + (notif.data.slotIdx ?? ''));
                                                window.location.href = '/?tab=partidos';
                                              } else {
                                                setActiveTab('partidos');
                                                setShowNotifications(false);
                                                setTimeout(() => {
                                                  window.dispatchEvent(new CustomEvent('openPublicMatchInviteView', { detail: { matchId, notificationId: notif.id, slotIdx: notif.data.slotIdx } }));
                                                }, 300);
                                              }
                                            }}
                                            className="flex-1 py-1.5 px-3 rounded-lg text-xs font-black bg-white hover:bg-gray-100 text-black transition-colors border border-transparent shadow-lg"
                                          >
                                            Ver Invitación
                                          </button>
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              const matchId = notif.data.matchId;
                                              try {
                                                const matchRef = doc(db, getCollectionPath('public_matches'), matchId);
                                                const matchSnap = await getDoc(matchRef);
                                                if (matchSnap.exists()) {
                                                  const matchData = matchSnap.data();
                                                  if (matchData.pendingInvitations) {
                                                    const newPending = matchData.pendingInvitations.filter((inv: any) => inv.inviteeId !== user?.uid);
                                                    await updateDoc(matchRef, { pendingInvitations: newPending });
                                                  }
                                                }
                                              } catch (err) {
                                                console.error('Error rejecting invite', err);
                                              }
                                              await updateDoc(doc(db, getCollectionPath('notifications'), notif.id), { read: true });
                                            }}
                                            className="px-3 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white font-bold text-xs py-1.5 rounded-lg transition-colors border border-white/10 flex items-center justify-center gap-1"
                                          >
                                            <X size={14} />
                                          </button>
                                        </div>
                                      )}
                                      {/* BOTONES DE ACCIÓN IN-APP PARA VALIDACIÓN DE RESULTADOS PUBLICOS */}
                                      {notif.type === 'match_reported' && notif.title === 'Resultado Cargado' && notif.matchId && !notif.actionTaken && (
                                        <div className="flex gap-1.5 mt-2 mb-1" onClick={(e) => e.stopPropagation()}>

                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              setShowNotifications(false);
                                              const match = matches.find(m => m.id === notif.matchId);
                                              if (match) {
                                                if (match.tournamentId) {
                                                  setActiveTab('torneos');
                                                  setTimeout(() => {
                                                    setExpandedPublicTournamentId(match.tournamentId);
                                                    setTimeout(() => {
                                                      const el = document.querySelector(`[data-tournament-id="${match.tournamentId}"]`);
                                                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                      setSelectedMatchDetails(match);
                                                    }, 350);
                                                  }, 100);
                                                } else {
                                                  setSelectedMatchDetails(match);
                                                }
                                              } else {
                                                setActiveTab('partidos');
                                                setTimeout(() => {
                                                  window.dispatchEvent(new CustomEvent('openPublicMatch', { detail: { matchId: notif.matchId } }));
                                                }, 500);
                                              }
                                            }}
                                            className="flex-1 bg-white/10 hover:bg-white/20 text-white font-black text-[9px] py-2 rounded-lg transition-all uppercase tracking-wider border border-white/10 active:scale-95 flex items-center justify-center gap-1"
                                          >
                                            Ver Resultado
                                          </button>
                                        </div>
                                      )}

                                      <div className="text-[9px] opacity-40">Hace {Math.floor((Date.now() - notif.createdAt) / 60000)} min</div>
                                    </div>
                                    {!notif.read && (
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          await updateDoc(doc(db, 'notifications', notif.id), { read: true });
                                        }}
                                        className="absolute top-2 right-2 p-1 rounded-full text-white/30 hover:text-white transition-all duration-300"
                                        title="Marcar como leída"
                                      >
                                        <CheckCircle2 size={12} strokeWidth={2.5} />
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteDoc(doc(db, 'notifications', notif.id));
                                      }}
                                      className="absolute right-2 bottom-2 p-1 opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 align-bottom transition-all bg-black/40 rounded-md"
                                      title="Archivar"
                                    >
                                      <Trash2 size={10} />
                                    </button>
                                  </motion.div>
                                ))}
                                {filteredNotifications.length > notificationsLimit && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setNotificationsLimit(prev => prev + 4); }}
                                    className="w-full py-2 text-xs font-bold text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition-colors mt-2"
                                  >
                                    Ver más
                                  </button>
                                )}
                              </div>
                            </motion.div>
                          </div>
                        )}
                      </div>

                      {/* AVATAR + DROPDOWN MENU */}
                      <div className="relative" ref={profileMenuRef}>
                        <button
                          onClick={() => {
                            setShowProfileMenu(!showProfileMenu);
                            setShowNotifications(false);
                            setShowPrivateMessages(false);
                          }}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                          className={`p-1 rounded-full outline-none focus:outline-none transition-all flex items-center group hover:scale-105 ${showProfileMenu
                            ? 'ring-2 ring-theme-primary shadow-[0_0_12px_rgba(var(--glow-rgb),0.5)]'
                            : ''
                            }`}
                          title="Menú de perfil"
                        >
                          {userProfile?.photoUrl ? (
                            <img
                              src={userProfile.photoUrl}
                              alt="Perfil"
                              className={`w-8 h-8 md:w-9 md:h-9 rounded-full object-cover shrink-0 aspect-square transition-all duration-300 ${showProfileMenu ? 'ring-2 ring-theme-primary shadow-[0_0_12px_rgba(var(--glow-rgb), 0.8)] brightness-110' : 'ring-1 ring-white/10 group-hover:ring-theme-primary/50'
                                }`}
                            />
                          ) : (
                            <div className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full transition-all ${showProfileMenu ? 'bg-theme-primary text-black shadow-[0_0_12px_rgba(var(--glow-rgb), 0.6)]' : 'bg-white/5 group-hover:bg-theme-primary/20 group-hover:theme-primary-text'}`}>
                              <UserIcon size={18} />
                            </div>
                          )}
                        </button>

                        {/* Dropdown Menu */}
                        {showProfileMenu && (
                          <motion.div
                            initial={{ opacity: 0, y: -8, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.95 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                            className="absolute right-0 top-full mt-2 w-64 bg-[#0f1118]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden z-[200]"
                          >
                            {/* User info header */}
                            <div className="px-4 py-3.5 border-b border-white/5 bg-white/[0.02]">
                              <div className="flex items-center gap-3">
                                {userProfile?.photoUrl ? (
                                  <FadeInImage src={userProfile.photoUrl} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center ring-2 ring-white/10 shrink-0">
                                    <UserIcon size={20} className="text-white/50" />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <p className="text-sm font-black text-white truncate">{userProfile?.name || user?.displayName || 'Jugador'}</p>
                                  <p className="text-[10px] text-white/40 truncate">{user?.email}</p>
                                  {userRole && userRole !== 'user' && (
                                    <span className="inline-block mt-0.5 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider bg-yellow-400/10 text-yellow-400 rounded-md">
                                      {userRole === 'admin' ? 'Administrador' : userRole === 'superadmin' ? 'Super Admin' : userRole === 'co_admin' ? 'Co-Admin' : userRole}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Menu Items */}
                            <div className="py-1.5">
                              {(userRole !== 'admin' && userRole !== 'superadmin') && (
                                <button
                                  onClick={() => {
                                    setShowProfileMenu(false);
                                    setActiveTab('perfil');
                                  }}
                                  className="w-full px-4 py-2.5 text-left text-sm text-white/80 hover:text-white hover:bg-white/5 transition-all flex items-center gap-3 group"
                                >
                                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-theme-primary/10 transition-colors">
                                    <UserIcon size={16} className="text-white/50 group-hover:text-theme-primary transition-colors" />
                                  </div>
                                  <div>
                                    <span className="font-bold block">Mi Perfil</span>
                                    <span className="text-[10px] text-white/30">Datos personales y foto</span>
                                  </div>
                                </button>
                              )}

                              <button
                                onClick={() => {
                                  setShowProfileMenu(false);
                                  setShowSettingsModal(true);
                                  setSettingsTab('general');
                                }}
                                className="w-full px-4 py-2.5 text-left text-sm text-white/80 hover:text-white hover:bg-white/5 transition-all flex items-center gap-3 group"
                              >
                                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-theme-primary/10 transition-colors">
                                  <Settings size={16} className="text-white/50 group-hover:text-theme-primary transition-colors" />
                                </div>
                                <div>
                                  <span className="font-bold block">Configuración</span>
                                  <span className="text-[10px] text-white/30">Ajustes y notificaciones</span>
                                </div>
                              </button>

                              {userProfile?.isOrganizer && (
                                <>
                                  <div className="mx-3 my-1 border-t border-white/5" />
                                  <div className="px-4 py-2 flex items-center justify-between gap-3 select-none hover:bg-white/5 transition-colors rounded-xl mx-2">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${isOrganizerModeActive
                                          ? 'theme-primary-bg text-black border border-theme-primary/20'
                                          : 'bg-white/5 text-white/40 border border-white/5'
                                        }`}>
                                        <Trophy size={16} className={`transition-transform duration-500 ${isOrganizerModeActive ? 'scale-110' : 'opacity-60'}`} />
                                      </div>
                                      <div>
                                        <span className="font-bold block text-xs text-white/90">Modo Organizador</span>
                                        <span className={`text-[9px] transition-colors duration-300 ${isOrganizerModeActive ? 'text-theme-primary font-semibold' : 'text-white/30'}`}>
                                          {isOrganizerModeActive ? 'Gestión Activada' : 'Vista de Jugador'}
                                        </span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleOrganizerMode();
                                        // If activating organizer, disable admin if it was active
                                        if (!isOrganizerModeActive && isAdminModeActive) {
                                          toggleAdminMode();
                                        }
                                      }}
                                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-300 ease-in-out focus:outline-none ${isOrganizerModeActive ? 'theme-primary-bg shadow-[0_0_10px_rgba(var(--glow-rgb),0.3)]' : 'bg-white/10'
                                        }`}
                                    >
                                      <span
                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-black shadow-md ring-0 transition duration-300 ease-in-out ${isOrganizerModeActive ? 'translate-x-4' : 'translate-x-0'
                                          }`}
                                      />
                                    </button>
                                  </div>
                                </>
                              )}

                              {realUserProfile?.role === 'superadmin' && (
                                <>
                                  <div className="mx-3 my-1 border-t border-white/5" />
                                  <div className="px-4 py-2 flex items-center justify-between gap-3 select-none hover:bg-white/5 transition-colors rounded-xl mx-2">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${isAdminModeActive
                                          ? 'bg-yellow-400/10 text-yellow-400 border border-yellow-400/20'
                                          : 'bg-white/5 text-white/40 border border-white/5'
                                        }`}>
                                        <Shield size={16} className={`transition-transform duration-500 ${isAdminModeActive ? 'scale-110 animate-pulse' : 'opacity-60'}`} />
                                      </div>
                                      <div>
                                        <span className="font-bold block text-xs text-white/90">Modo Administrador</span>
                                        <span className={`text-[9px] transition-colors duration-300 ${isAdminModeActive ? 'text-yellow-400/70 font-semibold' : 'text-white/30'}`}>
                                          {isAdminModeActive ? 'Acceso Master Activado' : 'Vista de Jugador'}
                                        </span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleAdminMode();
                                      }}
                                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-300 ease-in-out focus:outline-none ${isAdminModeActive ? 'bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.3)]' : 'bg-white/10'
                                        }`}
                                    >
                                      <span
                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-black shadow-md ring-0 transition duration-300 ease-in-out ${isAdminModeActive ? 'translate-x-4' : 'translate-x-0'
                                          }`}
                                      />
                                    </button>
                                  </div>
                                </>
                              )}

                              <div className="mx-3 my-1 border-t border-white/5" />

                              <button
                                onClick={() => {
                                  setShowProfileMenu(false);
                                  handleAdminLogout();
                                }}
                                className="w-full px-4 py-2.5 text-left text-sm text-red-400/80 hover:text-red-400 hover:bg-red-500/5 transition-all flex items-center gap-3 group"
                              >
                                <div className="w-8 h-8 rounded-lg bg-red-500/5 flex items-center justify-center group-hover:bg-red-500/10 transition-colors">
                                  <LogOut size={16} className="text-red-400/50 group-hover:text-red-400 transition-colors" />
                                </div>
                                <div>
                                  <span className="font-bold block">Cerrar Sesión</span>
                                  <span className="text-[10px] text-red-400/30">Salir de tu cuenta</span>
                                </div>
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </div>

                      {isAdminOrSuper && (
                        <div className="flex flex-col items-center justify-center mx-0.5">
                          <button
                            onClick={() => {
                              setActiveTab('admin');
                              if (userRole === 'admin') {
                                setAdminSection('turnos');
                              }
                            }}
                            style={{ WebkitTapHighlightColor: 'transparent', color: activeTab === 'admin' ? '#000000' : '#ffffff' }}
                            className={`px-3 py-1 md:px-4 md:py-1.5 rounded-full border outline-none focus:outline-none text-xs font-black transition-all flex items-center gap-1.5 group hover:scale-105 shrink-0 ${activeTab === 'admin'
                              ? 'bg-yellow-400 text-black border-yellow-500 shadow-[0_0_15px_rgba(250,204,21,0.5)]'
                              : 'bg-gray-800 hover:bg-gray-700 text-white border-gray-600 shadow-md'
                              }`}
                            title="Panel de Gestión"
                          >
                            <Settings size={14} color={activeTab === 'admin' ? '#000000' : '#ffffff'} className={`shrink-0 ${activeTab === 'admin' ? 'animate-spin-slow' : ''}`} />
                            <span className="hidden sm:inline font-bold tracking-tight md:tracking-wider text-[9px] md:text-[10px]" style={{ color: activeTab === 'admin' ? '#000000' : '#ffffff' }}>PANEL</span>
                          </button>
                          <span className="hidden sm:block text-[7px] md:text-[8px] font-bold text-white/50 tracking-widest uppercase mt-0.5 truncate max-w-[65px] md:max-w-[85px] text-center leading-tight">
                            {userRole === 'admin' ? 'ADMIN' : userRole === 'superadmin' ? 'MASTER' : (() => {
                              const staffClub = sedesPublicas.find(s => s.uid === userProfile?.clubId || (s as any).id === userProfile?.clubId);
                              return staffClub?.name || userProfile?.name || 'CLUB';
                            })()}
                          </span>
                        </div>
                      )}


                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 md:gap-3">
                      <div className="flex items-center gap-0.5 md:gap-1 mr-1">
                        {/* Decorative Friends */}
                        <button className="relative p-1.5 md:p-2 rounded-full transition-colors hover:bg-white/10 text-white/30 hover:text-white" title="Amigos (Ingresa para usar)">
                          <Users size={18} className="md:w-5 md:h-5" />
                        </button>
                        {/* Decorative Messages */}
                        <button className="relative p-1.5 md:p-2 rounded-full transition-colors hover:bg-white/10 text-white/30 hover:text-white" title="Mensajes (Ingresa para usar)">
                          <MessageCircle size={18} className="md:w-5 md:h-5" />
                        </button>
                        {/* Decorative Notifications */}
                        <button className="relative p-1.5 md:p-2 rounded-full transition-colors hover:bg-white/10 text-white/30 hover:text-white" title="Notificaciones (Ingresa para usar)">
                          <Bell size={18} className="md:w-5 md:h-5" />
                        </button>
                      </div>
                      <button
                        onClick={() => handleOpenLoginModal('login')}
                        className={`px-3 py-1.5 md:px-4 md:py-2 rounded-xl md:rounded-full text-[10px] md:text-xs font-bold flex items-center gap-1.5 text-white/80 hover:text-white bg-white/5 hover:bg-white/10 transition-colors border border-white/10 shadow-sm`}
                      >
                        <LogIn size={14} className="md:w-4 md:h-4" /> <span className="hidden sm:inline uppercase tracking-wider">Ingresar</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* SETTINGS MODAL */}
        {showSettingsModal && user && typeof document !== 'undefined' && createPortal(
          <div className="fixed inset-0 flex items-center justify-center bg-black/90 p-4 animate-in fade-in duration-200" style={{ zIndex: 99999 }}>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-full max-w-lg bg-[#0f1118] border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
            >
              {/* Header */}
              <div className="relative px-5 py-4 border-b border-white/5 flex items-center justify-center bg-white/[0.02] shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-theme-primary/10 flex items-center justify-center">
                    <Settings size={18} className="text-theme-primary" />
                  </div>
                  <h2 className="text-base font-black uppercase tracking-wider text-white">Configuración</h2>
                </div>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="absolute right-4 p-2 hover:bg-white/10 rounded-xl transition-colors text-white/50 hover:text-white"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Sub-tabs */}
              <div className="flex border-b border-white/5 bg-black/20 p-1.5 gap-1.5 shrink-0">
                <button
                  onClick={() => setSettingsTab('general')}
                  className={`flex-1 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 ${settingsTab === 'general' ? 'bg-[#facc15] text-black shadow-lg shadow-yellow-400/20' : 'bg-[#242730] border border-white/5 text-white/40 hover:text-white/70 hover:bg-white/5'}`}
                >
                  <Settings size={14} />
                  Ajustes Generales
                </button>
                <button
                  onClick={() => setSettingsTab('notifications')}
                  className={`flex-1 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 ${settingsTab === 'notifications' ? 'bg-[#facc15] text-black shadow-lg shadow-yellow-400/20' : 'bg-[#242730] border border-white/5 text-white/40 hover:text-white/70 hover:bg-white/5'}`}
                >
                  <Bell size={14} />
                  Notificaciones
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-5 custom-scrollbar" style={{ WebkitTransform: 'translateZ(0)' }}>
                {settingsTab === 'general' && (
                  <div className="space-y-5 animate-in fade-in duration-200">
                    {/* App Version */}
                    <div className="bg-[#242730] border border-white/10 rounded-xl p-4 space-y-4 shadow-sm">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-yellow-400/10 flex items-center justify-center shrink-0">
                          <Info size={14} className="text-yellow-400" />
                        </div>
                        <h3 className="text-sm font-black uppercase tracking-wide text-white">Información de la App</h3>
                      </div>
                      <div className="flex items-center justify-between pl-9.5">
                        <span className="text-xs text-white/50">Versión</span>
                        <span className="text-xs font-bold text-black bg-white px-2.5 py-1 rounded-lg">v2.0.0</span>
                      </div>
                      <div className="flex items-center justify-between pl-9.5">
                        <span className="text-xs text-white/50">Plataforma</span>
                        <span className="text-xs font-bold text-black bg-white px-2.5 py-1 rounded-lg">
                          {/android/i.test(navigator.userAgent) ? 'Android' : /iphone|ipad/i.test(navigator.userAgent) ? 'iOS' : 'Web'}
                        </span>
                      </div>
                    </div>

                    {/* Account */}
                    <div className="bg-[#242730] border border-white/10 rounded-xl p-4 space-y-4 shadow-sm">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-yellow-400/10 flex items-center justify-center shrink-0">
                          <UserIcon size={14} className="text-yellow-400" />
                        </div>
                        <h3 className="text-sm font-black uppercase tracking-wide text-white">Mi Cuenta</h3>
                      </div>
                      <div className="flex items-center justify-between pl-9.5">
                        <span className="text-xs text-white/50">Email</span>
                        <span className="text-xs font-bold text-white/80 truncate max-w-[200px]">{user.email}</span>
                      </div>
                      <div className="flex items-center justify-between pl-9.5">
                        <span className="text-xs text-white/50">Rol</span>
                        <span className="text-xs font-bold text-black bg-white px-2.5 py-1 rounded-lg capitalize">
                          {userRole === 'admin' ? 'Administrador' : userRole === 'superadmin' ? 'Super Admin' : userRole === 'user' ? 'Jugador' : userRole}
                        </span>
                      </div>
                    </div>

                    {/* Install PWA */}
                    <div className="bg-[#242730] border border-white/10 rounded-xl p-4 space-y-4 shadow-sm">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-yellow-400/10 flex items-center justify-center shrink-0">
                          <Download size={14} className="text-yellow-400" />
                        </div>
                        <h3 className="text-sm font-black uppercase tracking-wide text-white">Instalar App</h3>
                      </div>
                      <p className="text-xs text-white/50 leading-relaxed pl-9.5">
                        Instalá la app en tu dispositivo para acceder más rápido y recibir notificaciones push incluso con el navegador cerrado.
                      </p>
                      <button
                        onClick={() => {
                          if ((window as any).deferredPrompt) {
                            (window as any).deferredPrompt.prompt();
                          } else {
                            alert('Para instalar: abrí el menú del navegador (⋮) y seleccioná "Instalar app" o "Agregar a pantalla de inicio".');
                          }
                        }}
                        className="w-full ml-9.5 py-2.5 bg-[#facc15] hover:bg-[#eab308] text-black font-black text-[10px] uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm"
                        style={{ width: 'calc(100% - 2.375rem)' }}
                      >
                        <Download size={14} />
                        Instalar en mi dispositivo
                      </button>
                    </div>
                  </div>
                )}

                {settingsTab === 'notifications' && userProfile && (
                  <NotificationSettings 
                    userProfile={userProfile}
                    onUpdatePreferences={async (newPrefs) => {
                      try {
                        await updateDoc(doc(db, 'users', user.uid), {
                          notificationPreferences: newPrefs
                        });
                      } catch (err) {
                        console.error('Error updating notification preferences:', err);
                        alert('Error al guardar preferencias.');
                      }
                    }}
                    onToggleMasterPush={async () => {
                      if (!userProfile?.notificationsEnabled && Notification.permission === 'denied') {
                        setIsBlockedModalOpen(true);
                        return;
                      }
                      const { requestNotificationPermission, disableNotifications } = await import('./lib/notifications');
                      if (userProfile?.notificationsEnabled) {
                        await disableNotifications(user.uid);
                      } else {
                        await requestNotificationPermission(user.uid);
                      }
                    }}
                  />
                )}
              </div>
            </motion.div>
          </div>,
          document.body
        )}

        {/* ADMIN PENDING REQUESTS BANNER */}
        {['admin', 'co_admin', 'empleado', 'gestor_turnos'].includes(userRole) && pendingBookingRequestsCount > 0 && (
          <>
            {/* Spacer: Always visible on mobile since banner is always fixed, conditionally visible on desktop */}
            <div className={`w-full h-[41px] shrink-0 block md:${isBannerFixed ? 'block' : 'hidden'}`} />
            
            <div className={`w-full backdrop-blur-md z-[90] cursor-pointer transition-colors duration-200 group fixed md:${isBannerFixed ? 'fixed md:top-0' : 'relative md:top-auto'} top-[calc(5rem+env(safe-area-inset-top,0px))] left-0 right-0`}
              style={{
                backgroundColor: 'rgba(190, 242, 100, 0.12)',
                borderBottom: '1px solid rgba(190, 242, 100, 0.25)'
              }}
              onClick={() => {
                setAdminSection('turnos');
                setActiveTab('admin');
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('open-pending-requests'));
                }, 300);
              }}>
              <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-2.5 flex justify-center items-center">
                <span className="font-black text-xs md:text-sm tracking-widest uppercase flex items-center gap-2 group-hover:scale-105 transition-transform duration-300" style={{ color: '#bef264' }}>
                  <span className="relative flex h-2.5 w-2.5 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: '#bef264' }}></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 shadow-[0_0_8px_rgba(190,242,100,0.8)]" style={{ backgroundColor: '#bef264' }}></span>
                  </span>
                  Turnos pendientes ({pendingBookingRequestsCount})
                </span>
              </div>
            </div>
          </>
        )}

        {/* CARRUSEL DE SPONSORS ARRIBA DE TODO, DEBAJO DEL PANEL DE NAVEGACION (Solo para usuarios registrados) */}
        {user && renderSponsorsCarousel(false, true)}

        <main className={`${activeTab === 'admin' || activeTab === 'reservas' ? 'max-w-[90rem]' : 'max-w-6xl'} mx-auto ${activeTab === 'reservas' ? 'px-0 sm:px-4' : 'px-2 sm:px-4'} ${activeTab === 'partidos' ? 'pt-4' : 'pt-8'} pb-24 lg:pb-8 w-full`}>
          {/* NotificationBanner removed in favor of onboarding + dropdown toggle */}

          {/* VISTA: SOCIAL (Para usuarios registrados y publico) */}
          {activeTab === 'social' && (
            <div className="animate-in fade-in duration-500 w-full flex flex-col min-w-0">
              <SocialDashboard
                userProfile={userProfile}
                currentPlayer={players.find(p => p.id === user?.uid)}
                config={config}
                sedesPublicas={sedesPublicas}
                globalCity={globalCity}
                matches={matches.filter(m => {
                  if (m.team1Id || m.team2Id) {
                    return teams.some(t => (t.player1Id === user?.uid || t.player2Id === user?.uid || t.player3Id === user?.uid) && (t.id === m.team1Id || t.id === m.team2Id));
                  }
                  return m.players?.includes(user?.uid || '');
                })}
                players={players}
                teams={teams}
                tournaments={tournaments}
                recentNotifications={filteredNotifications}
                onMatchClick={(m) => {
                  setActiveTab('torneos');
                  setTorneosTab('mi_fixture');
                  
                  const tName = m.tournamentName || 'Torneo';
                  const bannersToExpand = [tName];
                  const tournamentObj = tournaments.find(t => t.id === m.tournamentId);

                  if (tournamentObj?.type === 'liga' && m.zoneName && m.zoneName !== 'Zona Única') {
                    bannersToExpand.push(`${tName}::${m.zoneName}`);
                  }
                  const rName = (m.round || '').toLowerCase();
                  const isElim = rName.includes('8vos') || rName.includes('4tos') ||
                    rName.includes('semi') || rName.includes('final') ||
                    rName.includes('eliminaci');
                  if (isElim) bannersToExpand.push(`${tName}::Elimination`);

                  setExpandedBanners(prev => [...new Set([...prev, ...bannersToExpand])]);

                  let attempts = 0;
                  const interval = setInterval(() => {
                    const element = document.querySelector(`[data-match-id="${m.id}"]`);
                    if (element) {
                      clearInterval(interval);
                      setTimeout(() => {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setHighlightedMatchId(m.id);
                        setTimeout(() => setHighlightedMatchId(null), 2500);
                      }, 150);
                    } else if (attempts++ >= 20) {
                      clearInterval(interval);
                    }
                  }, 100);
                }}
                onContactClick={(playerId, playerName, playerPhoto) => {
                  setActivePrivateChatRecipientId(playerId);
                  setActivePrivateChatRecipientName(playerName);
                  setActivePrivateChatRecipientPhoto(playerPhoto || '');
                }}
                onPlayerClick={openPlayerDetails}
                onSedeClick={(sedeId) => {
                  const sede = sedesPublicas.find(s => s.uid === sedeId);
                  if (sede) setViewingSede(sede);
                }}
              />
            </div>
          )}

          {/* VISTA: INICIO */}
          {activeTab === 'inicio' && (
            <div className="animate-in fade-in duration-500 w-full flex flex-col min-w-0">

              {/* GUEST LANDING / PUBLIC TOURNAMENT VIEW — solo para usuarios no logueados */}
              {!user && (
                <GuestLanding
                  config={config}
                  players={players}
                  onRegisterClick={guestRegisterClick}
                  onLoginClick={guestLoginClick}
                  sponsorsCarousel={
                    <div className="hidden lg:block w-full">
                      {renderSponsorsCarousel(false)}
                    </div>
                  }
                  topComponent={
                    guestTournamentViewId && tournaments.find(t => t.id === guestTournamentViewId) ? (
                      <PublicTournamentView
                        tournament={tournaments.find(t => t.id === guestTournamentViewId)!}
                        config={config}
                        rankingConfig={rankingConfig}
                        allTeams={teams}
                        players={players}
                        allUsers={registeredUsers}
                        clubs={clubs}
                        sedesPublicas={sedesPublicas}
                        onRegisterClick={guestRegisterClick}
                        onLoginClick={guestLoginClick}
                      />
                    ) : undefined
                  }
                />
              )}

              {/* SPONSORS */}
              {user && renderSponsorsCarousel(false)}


              {/* BANNER CARRUSEL */}
              {config.feature_welcome !== false && config.showWelcomeMessage !== false && (config.welcomeBanners?.length ? config.welcomeBanners.length > 0 : false) && (
                <div className="relative w-full max-w-6xl mx-auto mb-6 flex flex-col items-center">
                  <div className="relative w-full rounded-2xl md:rounded-[var(--radius-large)] overflow-hidden animate-in slide-in-from-top-4 duration-700 aspect-[15/4]">
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.img
                        key={currentWelcomeBanner}
                        src={config.welcomeBanners[currentWelcomeBanner]}
                        alt={`Welcome Banner ${currentWelcomeBanner + 1}`}
                        initial={{ opacity: 0, scale: 1.05 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </AnimatePresence>
                  </div>

                  {/* CTA BUTTON COMPONENT */}
                  {!user && config.welcomeCtaUrl && (
                    <button
                      onClick={() => handleOpenLoginModal('register')}
                      className="group relative mt-4 md:mt-6 z-20 w-[85%] max-w-[320px] md:max-w-[460px] lg:max-w-[560px] aspect-[4/1] hover:scale-[1.04] active:scale-[0.98] transition-all duration-700 ease-out overflow-visible focus:outline-none"
                    >
                      {/* Subtle Glow Behind */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-40 transition-opacity duration-1000 blur-2xl rounded-full" style={{ backgroundColor: config.primaryColor || '#eab308' }} />

                      <img
                        src={config.welcomeCtaUrl}
                        alt="Call to action"
                        className="relative z-10 w-full h-full object-contain filter group-hover:brightness-110 drop-shadow-[0_5px_15px_rgba(0,0,0,0.5)] group-hover:drop-shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-700"
                      />
                    </button>
                  )}
                </div>
              )}


              {/* NOVEDADES PUBLICAS */}
              {config.feature_news !== false && news.length > 0 && (
                <div className="mb-6 space-y-4">
                  <div className="flex items-center justify-center gap-4 mb-4 mt-6">
                    <div className="h-px bg-white opacity-50 flex-1 max-w-[30px] md:max-w-[50px]" />
                    <h3 className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.5em] text-center italic m-0 text-white opacity-50">
                      NOVEDADES
                    </h3>
                    <div className="h-px bg-white opacity-50 flex-1 max-w-[30px] md:max-w-[50px]" />
                  </div>
                  <div className={`grid grid-cols-1 ${news.length > 1 ? 'md:grid-cols-2' : ''} gap-4`}>
                    {news
                      .sort((a, b) => b.createdAt - a.createdAt)
                      .map((n) => (
                        <div
                          key={n.id}
                          className={`p-6 rounded-2xl border backdrop-blur-md shadow-lg ${n.type === 'alert' ? 'bg-red-500/10 border-red-500/30' : 'theme-bg-card border-white/10'}`}
                        >
                          <div className="flex items-start gap-3">
                            {n.type === 'alert' ? (
                              <AlertTriangle className="text-red-400 mt-1 flex-shrink-0" />
                            ) : (
                              <FileText className="theme-primary-text mt-1 flex-shrink-0" />
                            )}
                            <div>
                              <h3 className={`font-bold text-lg mb-2 ${n.type === 'alert' ? 'text-red-300' : ''}`}>{n.title}</h3>
                              <p className="opacity-80 text-sm whitespace-pre-wrap leading-relaxed">{n.content}</p>
                              {n.images && n.images.length > 0 && (
                                <div className="mt-4 flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
                                  {n.images.map((img, i) => (
                                    <img
                                      key={i}
                                      src={img}
                                      alt={`Foto ${i + 1}`}
                                      onClick={() => setExpandedImage(img)}
                                      className="h-32 sm:h-40 w-auto rounded-lg object-cover border border-white/10 shadow-xl flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                    />
                                  ))}
                                </div>
                              )}
                              <div className="mt-3 text-[10px] uppercase tracking-widest opacity-40 font-bold">
                                {new Date(n.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

            </div>
          )}






          {/* VISTA: RESERVAS */}
          {activeTab === 'reservas' && (
            <div className="animate-in fade-in duration-500 w-full flex flex-col min-w-0 relative">

              <div className={`w-full flex flex-col min-w-0 transition-all duration-500`}>
                <MyBookings
                  currentUser={{
                    uid: user?.uid || '',
                    email: user?.email || '',
                    name: userProfile?.name || user?.displayName || '',
                    photoUrl: userProfile?.photoUrl || user?.photoURL || ''
                  }}
                  players={players}
                  db={db}
                  getCollectionPath={getCollectionPath}
                  config={config}
                  sedesPublicas={sedesPublicas.filter(s => globalCity === 'Todas las ciudades' || s.city === globalCity)}
                  initialSubTab={bookingSubTab}
                  onSubTabChange={setBookingSubTab}
                  onRequireLogin={() => handleOpenLoginModal('login')}
                  onOpenChatWithClub={(clubId) => {
                    if (!user) {
                      handleOpenLoginModal('login');
                      return;
                    }
                    const club = sedesPublicas.find(s => s.uid === clubId);
                    if (club) {
                      setActivePrivateChatRecipientId(club.uid);
                      setActivePrivateChatRecipientName(club.name || 'Sede Oficial');
                      setActivePrivateChatRecipientPhoto(club.photoUrl || '');
                      setActivePrivateChatRecipientPhone((club as any).whatsapp || club.phone || '');
                      setActivePrivateChatIsSupport(false);
                      setShowPrivateMessages(true);
                    } else {
                      setActivePrivateChatRecipientId(clubId);
                      setActivePrivateChatRecipientName('Administración');
                      setActivePrivateChatRecipientPhoto('');
                      setActivePrivateChatRecipientPhone('');
                      setActivePrivateChatIsSupport(false);
                      setShowPrivateMessages(true);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* VISTA: TORNEOS PUBLICOS */}
          {activeTab === 'torneos' && (
            <div className="animate-in fade-in duration-500 relative">

              <div className={`flex flex-col gap-6 mb-8 transition-all duration-500`}>
                <div className="flex flex-col items-center text-center gap-5">
                  <div className="flex flex-col items-center w-full px-0 sm:px-0">
                    <div className="flex items-center justify-center w-full sm:max-w-4xl gap-1 sm:gap-2">
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-white/40"></div>
                      <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-widest text-white flex items-center justify-center gap-3 shrink-0 drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]">
                        <Trophy className="text-white w-7 h-7 sm:w-9 sm:h-9 opacity-90" />
                        Torneos / Ligas
                      </h2>
                      <div className="h-px flex-1 bg-gradient-to-l from-transparent via-white/10 to-white/40"></div>
                    </div>
                  </div>

                  {/* Main Primary Sub-tabs */}
                  <div className="flex w-full max-w-md md:max-w-2xl lg:max-w-3xl mx-auto justify-between items-center gap-2 md:gap-4 relative z-10">
                    {[
                      { id: 'inscripciones', label: 'Inscripciones' },
                      { id: 'mi_fixture', label: 'Mi Fixture' },
                      { id: 'activos', label: 'Torneos' }
                    ].map((tab) => {
                      const isActive = torneosTab === tab.id;
                      let count = 0;
                      if (tab.id === 'inscripciones') {
                        count = tournaments.filter(t => {
                          const org = sedesPublicas.find(s => s.uid === t.organizerId);
                          const matchesCity = globalCity === 'Todas las ciudades' || !org || org.city === globalCity;
                          return matchesCity && !t.isArchived && (t.status === 'Registro' || t.status === 'Borrador' || t.status === 'Lobby' || !t.status);
                        }).length;
                      } else if (tab.id === 'activos') {
                        count = tournaments.filter(t => {
                          const org = sedesPublicas.find(s => s.uid === t.organizerId);
                          const matchesCity = globalCity === 'Todas las ciudades' || !org || org.city === globalCity;
                          return matchesCity && !t.isArchived && (t.status === 'En Juego' || t.status === 'Fase de Grupos' || t.status === 'Eliminatorias');
                        }).length;
                      } else if (tab.id === 'mi_fixture' && userProfile) {
                        const userMatches = matches.filter(m => {
                          if (!m.tournamentId) return false;
                          const tournament = tournaments.find(t => t.id === m.tournamentId);
                          if (!tournament || tournament.isArchived) return false;
                          const org = sedesPublicas.find(s => s.uid === tournament.organizerId);
                          const matchesCity = globalCity === 'Todas las ciudades' || !org || org.city === globalCity;
                          if (!matchesCity) return false;
                          const t1 = getTeam(m.team1Id);
                          const t2 = getTeam(m.team2Id);
                          const isUserInTeam1 = t1?.player1Id === userProfile.uid || t1?.player2Id === userProfile.uid || t1?.player3Id === userProfile.uid;
                          const isUserInTeam2 = t2?.player1Id === userProfile.uid || t2?.player2Id === userProfile.uid || t2?.player3Id === userProfile.uid;
                          return isUserInTeam1 || isUserInTeam2;
                        });
                        count = new Set(userMatches.map(m => m.tournamentId)).size;
                      }

                      const isAdminOrSuper = userRole === 'admin' || userRole === 'superadmin';
                      const isLockedTab = tab.id === 'mi_fixture' && isAdminOrSuper;

                      return (
                        <button
                          key={tab.id}
                          onClick={() => {
                            if (isLockedTab) return;
                            setTorneosTab(tab.id as any);
                            setExpandedPublicTournamentId(null);
                          }}
                          disabled={isLockedTab}
                          title={isLockedTab ? 'No disponible para administradores' : undefined}
                          className={`relative flex-1 min-w-0 px-2 sm:px-3 py-3 md:py-4 rounded-xl text-[9px] sm:text-[10px] md:text-sm font-black uppercase tracking-wider md:tracking-widest flex items-center justify-center outline-none transition-all shadow-xl ${isLockedTab
                            ? 'bg-black/20 text-white/20 border border-white/5 cursor-not-allowed'
                            : isActive
                              ? 'theme-primary-bg text-black border-2 border-transparent scale-[1.02] z-10 shadow-[0_8px_20px_rgba(var(--glow-rgb),0.3)]'
                              : 'bg-black/40 backdrop-blur-md text-white/50 hover:text-white hover:bg-black/50 border border-white/10 hover:border-white/20'
                            }`}
                        >
                          <span className="w-full text-center leading-tight whitespace-normal break-words max-w-full flex items-center justify-center gap-1.5">
                            {isLockedTab && <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>}
                            {tab.label}
                          </span>
                          {count > 0 && !isLockedTab && (
                            <span className="absolute top-1 right-1 md:top-1.5 md:right-1.5 w-[14px] h-[14px] md:w-[16px] md:h-[16px] flex items-center justify-center rounded-full text-[7px] md:text-[8px] font-black bg-white text-black shadow-sm">
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {torneosTab === 'mi_fixture' && (
                  <div className="animate-in fade-in duration-300">


              <FixtureTutorial />

              <div className="space-y-6">
                {(() => {
                  const pendingMatches = matches.filter((m) => {
                    if (!m.tournamentId) return false;
                    const tournament = tournaments.find(t => t.id === m.tournamentId);
                    if (!tournament || tournament.isArchived) return false;

                    if (userProfile) {
                      const t1 = getTeam(m.team1Id);
                      const t2 = getTeam(m.team2Id);
                      const isUserInTeam1 = t1?.player1Id === userProfile.uid || t1?.player2Id === userProfile.uid || t1?.player3Id === userProfile.uid;
                      const isUserInTeam2 = t2?.player1Id === userProfile.uid || t2?.player2Id === userProfile.uid || t2?.player3Id === userProfile.uid;
                      return isUserInTeam1 || isUserInTeam2;
                    }

                    return false;
                  });

                  const filteredMatches = pendingMatches;

                  if (filteredMatches.length === 0) {
                    return (
                      <div className="bg-black/20 backdrop-blur-md py-8 px-6 rounded-[var(--radius-large)] text-center border border-white/10 shadow-xl flex flex-col items-center justify-center gap-6">
                        <p className="text-[10px] sm:text-xs text-white/60 font-bold uppercase tracking-wider">
                          Aún no tienes partidos de torneos pendientes
                        </p>
                        <button
                          onClick={() => setTorneosTab('inscripciones')}
                          className="px-4 py-1.5 rounded-lg text-black font-black uppercase text-[10px] sm:text-xs tracking-wider shadow-[0_0_15px_rgba(250,204,21,0.3)] hover:scale-105 transition-all"
                          style={{ backgroundColor: config.primaryColor || '#EAB308' }}
                        >
                          Anotarme en un torneo
                        </button>
                      </div>
                    );
                  }

                  // Hierarchical Grouping: Tournament -> [Zone] -> Round
                  const hierarchy: any = {};
                  filteredMatches.forEach(m => {
                    const tName = m.tournamentName || 'Torneo';
                    const tournament = tournaments.find(t => t.id === m.tournamentId);
                    const isLiga = tournament?.type === 'liga';
                    const zName = isLiga && m.zoneName && m.zoneName !== 'Zona Única' ? m.zoneName : null;
                    const rName = m.round || 'Sin Fecha';

                    const isElimination = rName.toLowerCase().includes('8vos') ||
                      rName.toLowerCase().includes('4tos') ||
                      rName.toLowerCase().includes('semi') ||
                      rName.toLowerCase().includes('final') ||
                      rName.toLowerCase().includes('3er') ||
                      rName.toLowerCase().includes('eliminaci');

                    if (!hierarchy[tName]) hierarchy[tName] = { isLiga, zones: {}, rounds: {}, elimination: {} };

                    if (isElimination) {
                      if (!hierarchy[tName].elimination[rName]) hierarchy[tName].elimination[rName] = [];
                      hierarchy[tName].elimination[rName].push(m);
                    } else if (zName) {
                      if (!hierarchy[tName].zones[zName]) hierarchy[tName].zones[zName] = {};
                      if (!hierarchy[tName].zones[zName][rName]) hierarchy[tName].zones[zName][rName] = [];
                      hierarchy[tName].zones[zName][rName].push(m);
                    } else {
                      if (!hierarchy[tName].rounds[rName]) hierarchy[tName].rounds[rName] = [];
                      hierarchy[tName].rounds[rName].push(m);
                    }
                  });

                  return Object.entries(hierarchy).map(([tName, tData]: [string, any]) => {
                    const isExpanded = expandedBanners.includes(tName);
                    const isSelected = selectedBanner === tName;
                    const tournamentObj = tournaments.find(t => t.name === tName || t.id === tName);
                    const sede = sedesPublicas.find(s => s.uid === tournamentObj?.organizerId);
                    const playerOrganizer = !sede ? players.find(p => p.id === tournamentObj?.organizerId) : null;
                    const foundUser = registeredUsers?.find(u => u.uid === tournamentObj?.organizerId);
                    const isSuperAdminOrganizer = sede?.role === 'superadmin' || 
                      tournamentObj?.organizerId === 'admin' || 
                      tournamentObj?.organizerId === 'main_club' || 
                      tournamentObj?.organizerId === 'agunavarro1989@gmail.com' ||
                      foundUser?.role === 'superadmin' ||
                      foundUser?.email === 'agunavarro1989@gmail.com' ||
                      playerOrganizer?.email === 'agunavarro1989@gmail.com' ||
                      playerOrganizer?.name?.toLowerCase().includes('agustin navarro');

                    const organizerName = isSuperAdminOrganizer ? config.pageName : (sede ? sede.name : (playerOrganizer ? (playerOrganizer.organizerProfile?.name || playerOrganizer.name) : 'Torneo'));
                    const organizerPhoto = isSuperAdminOrganizer ? config.logoUrl : (sede ? (sede.photoUrl || config.logoUrl) : (playerOrganizer ? (playerOrganizer.organizerProfile?.logoUrl || playerOrganizer.photoUrl) : null));

                    return (
                      <div key={tName} className="space-y-4">
                        {/* Tournament Banner */}
                        <div
                          key={tName}
                          data-grilla-tournament-id={tName}
                          className={`group bg-[#4aa6d6]/25 transition-all duration-300 hover:bg-[#4aa6d6]/35 ${!isSelected ? 'hover:scale-[1.01]' : ''} border mx-2 px-6 py-4 rounded-2xl flex items-center justify-between ring-1 ring-white/5 cursor-pointer relative overflow-hidden shadow-[0_4px_10px_rgba(0,0,0,0.35)] ${isSelected ? 'border-white/50' : 'border-white/10 hover:border-white/20'}`}
                          onClick={() => toggleBanner(tName)}
                        >
                          {/* Status Tags */}
                          {tournamentObj && (
                            <div className="absolute top-0 right-0 flex z-30 pointer-events-none">
                              {(tournamentObj.status === 'Lobby' || tournamentObj.status === 'Registro' || tournamentObj.status === 'Borrador' || !tournamentObj.status) && (
                                <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-bl-xl bg-lime-500/20 text-lime-400 text-[7px] md:text-[8px] font-black tracking-widest border-b border-l border-lime-500/30 shadow-sm backdrop-blur-md">
                                  <div className="w-1.5 h-1.5 rounded-full bg-lime-500/80 shadow-[0_0_8px_rgba(132,204,22,0.3)] animate-pulse" />
                                  INSCRIPCIÓN
                                </span>
                              )}
                              {(tournamentObj.status === 'En Juego' || tournamentObj.status === 'Fase de Grupos' || tournamentObj.status === 'Eliminatorias') && (
                                <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-bl-xl bg-white/10 text-white/90 text-[7px] md:text-[8px] font-black tracking-widest border-b border-l border-white/20 shadow-sm backdrop-blur-md">
                                  <div className="w-1.5 h-1.5 rounded-full bg-lime-500 shadow-[0_0_8px_rgba(132,204,22,0.4)]" />
                                  {tournamentObj.status?.toUpperCase()}
                                </span>
                              )}
                            </div>
                          )}

                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div className="flex flex-col items-center justify-center gap-1.5 shrink-0 pr-4 md:pr-5 border-r border-white/10">
                              <span className="text-[7px] md:text-[8px] font-black uppercase tracking-[0.2em] text-white/40">Organiza:</span>
                              
                              {organizerPhoto ? (
                                <div className="w-16 h-16 md:w-24 md:h-24 flex items-center justify-center">
                                  <FadeInImage
                                    src={organizerPhoto}
                                    alt={organizerName}
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                              ) : (
                                <div className="w-16 h-16 md:w-24 md:h-24 rounded-xl flex items-center justify-center theme-primary-bg text-black shadow-lg">
                                  <Trophy size={24} />
                                </div>
                              )}
                              
                              <span className="text-[8px] md:text-[9px] font-black uppercase tracking-wider text-theme-primary truncate w-full text-center max-w-[80px] md:max-w-[100px]">
                                {organizerName}
                              </span>
                            </div>

                            <div className="flex flex-col min-w-0 gap-1.5">
                              <h3 className="text-lg md:text-xl font-black theme-primary-text theme-font-secondary uppercase italic leading-none pt-2 md:pt-3 break-words w-full [text-wrap:balance]">
                                {tName}
                              </h3>

                              {/* Tournament Info Badges */}
                              {tournamentObj && (
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                  {tournamentObj.category && (
                                    <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border border-white/30 text-white shrink-0">
                                      {tournamentObj.category.toLowerCase() === 'libre' ? 'CAT. LIBRE' : tournamentObj.category}
                                    </span>
                                  )}
                                  {tournamentObj.gender && (
                                    <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest shrink-0 px-2 py-0.5 rounded border border-white/30 text-white">
                                      {tournamentObj.gender.toLowerCase() === 'masculino' && <Mars size={10} />}
                                      {tournamentObj.gender.toLowerCase() === 'femenino' && <Venus size={10} />}
                                      {(!['masculino', 'femenino', 'sin_restriccion', 'sin restricción', 'sin restriccion'].includes(tournamentObj.gender.toLowerCase())) && (
                                        <div className="flex -space-x-1 text-yellow-400">
                                          <Mars size={10} />
                                          <Venus size={10} />
                                        </div>
                                      )}
                                      {tournamentObj.gender.toLowerCase() === 'masculino' ? 'Caballeros' :
                                        tournamentObj.gender.toLowerCase() === 'femenino' ? 'Damas' : 
                                        (tournamentObj.gender.toLowerCase() === 'sin_restriccion' || tournamentObj.gender.toLowerCase() === 'sin restricción' || tournamentObj.gender.toLowerCase() === 'sin restriccion') ? 'Género Libre' : 'Mixto'}
                                    </div>
                                  )}
                                  {(tournamentObj.startedAt || tournamentObj.startDate) && (
                                    <div className="flex items-center gap-1.5 opacity-70 shrink-0">
                                      <CalendarDays size={12} className="theme-primary-text" />
                                      <span className="text-[10px] font-bold uppercase tracking-tight">
                                        {tournamentObj.startedAt
                                          ? `Inicio: ${new Date(tournamentObj.startedAt).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}`
                                          : `Est: ${tournamentObj.startDate}`
                                        }
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <motion.div
                            animate={{ rotate: isExpanded ? 0 : -90 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="flex items-center justify-center text-white/40 group-hover:text-white/70 transition-colors ml-2 shrink-0"
                          >
                            <ChevronDown size={20} />
                          </motion.div>
                        </div>

                        {isExpanded && (
                          <div className="pl-4 md:pl-8 space-y-4 border-l-2 border-white/5 ml-5 md:ml-10 animate-in slide-in-from-top-4 duration-300">

                            {/* Fase de Eliminación (Si existen rondas de eliminación) */}
                            {Object.keys(tData.elimination).length > 0 && (
                              (() => {
                                const elimKey = `${tName}::Elimination`;
                                const isElimExpanded = expandedBanners.includes(elimKey);
                                const isElimSelected = selectedBanner === elimKey;

                                return (
                                  <div className="space-y-4">
                                    <div
                                      className={`px-5 py-3 rounded-xl flex items-center justify-between border cursor-pointer transition-all ${isElimSelected ? 'theme-primary-border bg-white/10 shadow-[0_0_15px_rgba(var(--glow-rgb), 0.1)] backdrop-blur-md' : 'bg-white/5 border-white/10 hover:bg-white/10 opacity-90'}`}
                                      onClick={() => toggleBanner(elimKey)}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-1.5 h-6 theme-accent-bg rounded-full shadow-[0_0_8px_rgba(251,191,36,0.4)]" />
                                        <h4 className="text-sm font-black uppercase tracking-widest opacity-90 italic theme-accent-text">Fase de eliminación</h4>
                                      </div>
                                      {isElimExpanded ? <ChevronUp size={20} className="theme-primary-text" /> : <ChevronDown size={20} className="opacity-40" />}
                                    </div>

                                    {isElimExpanded && (
                                      <div className="pl-6 space-y-8 animate-in slide-in-from-top-2 duration-200">
                                        {Object.entries(tData.elimination)
                                          .sort(([a], [b]) => {
                                            const getWeight = (s: string) => {
                                              s = s.toLowerCase();
                                              if (s.includes('3er')) return 2;
                                              if (s.includes('semi')) return 3;
                                              if (s.includes('4tos') || s.includes('cuarto')) return 4;
                                              if (s.includes('8vos') || s.includes('octavo')) return 5;
                                              if (s.includes('16vos') || s.includes('dieciseisavo')) return 6;
                                              if (s.includes('final')) return 1;
                                              return 99;
                                            };
                                            return getWeight(a) - getWeight(b);
                                          })
                                          .map(([rName, rMatches]: [string, any]) => {
                                            const hasBye = rMatches.some((m: Match) => m.team1Id === 'BYE' || m.team2Id === 'BYE');
                                            const isElimination = rMatches.some((m: Match) => m.zoneIndex === undefined);
                                            const displayRoundName = rName.replace(' (V)', ' (vuelta)') + (hasBye ? (isElimination ? ' (Pase Directo)' : ' (Libre)') : '');
                                            return (
                                              <div key={rName} className="space-y-4">
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                                                  <div className="flex items-center gap-3">
                                                    <div className={`w-2 h-2 rounded-full shadow-[0_0_10px_rgba(251,191,36,0.5)] ${hasBye ? 'bg-white/30' : 'theme-accent-bg'}`} />
                                                    <span className={`text-xs md:text-sm font-black uppercase tracking-[0.2em] ${hasBye ? 'text-white/40' : 'theme-accent-text'}`}>
                                                      {displayRoundName}
                                                    </span>
                                                  </div>

                                                  <div className="h-px flex-1 bg-white/5 hidden sm:block" />
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                                  {rMatches.map((match: Match) => {
                                                    if (match.team1Id === 'BYE' || match.team2Id === 'BYE') {
                                                      return <FreeDateMatchCard key={match.id} match={match} tournament={tournaments.find((t: Tournament) => t.id === match.tournamentId)} />;
                                                    }
                                                    return <GrillaMatchCard key={match.id} match={match} roundName={rName} teams={teams} tournaments={tournaments} players={players} user={user} userRole={userRole} config={config} highlightedMatchId={highlightedMatchId} openPlayerDetails={openPlayerDetails} setSelectedMatchMode={setSelectedMatchMode} setSelectedMatchDetails={setSelectedMatchDetails} />;
                                                  })}
                                                </div>
                                              </div>
                                            );
                                          })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()
                            )}

                            {/* Zonas (Si existen) */}
                            {Object.entries(tData.zones).map(([zName, zRounds]: [string, any]) => {
                              const zoneKey = `${tName}::${zName}`;
                              const isZoneExpanded = expandedBanners.includes(zoneKey);
                              const isZoneSelected = selectedBanner === zoneKey;

                              return (
                                <div key={zName} className="space-y-4">
                                  {/* Zone Sub-banner */}
                                  <div
                                    className={`px-5 py-3 rounded-xl flex items-center justify-between border cursor-pointer transition-all ${isZoneSelected ? 'theme-primary-border bg-white/10 shadow-[0_0_15px_rgba(var(--glow-rgb), 0.1)] backdrop-blur-md' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                                    onClick={() => toggleBanner(zoneKey)}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-1.5 h-6 theme-primary-bg rounded-full" />
                                      <h4 className="text-sm font-black uppercase tracking-widest opacity-80">{zName}</h4>
                                    </div>
                                    {isZoneExpanded ? <ChevronUp size={20} className="theme-primary-text" /> : <ChevronDown size={20} className="opacity-40" />}
                                  </div>

                                  {isZoneExpanded && (
                                    <div className="pl-6 space-y-8 animate-in slide-in-from-top-2 duration-200">
                                      {Object.entries(zRounds)
                                        .sort(([a], [b]) => {
                                          const numA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
                                          const numB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
                                          return numA - numB;
                                        })
                                        .map(([rName, rMatches]: [string, any]) => {
                                          const hasBye = rMatches.some((m: Match) => m.team1Id === 'BYE' || m.team2Id === 'BYE');
                                          const isElimination = rMatches.some((m: Match) => m.zoneIndex === undefined);
                                          const displayRoundName = rName.replace(' (V)', ' (vuelta)') + (hasBye ? (isElimination ? ' (Pase Directo)' : ' (Libre)') : '');
                                          return (
                                            <div key={rName} className="space-y-4">
                                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                                                <div className="flex items-center gap-3">
                                                  <div className={`w-2 h-2 rounded-full shadow-[0_0_10px_rgba(var(--glow-rgb), 0.5)] ${hasBye ? 'bg-white/30' : 'theme-primary-bg'}`} />
                                                  <span className={`text-xs md:text-sm font-black uppercase tracking-[0.2em] ${hasBye ? 'text-white/40' : 'theme-primary-text'}`}>
                                                    {displayRoundName}
                                                  </span>
                                                </div>
                                                <div className="h-px flex-1 bg-white/5 hidden sm:block" />
                                              </div>
                                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                                {rMatches.map((match: Match) => {
                                                  if (match.team1Id === 'BYE' || match.team2Id === 'BYE') {
                                                    return <FreeDateMatchCard key={match.id} match={match} tournament={tournaments.find(t => t.id === match.tournamentId)} />;
                                                  }
                                                  return <GrillaMatchCard key={match.id} match={match} roundName={rName} teams={teams} tournaments={tournaments} players={players} user={user} userRole={userRole} config={config} highlightedMatchId={highlightedMatchId} openPlayerDetails={openPlayerDetails} setSelectedMatchMode={setSelectedMatchMode} setSelectedMatchDetails={setSelectedMatchDetails} />;
                                                })}
                                              </div>
                                            </div>
                                          );
                                        })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {/* Rondas Directas / Eliminación (Sin Zonas) */}
                            {Object.entries(tData.rounds || {})
                              .sort(([a], [b]) => {
                                const numA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
                                const numB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
                                return numA - numB;
                              })
                              .map(([rName, rMatches]: [string, any]) => {
                                const hasBye = rMatches.some((m: Match) => m.team1Id === 'BYE' || m.team2Id === 'BYE');
                                const isElimination = rMatches.some((m: Match) => m.zoneIndex === undefined);
                                const displayRoundName = rName.replace(' (V)', ' (vuelta)') + (hasBye ? (isElimination ? ' (Pase Directo)' : ' (Libre)') : '');
                                return (
                                <div key={rName} className="space-y-4">
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-2 h-2 rounded-full shadow-[0_0_10px_rgba(251,191,36,0.5)] ${hasBye ? 'bg-white/30' : 'theme-accent-bg'}`} />
                                      <span className={`text-xs md:text-sm font-black uppercase tracking-[0.2em] ${hasBye ? 'text-white/40' : 'theme-accent-text'}`}>
                                        {displayRoundName}
                                      </span>
                                    </div>
                                    <div className="h-px flex-1 bg-white/5 hidden sm:block" />
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {rMatches.map((match: Match) => {
                                      if (match.team1Id === 'BYE' || match.team2Id === 'BYE') {
                                        return <FreeDateMatchCard key={match.id} match={match} tournament={tournaments.find(t => t.id === match.tournamentId)} />;
                                      }
                                      return <GrillaMatchCard key={match.id} match={match} roundName={rName} teams={teams} tournaments={tournaments} players={players} user={user} userRole={userRole} config={config} highlightedMatchId={highlightedMatchId} openPlayerDetails={openPlayerDetails} setSelectedMatchMode={setSelectedMatchMode} setSelectedMatchDetails={setSelectedMatchDetails} />;
                                    })}
                                  </div>
                                </div>
                              )})}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
                  </div>
                )}                {torneosTab === 'activos' && (
                  <div className="flex justify-center mt-2 mb-6">
                    <div className="flex items-center bg-black/40 border border-white/5 p-1 rounded-xl">
                      <button
                        onClick={() => setActivosFilter('mis_torneos')}
                        className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                          activosFilter === 'mis_torneos' ? 'bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]' : 'text-white/40 hover:text-white/80'
                        }`}
                      >
                        Mis Torneos
                      </button>
                      <button
                        onClick={() => setActivosFilter('todos')}
                        className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                          activosFilter === 'todos' ? 'bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]' : 'text-white/40 hover:text-white/80'
                        }`}
                      >
                        Todos
                      </button>
                    </div>
                  </div>
                )}

                {torneosTab !== 'mi_fixture' && (
                  <div className="flex items-center justify-center pt-4 border-t border-white/5 w-full">
                    <div className="flex items-center gap-2 max-w-[360px] mx-auto w-full">
                      {/* Compact Search */}
                      <div className="relative flex-[3]">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 opacity-20" size={14} />
                        <input
                          type="text"
                          placeholder="Buscar torneo..."
                          value={searchTournaments}
                          onChange={(e) => setSearchTournaments(e.target.value)}
                          className="w-full bg-black/20 border border-white/5 rounded-xl py-2 pl-9 pr-3 focus:theme-primary-border outline-none transition-all placeholder:opacity-20 text-[11px] font-bold"
                        />
                      </div>

                      {/* Compact Filters */}
                      <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="bg-black/20 border border-white/5 rounded-xl px-2 py-2 text-[9px] font-black uppercase tracking-wider outline-none focus:theme-primary-border flex-[2] opacity-60 hover:opacity-100 transition-opacity"
                      >
                        <option value="">Categorías</option>
                        {Array.from(new Set(tournaments.map(t => t.category).filter(Boolean))).sort().map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {torneosTab !== 'mi_fixture' && (
                <>
                  {tournaments.length === 0 && <p className="opacity-60">Aún no hay torneos registrados en el sistema.</p>}

                  <div className="space-y-12">
                    {(() => {
                      const filtered = tournaments
                        .filter((t) => {
                          const name = (t.name || '').toLowerCase();
                          const query = searchTournaments.toLowerCase();
                          const matchesSearch = name.includes(query);

                          const org = sedesPublicas.find(s => s.uid === t.organizerId);
                          const matchesCity = globalCity === 'Todas las ciudades' || !org || org.city === globalCity;

                          const matchesCategory = !filterCategory || t.category === filterCategory;
                          const matchesOrganizer = !filterOrganizer || t.organizerId === filterOrganizer;

                          if (!matchesCategory || !matchesOrganizer || !matchesCity) return false;

                          if (torneosTab === 'inscripciones') {
                            return matchesSearch && !t.isArchived && (t.status === 'Registro' || t.status === 'Borrador' || t.status === 'Lobby' || !t.status);
                          }
                          if (torneosTab === 'activos') {
                            const isEnJuego = !t.isArchived && (t.status === 'En Juego' || t.status === 'Fase de Grupos' || t.status === 'Eliminatorias');
                            if (!isEnJuego) return false;
                            
                            if (activosFilter === 'mis_torneos') {
                              // Check if user is participant
                              const isParticipant = userProfile && (() => {
                                const tournamentTeamIds = new Set([
                                  ...(t.registeredTeamIds || []),
                                  ...(t.approvedTeamIds || []),
                                  ...(t.zones?.flatMap(z => z.teams.map(zt => zt.teamId)) || [])
                                ]);
                                return Array.from(tournamentTeamIds).some(teamId => {
                                  const team = teams.find(tm => tm.id === teamId);
                                  return team && (team.player1Id === userProfile.uid || team.player2Id === userProfile.uid || team.player3Id === userProfile.uid);
                                });
                              })();
                              if (!isParticipant) return false;
                            }
                            return matchesSearch;
                          }
                          return false;
                        })
                        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

                      if (filtered.length === 0) {
                        const emptyMessage = torneosTab === 'inscripciones' ? 'Aún no hay torneos en inscripción' : (activosFilter === 'mis_torneos' ? 'No estás en ningún torneo activo' : 'No hay torneos activos');
                        return (
                          <div className="bg-black/20 backdrop-blur-md py-8 px-6 rounded-[var(--radius-large)] text-center border border-white/10 shadow-xl flex flex-col items-center justify-center gap-6">
                            <p className="text-[10px] sm:text-xs text-white/60 font-bold uppercase tracking-wider">
                              {emptyMessage}
                            </p>
                            {torneosTab !== 'inscripciones' && (
                              <button
                                onClick={() => setTorneosTab('inscripciones')}
                                className="px-4 py-1.5 rounded-lg text-black font-black uppercase text-[10px] sm:text-xs tracking-wider shadow-[0_0_15px_rgba(250,204,21,0.3)] hover:scale-105 transition-all"
                                style={{ backgroundColor: config.primaryColor || '#EAB308' }}
                              >
                                Anotarme en un torneo
                              </button>
                            )}
                          </div>
                        );
                      }

                      return filtered.map((t, idx) => (
                        <PublicTournamentCard
                          key={t.id}
                          index={idx}
                          tournament={t}
                          config={config}
                          rankingConfig={rankingConfig}
                          allTeams={teams}
                          players={players}
                          allUsers={registeredUsers}
                          clubs={clubs}
                          sedesPublicas={sedesPublicas}
                          onPlayerClick={openPlayerDetails}
                          onJoin={handleJoinTournament}
                          onCancelJoin={handleCancelJoinTournament}
                          onOpenChat={handleOpenChatWithOrganizer}
                          handleShareCanvas={handleShareCanvas}
                          isExpanded={expandedPublicTournamentId === t.id}
                          onToggle={() => setExpandedPublicTournamentId(expandedPublicTournamentId === t.id ? null : t.id)}
                        />
                      ));
                    })()}
                  </div>
                </>
              )}
            </div>
          )}

          {/* VISTA: CLUBES */}
          {activeTab === 'clubes' && config.feature_clubs !== false && (
            <div className="animate-in fade-in duration-500 flex flex-col items-center text-center w-full">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-black uppercase tracking-widest text-white flex items-center justify-center theme-font-secondary drop-shadow-[0_0_15px_rgba(255,255,255,0.2)] mb-4">
                <MapPin className="mr-3 theme-primary-text w-5 h-5 md:w-8 md:h-8" /> Clubes y Sedes Oficiales
              </h2>
              <p className="text-sm opacity-60 mb-10 font-medium max-w-2xl">Conoce los clubes, complejos y sedes oficiales que son parte de Roca Padel Tour.</p>
              {sedesPublicas.length === 0 && <p className="opacity-60">Aún no hay sedes oficiales registradas.</p>}

              <div className="flex flex-wrap justify-center gap-6 w-full max-w-7xl">
                {sedesPublicas.filter(s => globalCity === 'Todas las ciudades' || s.city === globalCity).map((s) => (
                  <div key={s.uid} className="w-full max-w-[400px]">
                    <PublicSedeCard
                      s={s}
                      user={user}
                      setActivePrivateChatRecipientId={setActivePrivateChatRecipientId}
                      setActivePrivateChatRecipientName={setActivePrivateChatRecipientName}
                      setActivePrivateChatRecipientPhoto={setActivePrivateChatRecipientPhoto}
                      isHighlighted={highlightClubId === s.uid || highlightClubId === (s.name ? s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') : '')}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* VISTA: RANKING OFICIAL */}
          {activeTab === 'jugadores' && (
            <div className="animate-in fade-in duration-500">
              <div className="flex flex-col mb-6 gap-4 items-center text-center">
                <div className="flex flex-col items-center w-full px-0 sm:px-0">
                  <div className="flex items-center justify-center w-full sm:max-w-4xl gap-1 sm:gap-2 mb-1">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-white/40"></div>
                    <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-widest text-white flex items-center justify-center gap-3 shrink-0 drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]">
                      <Medal className="text-white w-7 h-7 sm:w-9 sm:h-9 opacity-90" />
                      Ranking Oficial
                      <button 
                        onClick={() => setShowRankingInfo(true)} 
                        className="ml-1 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center transition-all group border border-white/10 hover:border-white/30 shrink-0 shadow-lg cursor-pointer"
                        title="¿Cómo funciona el ranking?"
                      >
                        <HelpCircle size={16} className="text-white/60 group-hover:text-white transition-colors" />
                      </button>
                    </h2>
                    <div className="h-px flex-1 bg-gradient-to-l from-transparent via-white/10 to-white/40"></div>
                  </div>
                  
                </div>

                {/* Info Section: Cómo funciona el ranking (MODAL) */}
                <AnimatePresence>
                  {showRankingInfo && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-6">
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
                        onClick={() => setShowRankingInfo(false)}
                      />
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="relative w-full max-w-3xl bg-slate-800 border border-slate-600 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] z-10"
                      >
                        <div className="p-4 sm:p-5 border-b border-slate-600 flex items-center justify-between bg-slate-800/95 sticky top-0 z-20 backdrop-blur-md">
                          <h3 className="text-sm sm:text-base font-black uppercase tracking-widest text-white flex items-center gap-2">
                            <HelpCircle size={18} className="theme-primary-text" />
                            ¿Cómo funciona el Ranking?
                          </h3>
                          <button onClick={() => setShowRankingInfo(false)} className="p-1.5 hover:bg-white/10 rounded-xl transition-colors bg-white/5 border border-white/10">
                            <X size={18} className="text-white/70" />
                          </button>
                        </div>
                        
                        <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar bg-slate-800">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Card 1 */}
                            <div className="bg-slate-700 border border-slate-600 rounded-2xl p-5 flex flex-col items-center justify-center text-center gap-3 shadow-xl transition-all duration-300">
                              <div className="flex flex-col items-center justify-center gap-2 w-full">
                                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center border border-white/10">
                                  <MapPin size={20} className="theme-primary-text" />
                                </div>
                                <span className="text-[12px] sm:text-[13px] font-black uppercase tracking-widest text-white text-center leading-tight">Circuitos y Clubes</span>
                              </div>
                              <p className="text-[12px] text-white/70 leading-relaxed font-medium">
                                El ranking se divide por Sedes. Sumarás puntos para el club organizador. El <span className="theme-primary-text font-bold">Ranking General</span> es exclusivo para Torneos Master.
                              </p>
                            </div>

                            {/* Card 2 */}
                            <div className="bg-slate-700 border border-slate-600 rounded-2xl p-5 flex flex-col items-center justify-center text-center gap-3 shadow-xl transition-all duration-300">
                              <div className="flex flex-col items-center justify-center gap-2 w-full">
                                <div className="flex items-center justify-center gap-2 flex-wrap mb-1">
                                  <div className="px-2 py-1 rounded bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-[10px] font-black uppercase">+{rankingConfig.win20 || 40} pts</div>
                                  <div className="px-2 py-1 rounded bg-white/5 border border-white/10 text-white/70 text-[10px] font-black uppercase">+{rankingConfig.loss02 || 5} pts</div>
                                </div>
                                <span className="text-[12px] sm:text-[13px] font-black uppercase tracking-widest text-white text-center leading-tight">Puntos por Partido</span>
                              </div>
                              <div className="text-[12px] text-white/70 leading-relaxed font-medium flex flex-col gap-1 items-center justify-center">
                                <p>Dentro de cada torneo, cada partido suma. Obtienes puntos por <span className="text-emerald-400 font-bold">ganar</span> y también por <span className="text-white font-bold">participar</span>.</p>
                              </div>
                            </div>

                            {/* Card 3 */}
                            <div className="bg-slate-700 border border-slate-600 rounded-2xl p-5 flex flex-col items-center justify-center text-center gap-3 relative overflow-hidden group shadow-xl transition-all duration-300">
                              <div className="flex flex-col items-center justify-center gap-2 relative z-10 w-full">
                                <span className="px-2 py-1 rounded theme-primary-bg text-black text-[10px] font-black uppercase tracking-tighter">On Fire</span>
                                <span className="text-[12px] sm:text-[13px] font-black uppercase tracking-widest text-white text-center leading-tight">Racha Ganadora</span>
                              </div>
                              <p className="text-[12px] text-white/70 leading-relaxed font-medium relative z-10">
                                Gana <span className="theme-primary-text font-bold">{rankingConfig.streakThreshold || 3} partidos</span> seguidos y activas la racha. Cada victoria valdrá un <span className="text-white font-bold">bono multiplicador (x{rankingConfig.streakMultiplier || 1.5})</span>.
                              </p>
                            </div>

                            {/* Card 4 */}
                            <div className="bg-slate-700 border border-slate-600 rounded-2xl p-5 flex flex-col items-center justify-center text-center gap-3 shadow-xl transition-all duration-300">
                              <div className="flex flex-col items-center justify-center gap-2 w-full">
                                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                                  <Trophy size={20} className="text-amber-400" />
                                </div>
                                <span className="text-[12px] sm:text-[13px] font-black uppercase tracking-widest text-white text-center leading-tight">Podio en torneos</span>
                              </div>
                              <p className="text-[12px] text-white/70 leading-relaxed font-medium">
                                Al finalizar el torneo, recibes una cantidad importante de <span className="text-white font-bold">puntos fijos</span> extra si logras llegar al podio (Campeón, Finalista, etc.).
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  )}
                </AnimatePresence>

                {/* FILTROS */}
                <div className="relative w-full py-4 mb-6 flex flex-col items-center z-50">
                  {/* Full Bleed Background */}
                  <div className="absolute inset-y-0 w-[200vw] left-1/2 -translate-x-1/2 bg-black/20 backdrop-blur-md border-y border-white/5 pointer-events-none" />
                  
                  {/* Wrapper to force same horizontal extension on both rows */}
                  <div className="w-full max-w-md mx-auto flex flex-col gap-4 items-center relative z-50">
                    
                    {/* Circuit Filter (Dropdown) */}
                    <div className="flex flex-col gap-2 w-full mt-2 relative z-50">
                      {(() => {
                        // Options: Global + all clubs in the selected city + organizers
                        const availableClubs = sedesPublicas.filter(s => globalCity === 'Todas las ciudades' || s.city === globalCity);
                        const availableOrganizers = players.filter(p => p.isOrganizer && (globalCity === 'Todas las ciudades' || p.city === globalCity));

                        const combinedOptions = [
                          ...availableClubs.map(s => ({
                            id: s.uid,
                            name: s.name,
                            photoUrl: s.photoUrl || null,
                            type: 'club'
                          })),
                          ...availableOrganizers.map(o => ({
                            id: o.id,
                            name: o.organizerProfile?.name || o.name || 'Organizador',
                            photoUrl: o.organizerProfile?.logoUrl || o.photoUrl || null,
                            type: 'organizer'
                          }))
                        ];

                        // Eliminar posibles duplicados
                        const uniqueOptionsMap = new Map();
                        combinedOptions.forEach(opt => {
                          if (!uniqueOptionsMap.has(opt.id)) {
                            uniqueOptionsMap.set(opt.id, opt);
                          }
                        });

                        const sortedUniqueOptions = Array.from(uniqueOptionsMap.values()).sort((a, b) => a.name.localeCompare(b.name));

                        const options = [
                          { id: 'global', name: 'Ranking General', photoUrl: null, type: 'global' },
                          ...sortedUniqueOptions
                        ];

                        const selectedOption = options.find(o => o.id === filterCircuit) || options[0];

                        return (
                          <div className="relative w-full max-w-[280px] mx-auto">
                            <label className="text-[9px] font-black text-white/50 uppercase tracking-widest block mb-1.5 text-center">
                              Circuito Seleccionado
                            </label>
                            <button
                              onClick={() => setIsCircuitDropdownOpen(!isCircuitDropdownOpen)}
                              className="w-full flex items-center justify-between px-4 py-2.5 bg-black/40 border border-white/10 rounded-2xl hover:bg-black/60 transition-all focus:outline-none"
                              style={filterCircuit !== 'global' ? { borderColor: config.primaryColor, boxShadow: `0 0 10px ${config.primaryColor}20` } : {}}
                            >
                              <div className="flex items-center gap-3">
                                {selectedOption.type === 'global' ? (
                                  <img src={config.logoUrl || '/pwa-192x192.png'} alt="Global" className="w-7 h-7 rounded-full object-contain shrink-0 p-0.5 bg-black/20" />
                                ) : selectedOption.photoUrl ? (
                                  <img src={selectedOption.photoUrl} alt={selectedOption.name} className="w-7 h-7 rounded-full object-cover border border-white/10" />
                                ) : (
                                  <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white/50">
                                    <MapPin size={12} />
                                  </div>
                                )}
                                <span className={`text-xs font-black uppercase tracking-wider truncate text-left ${filterCircuit !== 'global' ? 'theme-primary-text' : 'text-white'}`}>
                                  {selectedOption.name}
                                </span>
                              </div>
                              <ChevronDown size={16} className={`text-white/50 transition-transform duration-300 ${isCircuitDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown Options */}
                            <AnimatePresence>
                              {isCircuitDropdownOpen && (
                                <motion.div
                                  initial={{ opacity: 0, y: -10, scaleY: 0.95 }}
                                  animate={{ opacity: 1, y: 0, scaleY: 1 }}
                                  exit={{ opacity: 0, y: -10, scaleY: 0.95 }}
                                  transition={{ duration: 0.15, ease: "easeOut" }}
                                  className="absolute top-[100%] left-0 w-full mt-2 bg-slate-800/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-[100] max-h-[300px] overflow-y-auto custom-scrollbar"
                                >
                                  {options.map((option) => (
                                    <button
                                      key={option.id}
                                      onClick={() => {
                                        setFilterCircuit(option.id);
                                        setIsCircuitDropdownOpen(false);
                                      }}
                                      className={`w-full flex items-center gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/10 transition-colors last:border-0 ${filterCircuit === option.id ? 'bg-[var(--primary-color)]/10' : ''}`}
                                    >
                                      {option.type === 'global' ? (
                                        <img src={config.logoUrl || '/pwa-192x192.png'} alt="Global" className="w-8 h-8 rounded-full object-contain shrink-0 p-0.5 bg-black/20" />
                                      ) : option.photoUrl ? (
                                        <img src={option.photoUrl} alt={option.name} className="w-8 h-8 rounded-full object-cover border border-white/10 shadow-sm shrink-0" />
                                      ) : (
                                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/50 shrink-0">
                                          <MapPin size={14} />
                                        </div>
                                      )}
                                      <span className={`text-xs font-black uppercase tracking-wider text-left truncate flex-1 ${filterCircuit === option.id ? 'theme-primary-text' : 'text-white/80'}`}>
                                        {option.name}
                                      </span>
                                    </button>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Gender Filter */}
                    <div className="flex flex-col gap-2 items-center w-full mt-2">
                      <div className="flex w-full gap-1.5">
                        {[
                          { value: 'Todas', label: 'Todos' },
                          { value: 'Masculino', label: 'Masculino' },
                          { value: 'Femenino', label: 'Femenino' }
                        ].map((gender) => {
                          const isActive = filterPlayersGender === gender.value;
                          return (
                            <button
                              key={gender.value}
                              onClick={() => setFilterPlayersGender(gender.value)}
                              className={`flex-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider py-1.5 rounded-lg border transition-all active:scale-95 cursor-pointer select-none ${
                                isActive
                                  ? 'bg-transparent text-white border-white opacity-100 shadow-sm'
                                  : 'bg-white/5 text-zinc-400 border-white/5 hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              {gender.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Category Filter */}
                    <div className="flex flex-col gap-2 items-center w-full">
                      <div className="flex flex-wrap w-full gap-0.5 sm:gap-1.5 justify-center sm:justify-between">
                        {[
                          'Todas',
                          ...Array.from(new Set(players.filter(p => !p.isGuest).map(p => p.category))).sort()
                        ].map((category) => {
                          const isActive = filterPlayersCategory === category;
                          const label = category === 'Todas' ? 'General' : category;
                          return (
                            <button
                              key={category}
                              onClick={() => setFilterPlayersCategory(category)}
                              className={`flex-1 min-w-[40px] text-[8px] sm:text-[10px] font-bold uppercase tracking-wider px-0 py-1 sm:px-3 sm:py-1.5 rounded-md sm:rounded-lg border transition-all active:scale-95 cursor-pointer select-none text-center ${
                                isActive
                                  ? 'bg-transparent text-white border-white opacity-100 shadow-sm'
                                  : 'bg-white/5 text-zinc-400 border-white/5 hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Search Bar */}
                    <div className="relative w-full mt-2">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 opacity-30" size={16} />
                      <input
                        type="text"
                        placeholder="Buscar jugador..."
                        value={searchPlayers}
                        onChange={(e) => setSearchPlayers(e.target.value)}
                        className="w-full bg-black/40 border border-white/10 rounded-2xl py-2 pl-10 pr-4 focus:border-[var(--primary-color)] outline-none transition-all placeholder:opacity-30 text-xs font-bold text-center text-white"
                      />
                    </div>

                  </div>
                </div>

                {/* Ver mi posición actual */}
                {userProfile && userProfile.role === 'user' && (
                  <div className="flex justify-center w-full mb-6 -mt-4 relative z-20">
                    <button
                      onClick={handleGoToMyPosition}
                      className="group/pos flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-theme-primary/10 via-theme-primary/5 to-transparent hover:from-theme-primary/20 hover:via-theme-primary/15 border border-theme-primary-border/20 hover:border-theme-primary-border/50 text-white/70 hover:text-white rounded-lg transition-all duration-300 font-black uppercase text-[8px] tracking-[0.15em] relative overflow-hidden active:scale-95 shadow-sm w-1/2 sm:w-auto"
                    >
                      <div className="absolute -inset-full bg-gradient-to-r from-transparent via-white/5 to-transparent skew-x-[-20deg] group-hover/pos:animate-shimmer" />
                      <Target size={11} className="theme-primary-text group-hover/pos:scale-110 transition-transform duration-300" />
                      <span>Ver mi posición actual</span>
                    </button>
                  </div>
                )}
              </div>

              {(() => {
                const hasSearch = searchPlayers.trim() !== '';
                const filteredPlayers = players
                  .filter((p) => !p.isGuest)
                  .filter((p) => globalCity === 'Todas las ciudades' || p.city === globalCity)
                  .filter((p) => {
                    if (!hasSearch) return true;
                    const searchTerm = searchPlayers.toLowerCase();
                    return p.name.toLowerCase().includes(searchTerm) ||
                      (p.nickname && p.nickname.toLowerCase().includes(searchTerm));
                  })
                  .filter((p) => {
                    return filterPlayersGender === 'Todas' || p.gender === filterPlayersGender;
                  })
                  .filter((p) => {
                    return filterPlayersCategory === 'Todas' || p.category === filterPlayersCategory;
                  });

                const getPageNumbers = (current: number, total: number) => {
                  const pages: (number | string)[] = [];
                  if (total <= 5) {
                    for (let i = 1; i <= total; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    let start = Math.max(2, current - 1);
                    let end = Math.min(total - 1, current + 1);
                    if (current <= 3) {
                      end = 4;
                    } else if (current >= total - 2) {
                      start = total - 3;
                    }
                    if (start > 2) {
                      pages.push('...');
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }
                    if (end < total - 1) {
                      pages.push('...');
                    }
                    pages.push(total);
                  }
                  return pages;
                };

                // Precompute the unfiltered, correct positions/ranks
                const getPlayerPoints = (p: Player) => {
                  if (filterCircuit === 'global') {
                    return p.rankingPoints?.global ?? (p.points || 0);
                  }
                  
                  // Check if the circuit corresponds to an organizer or a club
                  const org = players.find(o => o.id === filterCircuit && o.isOrganizer);
                  const club = sedesPublicas.find(c => c.uid === filterCircuit);
                  const emailToCheck = org?.email || club?.email;
                  
                  const pointsByUid = p.rankingPoints?.clubs?.[filterCircuit] || 0;
                  const pointsByEmail = (emailToCheck && p.rankingPoints?.clubs?.[emailToCheck]) || 0;
                  
                  // Aggregate legacy points if this is the main organizer
                  const isMainOrganizer = emailToCheck === 'agunavarro1989@gmail.com' || filterCircuit === 'main_club' || filterCircuit === 'admin';
                  
                  const legacyPoints = isMainOrganizer 
                    ? (p.rankingPoints?.clubs?.['main_club'] || 0) + 
                      (p.rankingPoints?.clubs?.['admin'] || 0) + 
                      (p.rankingPoints?.clubs?.['legacy_organizer'] || 0) +
                      (p.rankingPoints?.clubs?.['agunavarro1989@gmail.com'] || 0)
                    : 0;
                  
                  return pointsByUid + pointsByEmail + legacyPoints;
                };

                const allNonGuests = players.filter((p) => {
                  if (p.isGuest) return false;
                  if (globalCity !== 'Todas las ciudades' && p.city !== globalCity) return false;
                  if (filterCircuit !== 'global' && getPlayerPoints(p) <= 0) return false;
                  return true;
                });

                // Sorted list of all players for general ranking (optionally filtered by gender if selected)
                const generalPlayersUnfiltered = [...allNonGuests]
                  .filter((p) => filterPlayersGender === 'Todas' || p.gender === filterPlayersGender)
                  .sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));

                // Groups of category+gender from all players (unfiltered) sorted by points
                const categoryGenderGroupsUnfiltered: Record<string, Player[]> = {};
                allNonGuests.forEach((p) => {
                  const key = `${p.category} ${p.gender}`;
                  if (!categoryGenderGroupsUnfiltered[key]) categoryGenderGroupsUnfiltered[key] = [];
                  categoryGenderGroupsUnfiltered[key].push(p);
                });
                Object.keys(categoryGenderGroupsUnfiltered).forEach((key) => {
                  categoryGenderGroupsUnfiltered[key].sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));
                });

                if (filteredPlayers.length === 0) {
                  return (
                    <div className="py-12 flex flex-col items-center justify-center opacity-60 bg-white/5 rounded-[var(--radius-large)] border border-white/10">
                      <Users size={48} className="mb-4 opacity-50" />
                      <p className="text-lg font-bold">No se encontraron jugadores</p>
                      <p className="text-sm">Prueba ajustando los filtros de búsqueda.</p>
                    </div>
                  );
                }

                let sortedGroupKeys: string[] = [];
                const groups: Record<string, Player[]> = {};

                if (filterPlayersCategory === 'Todas') {
                  const key = `General ${filterPlayersGender === 'Todas' ? '' : filterPlayersGender}`.trim();
                  groups[key] = filteredPlayers.sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));
                  sortedGroupKeys = [key];
                } else {
                  // Group by Category + Gender
                  filteredPlayers.forEach(p => {
                    const key = `${p.category} ${p.gender}`;
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(p);
                  });

                  // Sort groups by category weight, then gender
                  const getCatWeight = (cat: string) => {
                    const c = cat.toLowerCase();
                    if (c.includes('1ra') || c.includes('primera')) return 1;
                    if (c.includes('2da') || c.includes('segunda')) return 2;
                    if (c.includes('3ra') || c.includes('tercera')) return 3;
                    if (c.includes('4ta') || c.includes('cuarta')) return 4;
                    if (c.includes('5ta') || c.includes('quinta')) return 5;
                    if (c.includes('6ta') || c.includes('sexta')) return 6;
                    if (c.includes('7ma') || c.includes('septima') || c.includes('séptima')) return 7;
                    return 8;
                  };

                  sortedGroupKeys = Object.keys(groups).sort((a, b) => {
                    const [catA, genderA] = a.split(' ');
                    const [catB, genderB] = b.split(' ');
                    const wA = getCatWeight(catA);
                    const wB = getCatWeight(catB);
                    if (wA !== wB) return wA - wB;
                    return (genderA || '').localeCompare(genderB || '');
                  });
                }

                return (
                  <div className="space-y-12 max-w-4xl mx-auto w-full">
                    {sortedGroupKeys.map(groupKey => {
                      // Sort players within group by points descending
                      const groupPlayers = groups[groupKey].sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));
                      const [cat, gender] = groupKey.split(' ');
                      const styles = getCategoryStyles(cat);

                      // Pagination logic
                      const itemsPerPage = 10;
                      const rawCurrentPage = rankingPages[groupKey] || 1;
                      const totalPages = Math.ceil(groupPlayers.length / itemsPerPage);
                      const currentPage = totalPages > 0 ? Math.min(rawCurrentPage, totalPages) : 1;
                      const startIndex = (currentPage - 1) * itemsPerPage;
                      const visiblePlayers = groupPlayers.slice(startIndex, startIndex + itemsPerPage);

                      const handlePrevPage = () => {
                        if (currentPage > 1) {
                          setRankingPages(prev => ({ ...prev, [groupKey]: currentPage - 1 }));
                        }
                      };

                      const handleNextPage = () => {
                        if (currentPage < totalPages) {
                          setRankingPages(prev => ({ ...prev, [groupKey]: currentPage + 1 }));
                        }
                      };

                      return (
                        <div key={groupKey} className="space-y-4">
                          {/* Group Header */}
                          <div className="flex justify-center mb-1.5">
                            <div className="flex items-center rounded-xl overflow-hidden border border-white/10 bg-black/40 shadow-lg scale-90 sm:scale-100">
                              <div className={`px-4 py-1 font-black uppercase text-[10px] sm:text-xs tracking-widest ${styles.badgeBg} ${styles.badge}`}>
                                {(() => {
                                  if (filterCircuit === 'global') return 'Ranking General';
                                  const club = sedesPublicas.find(s => s.uid === filterCircuit);
                                  const playerOrg = players.find(p => p.id === filterCircuit);
                                  return club?.name || playerOrg?.organizerProfile?.name || playerOrg?.name || 'Sede Local';
                                })()}
                              </div>
                              {(cat !== 'General' || gender) && (
                                <div className="px-3 py-1 text-[8px] sm:text-[10px] font-black italic uppercase tracking-widest text-white/60 bg-black/20 flex gap-1">
                                  {cat !== 'General' && <span>{cat}</span>}
                                  {gender && <span>{gender}</span>}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* List Container */}
                          <div className="relative bg-black/40 rounded-[1.5rem] overflow-hidden flex flex-col border border-white/10 shadow-2xl">
                            {/* Category Overlay Background */}
                            <div className={`absolute inset-0 bg-gradient-to-b ${styles.overlay} pointer-events-none opacity-35`} />
                            {/* Table Header */}
                            <div className="relative z-10 flex items-center px-4 md:px-6 py-2 bg-white/5 border-b border-white/5 text-[8px] font-black uppercase tracking-widest text-white/40 select-none">
                              {/* Position */}
                              <div className="w-6 md:w-8 text-center shrink-0">Pos</div>

                              {/* Divider 1 */}
                              <div className="w-[1px] h-3 bg-white/10 mx-1.5 shrink-0" />

                              {/* Player Info */}
                              <div className="flex-1 px-1 md:px-2 text-center">Jugador</div>

                              {/* Divider 2 */}
                              <div className="w-[1px] h-3 bg-white/10 mx-1.5 shrink-0" />

                              {/* Points */}
                              <div className="w-10 md:w-12 text-center shrink-0">Puntos</div>
                            </div>

                            {/* Table Rows */}
                            <div className="relative z-10 flex flex-col">
                              {visiblePlayers.map((p, idx) => {
                                let globalIdx = -1;
                                if (groupKey.startsWith('General')) {
                                  const groupGender = groupKey.includes('Masculino') ? 'Masculino' : groupKey.includes('Femenino') ? 'Femenino' : 'Todas';
                                  const targetGeneralList = [...allNonGuests]
                                    .filter((x) => groupGender === 'Todas' || x.gender === groupGender)
                                    .sort((a, b) => getPlayerPoints(b) - getPlayerPoints(a));
                                  globalIdx = targetGeneralList.findIndex((x) => x.id === p.id);
                                } else {
                                  const playerGroupKey = `${p.category} ${p.gender}`;
                                  const groupList = categoryGenderGroupsUnfiltered[playerGroupKey];
                                  if (groupList) {
                                    globalIdx = groupList.findIndex((x) => x.id === p.id);
                                  }
                                }
                                if (globalIdx === -1) {
                                  globalIdx = startIndex + idx;
                                }
                                const stylesPlayer = getCategoryStyles(p.category);
                                const prizeId = `${groupKey}_${globalIdx + 1}`.replace(/\s+/g, '_');
                                const targetPrize = prizes.find(pr => pr.id === prizeId);

                                return (
                                  <RankingRow
                                    key={p.id}
                                    player={p}
                                    idx={idx}
                                    globalIdx={globalIdx}
                                    highlightedPlayerId={highlightedPlayerId}
                                    config={config}
                                    targetPrize={targetPrize}
                                    userRole={userRole}
                                    groupKey={groupKey}
                                    onPlayerClick={openPlayerDetails}
                                    onPrizeClick={handlePrizeClick}
                                    pointsToDisplay={getPlayerPoints(p)}
                                  />
                                )
                              })}
                            </div>

                            {/* Pagination Footer */}
                            {totalPages > 1 && (
                              <div className="flex items-center justify-center py-3 bg-black/40 border-t border-white/5 px-4 md:px-6">
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={handlePrevPage}
                                    disabled={currentPage === 1}
                                    className="p-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:pointer-events-none transition-colors mr-1"
                                  >
                                    <ChevronLeft size={14} />
                                  </button>
                                  <div className="flex gap-1 items-center">
                                    {getPageNumbers(currentPage, totalPages).map((p, idx) => {
                                      if (p === '...') {
                                        return (
                                          <span
                                            key={`ellipsis-${idx}`}
                                            className="w-5 h-5 flex items-center justify-center text-[9px] font-black text-white/30 select-none"
                                          >
                                            ...
                                          </span>
                                        );
                                      }
                                      return (
                                        <button
                                          key={`page-${p}`}
                                          onClick={() => setRankingPages(prev => ({ ...prev, [groupKey]: p as number }))}
                                          className={`w-5 h-5 rounded flex items-center justify-center text-[9px] font-black transition-colors ${currentPage === p
                                            ? 'bg-white text-black'
                                            : 'bg-white/5 text-white/50 hover:bg-white/10'
                                            }`}
                                        >
                                          {p}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <button
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                    className="p-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:pointer-events-none transition-colors ml-1"
                                  >
                                    <ChevronRight size={14} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* VISTA: SPONSORS */}
          {activeTab === 'sponsors' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl mx-auto py-8">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="flex flex-col items-center justify-center mb-10 gap-2 text-center"
              >
                  <motion.h2 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-2xl md:text-4xl font-black uppercase tracking-[0.2em] text-white flex flex-col md:flex-row items-center theme-font-secondary drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]"
                  >
                    <Star className="mb-2 md:mb-0 md:mr-3 theme-primary-text w-10 h-10 md:w-12 md:h-12" /> Nuestros Sponsors
                  </motion.h2>
                  
                  <motion.button
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    onClick={() => {
                      const p = players.find(x => x.id === user?.uid);
                      if (p) setSupportChatPlayer(p);
                    }}
                    className="mt-1 px-4 py-1.5 rounded-full border border-white/20 hover:border-white/50 text-white/70 hover:text-white transition-all bg-transparent flex flex-col items-center group cursor-pointer"
                  >
                    <span className="text-[8px] uppercase tracking-[0.2em] opacity-80 leading-tight">¿Quieres promocionar tu marca?</span>
                    <span className="text-xs font-black uppercase tracking-widest group-hover:theme-primary-text transition-colors leading-tight">Contáctanos</span>
                  </motion.button>
              </motion.div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-8">
                {clubs.filter(c => c.type === 'sponsor').map((sponsor, idx) => (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.2, delay: idx * 0.15, ease: "easeOut" }}
                    key={sponsor.id} 
                    className="flex flex-col items-center text-center group"
                  >
                    <div className="relative w-28 h-28 md:w-36 md:h-36 rounded-3xl bg-black/20 border border-white/5 mb-3 overflow-hidden flex items-center justify-center p-4 group-hover:scale-105 transition-transform duration-500 hover:border-white/20 hover:shadow-[0_0_30px_rgba(255,255,255,0.05)]">
                      {/* Metallic sweep effect */}
                      <motion.div 
                        className="absolute inset-0 w-[200%] bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-25deg] z-10 pointer-events-none"
                        initial={{ x: '-150%' }}
                        whileInView={{ x: '150%' }}
                        viewport={{ once: false, amount: 0.1 }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.5 }}
                      />
                      
                      {sponsor.photoUrl ? (
                        <FadeInImage src={sponsor.photoUrl} alt={sponsor.name} className="w-full h-full object-contain relative z-0" noPlaceholderBg={true} />
                      ) : (
                        <Star className="w-10 h-10 opacity-30" />
                      )}
                    </div>
                    <h3 className="text-sm md:text-base font-black text-white tracking-widest uppercase mb-1 group-hover:theme-primary-text transition-colors">{sponsor.name}</h3>
                    {(sponsor as any).description && (
                      <p className="text-[10px] text-white/50 mb-2 line-clamp-2 leading-relaxed">{(sponsor as any).description}</p>
                    )}
                    
                    <div className="mt-1 flex items-center gap-2 w-full justify-center">
                      {(sponsor as any).instagram && (
                        <a href={(sponsor as any).instagram.startsWith('http') ? (sponsor as any).instagram : `https://instagram.com/${(sponsor as any).instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:theme-primary-border hover:theme-primary-text transition-all">
                          <Instagram size={14} />
                        </a>
                      )}
                      {(sponsor as any).whatsapp && (
                        <a href={`https://wa.me/${(sponsor as any).whatsapp.replace(/\+/g, '').replace(/\s/g, '')}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:text-green-500 hover:border-green-500/50 transition-all">
                          <MessageCircle size={14} />
                        </a>
                      )}
                      {(sponsor as any).facebook && (
                        <a href={(sponsor as any).facebook.startsWith('http') ? (sponsor as any).facebook : `https://facebook.com/${(sponsor as any).facebook}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:theme-primary-border hover:theme-primary-text transition-all">
                          <Globe size={14} />
                        </a>
                      )}
                      {sponsor.link && (
                        <a href={sponsor.link.startsWith('http') ? sponsor.link : `https://${sponsor.link}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:theme-primary-border hover:theme-primary-text transition-all">
                          <Globe size={14} />
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {clubs.filter(c => c.type === 'sponsor').length === 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="text-center py-20 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-sm mt-8"
                >
                  <Star className="w-20 h-20 mx-auto opacity-20 mb-6" />
                  <p className="text-white/50 text-xl font-medium">Aún no hay patrocinadores oficiales registrados.</p>
                </motion.div>
              )}
            </div>
          )}

          {/* VISTA: EXTRAS / HERRAMIENTAS */}
          {activeTab === 'herramientas' && (
            <ToolsDashboard userProfile={userProfile} config={config} onRequireLogin={() => handleOpenLoginModal('login')} />
          )}

          {/* VISTA: HISTORIAL (New Tab) */}
          {activeTab === 'historial' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex flex-col items-center justify-center mb-8 gap-4 text-center">
                  <div className="flex flex-col items-center w-full px-0 sm:px-0">
                    <div className="flex items-center justify-center w-full sm:max-w-4xl gap-1 sm:gap-2">
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-white/40"></div>
                      <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-widest text-white flex items-center justify-center gap-3 shrink-0 drop-shadow-[0_0_15px_rgba(255,255,255,0.2)] mb-2">
                        <History className="text-white w-7 h-7 sm:w-9 sm:h-9 opacity-90" />
                        Historial general
                      </h2>
                      <div className="h-px flex-1 bg-gradient-to-l from-transparent via-white/10 to-white/40"></div>
                    </div>
                  </div>
                  <div className="flex bg-black/40 p-1 rounded-xl border border-white/10 mt-2 shrink-0">
                    <button
                      onClick={() => setHistorialTab('mis_partidos_oficiales')}
                      className={`px-4 sm:px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${historialTab === 'mis_partidos_oficiales' ? 'theme-primary-bg text-black shadow-lg scale-105' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}
                    >
                      Mi Historial
                    </button>
                    <button
                      onClick={() => setHistorialTab('todos_partidos_oficiales')}
                      className={`px-4 sm:px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${historialTab === 'todos_partidos_oficiales' ? 'theme-primary-bg text-black shadow-lg scale-105' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}
                    >
                      Partidos Oficiales
                    </button>
                    <button
                      onClick={() => setHistorialTab('torneos')}
                      className={`px-4 sm:px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${historialTab === 'torneos' ? 'theme-primary-bg text-black shadow-lg scale-105' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}
                    >
                      Torneos
                    </button>
                  </div>
              </div>

              {(historialTab === 'mis_partidos_oficiales' || historialTab === 'todos_partidos_oficiales') && user && (
                <UnifiedHistory
                  players={players}
                  currentUserProfile={user}
                  activeTab={historialTab}
                  onPlayerClick={openPlayerDetails}
                  onMatchClick={(m) => {
                    if (m.source === 'tournament') {
                      setSelectedMatchDetails(m.originalMatch as Match);
                    } else {
                      // Do nothing or dispatch public match open
                      window.dispatchEvent(new CustomEvent('openPublicMatch', { detail: { matchId: m.id.replace('m_', '') } }));
                    }
                  }}
                  globalCity={globalCity}
                  sedesPublicas={sedesPublicas}
                />
              )}

              {historialTab === 'torneos' && (
                <div className="space-y-12">
                  {(() => {
                    const filtered = tournaments
                      .filter((t) => {
                        const name = (t.name || '').toLowerCase();
                        const query = searchHistory.toLowerCase();
                        const matchesSearch = name.includes(query);
                        
                        const org = sedesPublicas.find(s => s.uid === t.organizerId);
                        const matchesCity = globalCity === 'Todas las ciudades' || !org || org.city === globalCity;

                        return matchesSearch && matchesCity && (t.isArchived || t.status === 'Finalizado');
                      })
                      .sort((a, b) => (sortHistoryDesc ? (b.createdAt || 0) - (a.createdAt || 0) : (a.createdAt || 0) - (b.createdAt || 0)));

                    if (filtered.length === 0 && tournaments.length > 0) {
                      return (
                        <div className="text-center p-12 bg-black/20 rounded-[var(--radius-large)] border border-white/5">
                          <p className="opacity-60 text-lg">No hay torneos en el historial en este momento.</p>
                        </div>
                      );
                    }

                    return filtered.map((t, idx) => (
                      <PublicTournamentCard
                        key={t.id}
                        index={idx}
                        tournament={t}
                        config={config}
                        rankingConfig={rankingConfig}
                        allTeams={teams}
                        players={players}
                        allUsers={registeredUsers}
                        clubs={clubs}
                        sedesPublicas={sedesPublicas}
                        onPlayerClick={openPlayerDetails}
                        onJoin={handleJoinTournament}
                        onCancelJoin={handleCancelJoinTournament}
                        onOpenChat={handleOpenChatWithOrganizer}
                        handleShareCanvas={handleShareCanvas}
                        isExpanded={expandedPublicTournamentId === t.id}
                        onToggle={() => setExpandedPublicTournamentId(expandedPublicTournamentId === t.id ? null : t.id)}
                      />
                    ));
                  })()}
                </div>
              )}
            </div>
          )}

          {/* VISTA: MI PERFIL */}
          {activeTab === 'perfil' && user && userProfile && (
            <MyProfile
              user={userProfile}
              player={players.find(p => p.id === user.uid) || null}
              players={players}
              config={config}
              notifications={filteredNotifications}
              onMatchClick={(m) => setSelectedMatchDetails(m)}
              onPlayerClick={openPlayerDetails}
              onEditProfile={() => setIsEditingProfile(true)}
              onOpenSupportChat={(p) => {
                const targetPlayer = p || players.find(x => x.id === user.uid);
                if (targetPlayer) {
                  setSupportChatPlayer(targetPlayer);
                } else {
                  alert('No se pudo encontrar tu perfil de jugador en este momento.');
                }
              }}
              highlightValidation={shouldHighlightValidation}
              onTabChange={setActiveTab}
            />
          )}

          {/* VISTA: ADMIN */}
          {activeTab === 'admin' && (
            <div className="animate-in fade-in duration-500 relative z-20">
              {!isAdminOrSuper ? null : isSuspendedActive && userRole !== 'superadmin' ? (
                <div className="max-w-md mx-auto my-12 text-center p-8 bg-slate-900/80 backdrop-blur-xl border border-red-500/20 rounded-[2rem] shadow-[0_0_50px_rgba(239,68,68,0.15)] relative overflow-hidden animate-in zoom-in-95 duration-300">
                  <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-red-500/15 blur-[80px] opacity-20 pointer-events-none" />

                  <div className="mx-auto w-20 h-20 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-6">
                    <span className="text-4xl">🚫</span>
                  </div>

                  <h3 className="text-2xl font-black uppercase tracking-wider text-white mb-3">Acceso Restringido</h3>
                  <p className="text-white/70 text-sm leading-relaxed mb-6">
                    La cuenta de este club ha sido temporalmente inhabilitada por la administración central. No tienes autorización para gestionar torneos, zonas o resultados en este momento.
                  </p>

                  {userProfile?.suspensionReason && (
                    <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-4 mb-6 text-left">
                      <span className="text-[10px] font-black uppercase tracking-wider text-red-400 block mb-1">Motivo:</span>
                      <p className="text-xs text-white/60 font-semibold">{userProfile.suspensionReason}</p>
                    </div>
                  )}

                  <div className="text-xs font-bold text-white/40">
                    Si crees que esto es un error, por favor ponte en contacto con el soporte del circuito.
                  </div>
                </div>
              ) : isOrganizerModeActive && userProfile?.isOrganizer ? (
                <OrganizerDashboard
                  userId={userProfile.uid}
                  userProfile={userProfile}
                  players={players}
                  teams={teams}
                  matches={matches}
                  tournaments={tournaments}
                  sedesPublicas={sedesPublicas}
                  allUsers={registeredUsers}
                  config={config}
                  rankingConfig={rankingConfig}
                  db={db}
                  getCollectionPath={getCollectionPath}
                />
              ) : (
                <PosSessionProvider>
                  <AdminDashboard
                    userRole={userRole}
                    userId={(userProfile as any)?.clubId || user?.uid || ''}
                    players={players}
                    teams={teams}
                    matches={matches}
                    tournaments={tournaments}
                    news={news}
                    clubs={clubs}
                    sedesPublicas={sedesPublicas}
                    allUsers={registeredUsers}
                    config={config}
                    rankingConfig={rankingConfig}
                    db={db}
                    getCollectionPath={getCollectionPath}
                    allTeams={teams}
                    handleShareCanvas={handleShareCanvas}
                    onPlayerClick={openPlayerDetails}
                    onMatchClick={(m) => setSelectedMatchDetails(m)}
                    onOpenSupportChat={(p) => {
                      setSupportChatPlayer(p);
                    }}
                    onOpenChatWithPlayer={(playerId, playerName, playerPhoto) => {
                      setActivePrivateChatRecipientId(playerId);
                      setActivePrivateChatRecipientName(playerName);
                      setActivePrivateChatRecipientPhoto(playerPhoto);
                      setActivePrivateChatIsSupport(false);
                      setShowPrivateMessages(true);
                    }}
                    adminSection={adminSection}
                    setAdminSection={setAdminSection}
                    highlightBookingRequestId={highlightBookingRequestId}
                    onHighlightBookingRequestHandled={() => setHighlightBookingRequestId(null)}
                  />
                </PosSessionProvider>
              )}
            </div>
          )}
        </main>
      </div>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSuccess={handleLoginSuccess}
        initialView={loginModalInitialView}
        config={config}
      />

      {needsProfileSetup && user && (
        <AnimatePresence>
          <ProfileSetupModal
            user={{ uid: user.uid, email: user.email, displayName: user.displayName }}
            onComplete={(newProfile) => {
              setUserProfile(newProfile);
              if (activeTab === 'inicio' || activeTab === 'perfil') {
                setActiveTab('social');
              }
              localStorage.setItem('pwa_registration_timestamp', Date.now().toString());
              setWelcomeMessage({ type: 'new', name: newProfile.name.split(' ')[0] });
              setTimeout(() => setWelcomeMessage(null), 3500);
            }}
            logoUrl={config.logoUrl}
            initialPlayer={currentPlayerDoc}
            bgUrl={config.bgUrl}
            bgBlur={config.bgBlur}
          />
        </AnimatePresence>
      )}

      {showVerificationBlock && user && (
        <EmailVerificationBlocking
          user={user}
          onVerified={() => setShowVerificationBlock(false)}
        />
      )}

      {sessionConflictError && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4 bg-black/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-[#0f172a]/95 backdrop-blur-xl rounded-[2rem] border border-yellow-400/30 w-full max-w-md shadow-[0_0_50px_rgba(250,204,21,0.2)] relative overflow-hidden p-6 text-center animate-in zoom-in-95 duration-200">
            {/* Yellow glow effect */}
            <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-yellow-400/20 blur-[80px] opacity-20 pointer-events-none" />

            <div className="mx-auto w-16 h-16 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center mb-6">
              <AlertTriangle className="w-8 h-8 text-yellow-400 animate-pulse" />
            </div>

            <h3 className="text-xl font-bold text-white mb-3">Sesión Concurrente Detectada</h3>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              {sessionConflictError}
            </p>

            <button
              onClick={() => setSessionConflictError(null)}
              className="w-full py-3 px-4 rounded-xl bg-yellow-400 hover:bg-yellow-500 active:scale-95 transition-all text-black font-semibold text-sm shadow-[0_0_20px_rgba(250,204,21,0.3)] cursor-pointer"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {pendingTakeover && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4 bg-black/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-[#0f172a]/95 backdrop-blur-xl rounded-[2rem] border border-yellow-400/30 w-full max-w-md shadow-[0_0_50px_rgba(250,204,21,0.2)] relative overflow-hidden p-6 text-center animate-in zoom-in-95 duration-200">
            {/* Yellow glow effect */}
            <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-yellow-400/20 blur-[80px] opacity-20 pointer-events-none" />

            <div className="mx-auto w-16 h-16 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center mb-6">
              <AlertTriangle className="w-8 h-8 text-yellow-400 animate-pulse" />
            </div>

            <h3 className="text-xl font-bold text-white mb-3 uppercase tracking-wide">Sesión Activa</h3>
            <p className="text-white/70 text-sm leading-relaxed mb-8">
              Hay otra sesión abierta en otro dispositivo. ¿Quieres abrir la sesión en este dispositivo? Se cerrarán las otras sesiones.
            </p>

            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await signOut(auth);
                  } catch (err) {
                    console.error(err);
                  }
                  setPendingTakeover(null);
                  setUser(null);
                  setUserProfile(null);
                  setUserRole('user');
                  setProfileLoaded(false);
                  localStorage.removeItem('bypass_construction');
                  setBypassConstruction(false);
                }}
                className="flex-1 py-3.5 px-4 rounded-xl border border-white/10 hover:bg-white/5 active:scale-95 transition-all text-white font-bold text-xs uppercase tracking-wider cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => {
                  const { firebaseUser, profile } = pendingTakeover;
                  let localSessionId = window.localStorage.getItem('app_session_id');
                  if (!localSessionId) {
                    localSessionId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
                    window.localStorage.setItem('app_session_id', localSessionId);
                  }

                  try {
                    await setDoc(doc(db, getCollectionPath('users'), firebaseUser.uid), {
                      activeSessionId: localSessionId,
                      lastActiveAt: Date.now()
                    }, { merge: true });

                    setUser(firebaseUser);
                    setUserProfile(profile);
                    setUserRole(profile.role);
                    setProfileLoaded(true);

                    if (profile.role === 'superadmin') {
                      const isAdminMode = window.localStorage.getItem('is_admin_mode_active') === 'true';
                      setActiveTab(isAdminMode ? 'admin' : 'social');
                    } else if (profile.role === 'admin' || profile.role === 'empleado' || profile.role === 'gestor_turnos') {
                      setActiveTab('admin');
                    } else {
                      setActiveTab('social');
                    }
                  } catch (err) {
                    console.error(err);
                  } finally {
                    setPendingTakeover(null);
                  }
                }}
                className="flex-1 py-3.5 px-4 rounded-xl bg-yellow-400 hover:bg-yellow-500 active:scale-95 transition-all text-black font-bold text-xs uppercase tracking-wider shadow-[0_0_20px_rgba(250,204,21,0.3)] cursor-pointer"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Se eliminó el banner viejo por petición del usuario */}

      <NotificationBlockedModal
        isOpen={isBlockedModalOpen}
        onClose={() => setIsBlockedModalOpen(false)}
      />

      {/* FOOTER */}
      {config.feature_background !== false && (
        <footer className="relative z-10 w-full mt-auto theme-bg-card backdrop-blur-lg border-t border-white/5 pt-4 pb-24 lg:pb-4 px-4 flex flex-col items-center justify-center">
          {config.footerMode === 'image' && config.footerBannerUrl ? (
            <FadeInImage src={config.footerBannerUrl} alt="Footer Banner" className="max-w-full h-auto max-h-16 object-contain opacity-80" />
          ) : (
            <div className={`flex items-center gap-3 ${config.footerLogoPosition === 'left' ? 'flex-row' :
              config.footerLogoPosition === 'right' ? 'flex-row-reverse' :
                config.footerLogoPosition === 'bottom' ? 'flex-col-reverse' :
                  'flex-col'
              }`}>
              {config.footerLogoUrl && (
                <FadeInImage src={config.footerLogoUrl} alt="Footer Logo" className="w-8 h-8 object-contain opacity-60" noPlaceholderBg={true} />
              )}
              {config.footerText && (
                <p className="text-[10px] sm:text-xs font-bold opacity-40 uppercase tracking-widest text-center whitespace-pre-wrap">{config.footerText}</p>
              )}
            </div>
          )}
        </footer>
      )}

      {/* MODAL DETALLE DE JUGADOR */}
      <AnimatePresence>
        {viewingPlayer && (
          <PlayerDetailModal
            player={viewingPlayer}
            players={players}
            currentUserId={user?.uid}
            currentUserProfile={userProfile}
            onMatchClick={(m) => setSelectedMatchDetails(m)}
            onPlayerClick={openPlayerDetails}
            onContactClick={(playerId, playerName, playerPhoto) => {
              setActivePrivateChatRecipientId(playerId);
              setActivePrivateChatRecipientName(playerName);
              setActivePrivateChatRecipientPhoto(playerPhoto);
              setViewingPlayer(null);
            }}
            onClose={() => setViewingPlayer(null)}
            config={config}
          />
        )}
      </AnimatePresence>

      {/* MODAL DETALLE DE SEDE */}
      <AnimatePresence>
        {viewingSede && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 md:p-6"
            onClick={() => setViewingSede(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="w-full max-w-lg max-h-[90vh] overflow-y-auto custom-scrollbar"
              onClick={e => e.stopPropagation()}
            >
              <div className="theme-bg-card backdrop-blur-md rounded-[var(--radius-large)] border border-white/10 shadow-xl flex flex-col overflow-hidden">
                {/* Portada */}
                {(viewingSede.coverUrl || (viewingSede.galleryUrls && viewingSede.galleryUrls.length > 0)) && (
                  <div className="w-full h-32 md:h-40 bg-black/50 relative overflow-hidden shrink-0">
                    <img
                      src={viewingSede.coverUrl || (viewingSede.galleryUrls && viewingSede.galleryUrls[0]) || ''}
                      alt={viewingSede.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  </div>
                )}

                <div className="p-6 flex flex-col flex-1 relative">
                  <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                    <MapPin size={80} />
                  </div>

                  <div className="flex items-center gap-4 mb-4 relative z-10">
                    <div className="w-16 h-16 bg-black/50 rounded-full border-2 theme-primary-border flex items-center justify-center overflow-hidden shrink-0 shadow-lg">
                      {viewingSede.photoUrl ? (
                        <FadeInImage src={viewingSede.photoUrl} alt={viewingSede.name} className="w-full h-full object-cover" />
                      ) : (
                        <MapPin className="theme-primary-text opacity-50" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <h3 className="text-xl font-black leading-tight theme-font-secondary truncate">{viewingSede.name}</h3>
                        {viewingSede.city && (
                          <span className="text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-theme-primary/10 theme-primary-text border border-theme-primary/20 shrink-0">
                            {viewingSede.city}
                          </span>
                        )}
                      </div>
                      <div className="text-xs opacity-60 mt-1 flex items-center gap-1.5 min-w-0 truncate">
                        <MapPin size={12} className="mr-1 mt-0.5 shrink-0" />
                        <span className="truncate">{viewingSede.address || 'Sin dirección registrada'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-auto pt-4 flex items-center justify-between border-t border-white/5">
                    <div className="bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full theme-primary-bg animate-pulse"></div>
                      <span className="text-[10px] uppercase font-black tracking-widest text-white/70">
                        {viewingSede.courtsCount || 1} {viewingSede.courtsCount === 1 ? 'Cancha' : 'Canchas'}
                      </span>
                    </div>

                    {user && user.uid !== viewingSede.uid && (
                      <button
                        onClick={() => {
                          setActivePrivateChatRecipientId(viewingSede.uid);
                          setActivePrivateChatRecipientName(viewingSede.name || 'Sede Oficial');
                          setActivePrivateChatRecipientPhoto(viewingSede.photoUrl || '');
                          setActivePrivateChatRecipientPhone((viewingSede as any).whatsapp || viewingSede.phone || '');
                          setViewingSede(null);
                        }}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl theme-primary-bg text-black font-black uppercase tracking-widest text-[10px] hover:brightness-110 active:scale-95 transition-all shadow-[0_0_15px_rgba(var(--glow-rgb),0.2)]"
                      >
                        <MessageSquare size={14} />
                        Mensaje
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setViewingSede(null)}
                className="mt-3 w-full py-2.5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white text-xs font-black uppercase tracking-widest transition-all"
              >
                Cerrar
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MODAL CHAT PRIVADO */}
      {activePrivateChatRecipientId && userProfile && (
        <PrivateChatModal
          currentUser={userProfile}
          recipientId={activePrivateChatRecipientId}
          recipientName={activePrivateChatRecipientName}
          recipientPhotoUrl={activePrivateChatRecipientPhoto}
          recipientPhone={activePrivateChatRecipientPhone}
          isSupport={activePrivateChatIsSupport}
          isSuspended={isSuspendedActive}
          onClose={() => {
            setActivePrivateChatRecipientId(null);
            setActivePrivateChatRecipientName('');
            setActivePrivateChatRecipientPhoto('');
            setActivePrivateChatRecipientPhone('');
            setActivePrivateChatIsSupport(false);
          }}
        />
      )}

      {/* EXPANDED IMAGE MODAL */}
      {expandedImage && (
        <div
          className="fixed inset-0 z-[99999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-7xl max-h-screen">
            <button
              onClick={(e) => { e.stopPropagation(); setExpandedImage(null); }}
              className="absolute -top-12 right-0 bg-black/60 p-2 rounded-full text-white/50 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
            <img
              src={expandedImage}
              alt="Expanded"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking the image itself
            />
          </div>
        </div>
      )}

      {/* SCROLL TO TOP BUTTON (DESKTOP) */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="hidden lg:flex fixed bottom-8 right-8 z-[80] w-12 h-12 rounded-full theme-primary-bg text-black items-center justify-center shadow-[0_0_20px_rgba(var(--glow-rgb), 0.3)] hover:shadow-[0_0_30px_rgba(var(--glow-rgb), 0.6)] hover:scale-110 active:scale-95 transition-all group overflow-hidden"
          >
            <div className="relative flex flex-col items-center justify-center w-full h-full">
              <ChevronsUp size={24} className="group-hover:-translate-y-6 transition-transform duration-300" />
              <ChevronsUp size={24} className="absolute top-full group-hover:-translate-y-[150%] transition-transform duration-300" />
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {supportChatPlayer && userProfile && (
        <SupportChatModal
          player={supportChatPlayer}
          currentUser={userProfile}
          onClose={() => setSupportChatPlayer(null)}
          isAdminModeActive={isAdminModeActive}
        />
      )}

      <AnimatePresence>
        {selectedMatchDetails && (
          <MatchDetailModal
            match={selectedMatchDetails}
            players={players}
            user={userProfile}
            isAdmin={isAdminModeActive && (['admin', 'superadmin', 'co_admin'].includes(userRole || '') || selectedMatchDetails.organizerId === userProfile?.uid)}
            isOnline={isOnline}
            onClose={() => setSelectedMatchDetails(null)}
            initialMode={selectedMatchMode}
            isSuspended={isSuspendedActive}
          />
        )}
      </AnimatePresence>

      {editingPrizeData && (
        <PrizeEditModal
          groupKey={editingPrizeData.groupKey}
          position={editingPrizeData.position}
          currentPrize={editingPrizeData.currentPrize}
          onClose={() => setEditingPrizeData(null)}
          onSave={handleSavePrize}
          onDelete={handleDeletePrize}
        />
      )}

      {selectedPrizeDetail && (
        <PrizeDetailModal
          prize={selectedPrizeDetail}
          onClose={() => setSelectedPrizeDetail(null)}
        />
      )}

      {/* OFFLINE STATUS BANNER */}
      <AnimatePresence>
        {!isOnline && (
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="fixed top-20 left-4 right-4 md:left-auto md:right-6 md:w-[380px] z-[110] bg-black/75 backdrop-blur-xl border border-amber-500/20 rounded-2xl p-4 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
          >
            <div className="flex gap-3.5">
              <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-500 animate-pulse">
                <CloudOff size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-black uppercase tracking-widest text-amber-500 mb-1 theme-font-secondary">
                  Modo Sin Conexión
                </h4>
                <p className="text-[10px] leading-relaxed text-white/60">
                  Estás navegando sin conexión. Podrás consultar la información guardada localmente en modo lectura. Todas las transacciones y cambios requerirán internet para procesarse.
                </p>
              </div>
              <button
                onClick={() => {
                  const check = navigator.onLine;
                  if (check) {
                    window.location.reload();
                  }
                }}
                className="shrink-0 text-white/30 hover:text-white transition-colors self-start mt-0.5"
                title="Comprobar Conexión"
              >
                <AlertTriangle size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MOBILE MENU (MÁS) BOTTOM SHEET */}
      <AnimatePresence>
        {showMobileMenu && user && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] lg:hidden bg-black/40 backdrop-blur-sm flex items-end pb-2"
            onClick={() => setShowMobileMenu(false)}
          >
            <motion.div
              initial={{ y: 20, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full mx-2 bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.5)] p-2"
              style={{ marginBottom: 'calc(4.5rem + env(safe-area-inset-bottom, 0px))' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-1 overflow-x-auto custom-scrollbar">
                <button
                  onClick={() => { setActiveTab('herramientas'); setShowMobileMenu(false); }}
                  className="flex-1 min-w-[70px] flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-xl hover:bg-white/10 active:bg-white/5 active:scale-95 transition-all"
                >
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/5">
                    <LayoutList size={16} className="theme-primary-text" />
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-wider text-white whitespace-nowrap">Super 8/12</span>
                </button>
                <button
                  onClick={() => { setActiveTab('clubes'); setShowMobileMenu(false); }}
                  className="flex-1 min-w-[70px] flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-xl hover:bg-white/10 active:bg-white/5 active:scale-95 transition-all"
                >
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/5">
                    <MapPin size={16} className="theme-primary-text" />
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-wider text-white whitespace-nowrap">Clubes</span>
                </button>
                <button
                  onClick={() => { setActiveTab('historial'); setShowMobileMenu(false); }}
                  className="flex-1 min-w-[70px] flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-xl hover:bg-white/10 active:bg-white/5 active:scale-95 transition-all"
                >
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/5">
                    <History size={16} className="theme-primary-text" />
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-wider text-white whitespace-nowrap">Historial</span>
                </button>
                <button
                  onClick={() => { setActiveTab('sponsors'); setShowMobileMenu(false); }}
                  className="flex-1 min-w-[70px] flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-xl hover:bg-white/10 active:bg-white/5 active:scale-95 transition-all"
                >
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/5">
                    <Star size={16} className="theme-primary-text" />
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-wider text-white whitespace-nowrap">Sponsors</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MOBILE BOTTOM NAVIGATION BAR OR GUEST SPONSORS BANNER */}
      {!user && activeTab === 'inicio' && (
        <div className="fixed bottom-[4.2rem] left-0 right-0 z-[89]">
          {renderSponsorsCarousel(true)}
        </div>
      )}
      <div
        className="lg:hidden fixed left-0 right-0 z-[90] theme-primary-bg border-t border-black/10"
          style={{
            bottom: 0,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            backgroundColor: config.primaryColor || '#eab308',
            backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 40%, rgba(0,0,0,0.12) 100%)',
            boxShadow: '0 1px 0px rgba(255,255,255,0.25) inset, 0 -12px 40px rgba(0,0,0,0.45)'
          }}
        >
          <div className="flex items-center justify-between h-[4.2rem] px-2">
            {[
              { id: 'social', label: 'Inicio', icon: <Home size={22} /> },

              { id: 'reservas', label: 'Reservas', icon: <Clock size={22} /> },
              { id: 'torneos', label: 'Torneos', icon: <Trophy size={22} /> },
              { id: 'jugadores', label: 'Ranking', icon: <Medal size={22} /> },
              { id: 'mas', label: 'Más', icon: <Menu size={22} /> },
            ].map((item, index) => {
              const isActive = item.id === 'mas' ? showMobileMenu : (activeTab === item.id || (!user && item.id === 'social' && activeTab === 'inicio')) && !showMobileMenu;
              return (
              <React.Fragment key={item.id}>
                <button
                  onClick={() => {
                    if (!user && (item.id === 'partidos')) {
                      handleOpenLoginModal('login');
                      return;
                    }
                    if (item.id === 'mas') {
                      setShowMobileMenu(!showMobileMenu);
                    } else {
                      setActiveTab(item.id);
                      setShowMobileMenu(false);
                    }
                  }}
                  className={`flex flex-col items-center justify-center w-full h-full space-y-0.5 relative transition-all duration-300 ease-out pt-1 pb-2 origin-bottom ${isActive
                    ? 'scale-105 text-black'
                    : 'scale-98 text-black/50 hover:text-black/70'
                    }`}
                >
                  <span className={`relative transition-transform duration-300 ${isActive ? 'scale-100' : 'scale-100 opacity-80'}`}>
                    {item.id === 'reservas' ? (
                      <>
                        <img
                          src="/grid-icon.png"
                          alt="Reservas"
                          className={`w-[22px] h-[22px] object-contain transition-all ${isActive
                            ? 'mix-blend-multiply brightness-0'
                            : 'mix-blend-multiply brightness-[0.2] opacity-50'
                            }`}
                        />
                        {globalMyTurnosCount > 0 && (
                          <span className="absolute -top-1.5 -right-4 bg-white text-black text-[9px] font-black w-[16px] h-[16px] rounded-full flex items-center justify-center border border-black/10 shadow-sm z-10">
                            {globalMyTurnosCount}
                          </span>
                        )}
                      </>
                    ) : item.id === 'partidos' ? (
                      <img
                        src="/partidos-icon2.png"
                        alt="Partidos"
                        className={`w-[22px] h-[22px] object-contain transition-all ${isActive
                          ? 'mix-blend-multiply brightness-0'
                          : 'mix-blend-multiply brightness-[0.2] opacity-50'
                          }`}
                      />
                    ) : item.icon}
                  </span>
                  <span className={`text-[9px] uppercase tracking-wider transition-all duration-300 ${isActive ? 'font-black' : 'font-semibold'}`}>
                    {item.label}
                  </span>
                  {isActive && (
                    <div
                      className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-6 h-[2px] bg-black rounded-full shadow-[0_1px_1px_rgba(0,0,0,0.15)] animate-in fade-in duration-200"
                    />
                  )}
                </button>
                {index < 4 && (
                  <div className="w-[1px] h-5 bg-black/10 self-center shrink-0 rounded-full" />
                )}
              </React.Fragment>
              );
            })}
          </div>
        </div>


      {/* Botón flotante móvil (FAB) "Volver" cuando se pierde el botón superior por scroll */}
      {activeTab === 'admin' && adminSection !== 'turnos' && showFloatingBack && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[95] lg:hidden animate-in fade-in slide-in-from-bottom-5 duration-300"
          style={{ bottom: bottomOffset }}
        >
          <button
            onClick={() => {
              setAdminSection('turnos');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-transparent text-white border border-white font-extrabold text-[9px] uppercase tracking-wider rounded-full active:scale-95 transition-all duration-300 backdrop-blur-sm"
          >
            <ArrowLeft size={11} className="stroke-[3.5] text-white" />
            <span>Volver</span>
          </button>
        </div>
      )}

      {/* Global In-App Dialog Interceptor Portal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {globalDialog && globalDialog.isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/25 backdrop-blur-sm"
              onClick={() => {
                if (globalDialog.type === 'confirm') {
                  globalDialog.onResolve(false);
                } else if (globalDialog.type === 'prompt') {
                  globalDialog.onResolve(null);
                } else {
                  globalDialog.onResolve(true);
                }
              }}
            >
              <motion.div
                initial={{ scale: 0.95, y: 10, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.95, y: 10, opacity: 0 }}
                transition={{ type: "spring", duration: 0.3 }}
                className="w-full max-w-[340px] bg-slate-800/95 border border-white/10 rounded-2xl overflow-hidden shadow-[0_16px_48px_rgba(0,0,0,0.6)]"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header Accent Line */}
                <div
                  className="h-1 w-full"
                  style={{
                    backgroundColor: config.primaryColor || '#EAB308',
                    backgroundImage: `linear-gradient(90deg, ${config.primaryColor || '#EAB308'} 0%, #ffffff 50%, ${config.primaryColor || '#EAB308'} 100%)`
                  }}
                />
                
                <div className="p-5 flex flex-col items-center text-center">
                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-full bg-white/5 border flex items-center justify-center mb-4"
                    style={{ borderColor: `${config.primaryColor || '#EAB308'}30` }}
                  >
                    {globalDialog.type === 'confirm' ? (
                      <AlertTriangle style={{ color: config.primaryColor || '#EAB308' }} size={22} />
                    ) : globalDialog.type === 'prompt' ? (
                      <UserIcon style={{ color: config.primaryColor || '#EAB308' }} size={22} />
                    ) : (
                      <Bell style={{ color: config.primaryColor || '#EAB308' }} size={22} />
                    )}
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-base font-extrabold text-white uppercase tracking-wider mb-2">
                    {globalDialog.type === 'confirm' ? 'Confirmación' : globalDialog.type === 'prompt' ? globalDialog.title : 'Notificación'}
                  </h3>
                  
                  {/* Message */}
                  <p className="text-xs text-white/70 leading-relaxed font-semibold max-w-[280px]">
                    {globalDialog.message}
                  </p>

                  {/* Input field for prompt type */}
                  {globalDialog.type === 'prompt' && (
                    <input
                      type="text"
                      defaultValue={globalDialog.defaultValue || ''}
                      placeholder={globalDialog.title === 'Jugador Invitado' ? 'Ej: Juan Perez' : ''}
                      ref={promptInputRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          globalDialog.onResolve(promptInputRef.current?.value || '');
                        } else if (e.key === 'Escape') {
                          globalDialog.onResolve(null);
                        }
                      }}
                      autoFocus
                      className="mt-4 w-full px-4 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-white font-semibold focus:outline-none focus:border-theme-primary/50 transition-all text-center focus:ring-1 focus:ring-theme-primary/30"
                      style={{
                        borderColor: 'rgba(255,255,255,0.2)'
                      }}
                    />
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex border-t border-white/10">
                  {globalDialog.type === 'confirm' || globalDialog.type === 'prompt' ? (
                    <>
                      <button
                        onClick={() => globalDialog.onResolve(globalDialog.type === 'prompt' ? null : false)}
                        className="flex-1 py-3 text-xs font-black uppercase tracking-wider text-white/50 hover:text-white hover:bg-white/10 hover:scale-[1.02] transition-all border-r border-white/10 active:bg-white/[0.02] cursor-pointer"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => globalDialog.onResolve(globalDialog.type === 'prompt' ? (promptInputRef.current?.value || '') : true)}
                        className="flex-1 py-3 text-xs font-black uppercase tracking-wider text-black hover:brightness-110 active:brightness-95 hover:scale-[1.02] transition-all shadow-[0_0_12px_rgba(234,179,8,0.25)] hover:shadow-[0_0_20px_rgba(234,179,8,0.45)] font-extrabold cursor-pointer"
                        style={{
                          backgroundColor: config.primaryColor || '#EAB308',
                        }}
                      >
                        Confirmar
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => globalDialog.onResolve(true)}
                      className="w-full py-3 text-xs font-black uppercase tracking-wider text-black hover:brightness-110 active:brightness-95 hover:scale-[1.02] transition-all shadow-[0_0_12px_rgba(234,179,8,0.25)] hover:shadow-[0_0_20px_rgba(234,179,8,0.45)] font-extrabold cursor-pointer"
                      style={{
                        backgroundColor: config.primaryColor || '#EAB308',
                      }}
                    >
                      Aceptar
                    </button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Pre-Launch Modal */}
      <PreLaunchModal
        isOpen={showPreLaunchModal}
        onClose={() => setShowPreLaunchModal(false)}
        config={config}
        onAdminBypass={() => {
          setShowPreLaunchModal(false);
          setLoginModalInitialView('login');
          setIsLoginModalOpen(true);
        }}
      />
    </div>
  );
}

// trigger rebuild
